// CornerstoneManager.ts
import {
  init as csRenderInit,
  volumeLoader,
  isCornerstoneInitialized,
  RenderingEngine,
  getRenderingEngine,
  Enums,
  Types,
  getConfiguration,
  eventTarget,
  VolumeViewport3D,
  cache,
  setVolumesForViewports,
  getEnabledElementByViewportId,
  getEnabledElements,
  VolumeViewport,
  utilities,
  ImageVolume,
  getEnabledElement,
} from '@cornerstonejs/core';
import * as cornerstoneTools from '@cornerstonejs/tools';
import { OrientationAxis } from '@cornerstonejs/core/dist/esm/enums';
import {
  Cornerstone3DConfig,
  EventTypes,
} from '@cornerstonejs/core/dist/esm/types';
import {
  state,
  BaseTool,
  AngleTool,
  CobbAngleTool,
  init as csToolsInit,
  EllipticalROITool,
  LengthTool,
  RectangleROITool,
  StackScrollTool,
  ZoomTool,
  PlanarFreehandROITool,
  segmentation,
  PlanarRotateTool,
  SynchronizerManager,
  Synchronizer,
  synchronizers,
  annotation,
  PanTool,
  CrosshairsTool,
  ToolGroupManager,
  Enums as ToolEnums,
  addTool,
  Types as ToolTypes,
  TrackballRotateTool,
  SplineROITool,
  utilities as toolsUtils,
} from '@cornerstonejs/tools';
import { PanelComponentKeyType } from 'config/panelConfig';
import { RenderingQualityKeyType } from 'config/settingsConfig';
import { getPolySeg } from '@cornerstonejs/tools/dist/esm/config';
import { SegmentationPublicInput } from '@cornerstonejs/tools/dist/esm/types';
import { SegmentationRepresentations } from '@cornerstonejs/tools/dist/esm/enums';
import * as polySeg from '@cornerstonejs/polymorphic-segmentation';
import React from 'react';
import { Series } from '@models/Series';
import { downloadQueue } from 'utils/DownloadQueue';
import {
  AnalysisType,
  defaultSliceThickness,
  defaultWindowCenter,
  defaultWindowWidth,
  DirectionType,
  EditNames,
  getPlaqueDisplayType,
  getSegmentationAnalysisType,
  PanelState,
  Segmentation,
  SegmentationControllerType,
  SegmentationProps,
  ThicknessMethodType,
} from 'config/analysisConfig';
import { fetchSeriesArrayBuffer } from '@api/files';
import { vec3, mat4, vec4 } from 'gl-matrix';
import { runWorkerTask } from '../workerPool';
import { scheduleRender } from '../renderState';
import { ImageTag } from '@models/ImageTag';
import { getThickLineCoords3D } from '../../../../../utils/ConnectUtilities';
import type { Point3D } from '../../../../../utils/ConnectUtilities';
import {
  beginStroke,
  clearSegmentManager,
  endStroke,
  exitSegmentManager,
  hasSess,
  initializeSegmentManager,
  makeBrushGroupSvg,
  ModifierType,
  paintAtPoint,
  paintAtPointWithinIndices,
  redoStroke,
  subscribeSegmentHistoryChange,
  undoStroke,
} from '@features/analysis/segmentManager';
import type { UndoDelta } from '@features/analysis/segmentManager';
import { hasSegmentationState } from '@features/analysis/segmentManager/core/SegmentManagerCore';
import { configStore } from 'config/configStore';
import {
  createVisibleIJKSamplingPayload,
  mergeLinearIndicesSorted,
  sampleVisibleLinearIndicesInRangeFastOblique,
} from '../intensityRangeSampling';
import {
  applyPreviewBitToLinearIndices,
  setPreviewBitAtLinearIndex,
  shouldRefreshPreviewForRangeChange,
} from '../manualPreviewBit';
import {
  diffPreviewLinearIndices,
  getBrushConnect2DSelectionUpdateAction,
  getPreviewMaskConnect2DStrokeIndices,
  getShiftFloodPreviewDelayMs,
  isClientXYInsideViewportRect,
  isShiftFloodPreviewViewport,
  previewIJKsToLinearIndices,
  shouldBootstrapShiftFloodPreview,
  shouldCommitShiftFloodPreview,
  shouldScheduleShiftFloodPreview,
  shouldUseShiftFloodPreview,
} from '../manualFloodPreviewTrigger';
import { handleRedo, handleUndo } from 'utils/hotKeyActions';
import { publishImageLayerStackLiveWindowingPolicy } from '../ImageLayerStack';

export enum ViewportId {
  AXIAL = 'Axial',
  SAGITTAL = 'Sagittal',
  CORONAL = 'Coronal',
  LABELMAP = '3D',
}

export const mainEngineId = 'MONITOR1';

export const MIN_PARALLEL_SCALE = -50;
export const MAX_PARALLEL_SCALE = 300;

const axialColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-axial').trim();
const coronalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-coronal').trim();
const sagittalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-sagittal').trim();

export const viewportColors: Record<'Axial' | 'Coronal' | 'Sagittal', string> =
  {
    Axial: axialColor,
    Coronal: coronalColor,
    Sagittal: sagittalColor,
  };

type TabRangePreviewManualVolume = {
  voxelManager?: any;
  dimensions?: readonly number[];
  modified?: () => void;
};
type TabRangePreviewScalarData = Parameters<
  typeof sampleVisibleLinearIndicesInRangeFastOblique
>[0];

const tabRangePreviewState: {
  held: boolean;
  active: boolean;
  bindingToken: number;
  refreshRequestId: number;
  refreshTimer: number | null;
  ownerKey: string | null;
  manualVolume: TabRangePreviewManualVolume | null;
  indices: Uint32Array | null;
  range: [number, number] | null;
} = {
  held: false,
  active: false,
  bindingToken: 0,
  refreshRequestId: 0,
  refreshTimer: null,
  ownerKey: null,
  manualVolume: null,
  indices: null,
  range: null,
};

function getTabRangePreviewOwnerKey(seriesId: string, lowQuality: boolean) {
  return `${seriesId}:${lowQuality ? 'low' : 'high'}`;
}

function clearTabRangePreviewRefreshTimer() {
  if (tabRangePreviewState.refreshTimer === null) return;

  window.clearTimeout(tabRangePreviewState.refreshTimer);
  tabRangePreviewState.refreshTimer = null;
}

function clearTabRangePreviewIndices(render = true): boolean {
  const { indices, manualVolume } = tabRangePreviewState;

  if (!indices?.length) {
    tabRangePreviewState.indices = null;
    return false;
  }

  const result = applyPreviewBitToLinearIndices(
    manualVolume?.voxelManager,
    indices,
    manualVolume?.dimensions,
    false,
  );

  tabRangePreviewState.indices = null;
  if (result.changed) {
    manualVolume?.modified?.();
    if (render) renderEnabledElements();
  }

  return result.changed;
}

function stopTabRangePreview(render = true) {
  clearTabRangePreviewRefreshTimer();
  tabRangePreviewState.refreshRequestId += 1;
  tabRangePreviewState.active = false;
  clearTabRangePreviewIndices(render);
  tabRangePreviewState.ownerKey = null;
  tabRangePreviewState.manualVolume = null;
  tabRangePreviewState.range = null;
}

function setTabRangePreviewIndices(
  ownerKey: string,
  manualVolume: TabRangePreviewManualVolume,
  indices: Uint32Array,
) {
  const cleared = clearTabRangePreviewIndices(false);
  tabRangePreviewState.ownerKey = ownerKey;
  tabRangePreviewState.manualVolume = manualVolume;

  if (
    !manualVolume?.voxelManager ||
    !manualVolume?.dimensions ||
    !indices.length
  ) {
    tabRangePreviewState.indices = null;
    if (cleared) renderEnabledElements();
    return;
  }

  const result = applyPreviewBitToLinearIndices(
    manualVolume.voxelManager,
    indices,
    manualVolume.dimensions,
    true,
  );

  tabRangePreviewState.indices = result.indices.length ? result.indices : null;
  if (cleared || result.changed) {
    manualVolume.modified?.();
    renderEnabledElements();
  }
}

function isTabRangePreviewHeld(ownerKey: string) {
  return tabRangePreviewState.held && tabRangePreviewState.ownerKey === ownerKey;
}

function getTabRangePreviewIndices(ownerKey: string) {
  return isTabRangePreviewHeld(ownerKey) ? tabRangePreviewState.indices : null;
}

const DICOM_ZOOM_SYNC_VIEWPORT_IDS: PanelComponentKeyType[] = [
  'Axial',
  'Coronal',
  'Sagittal',
];
const DICOM_ZOOM_SYNC_EPSILON = 1e-6;
const DICOM_PAN_SYNC_EPSILON = 1e-4;
const suppressedDicomZoomSyncScales = new Map<string, number>();
const suppressedDicomPanSyncCameras = new Map<
  string,
  { focalPoint: Types.Point3; position: Types.Point3 }
>();
let isApplyingDicomZoomSync = false;
let isApplyingDicomPanSync = false;

const renderingQualityMapping: Record<RenderingQualityKeyType, number> = {
  high: 3,
  medium: 2,
  low: 1,
};
export const initialCornerstone3DConfig: Cornerstone3DConfig = {
  gpuTier: {
    tier: 1,
  },
  isMobile: false,
  rendering: {
    preferSizeOverAccuracy: false,
    useCPURendering: false,
    strictZSpacingForVolumeViewport: false,
  },
};

const TOOL_NAME_MAP: Record<string, string> = {
  // current navigation
  Scroll: 'CustomStackScrollTool',
  Pan: PanTool.toolName,
  Zoom: ZoomTool.toolName,
  WindowLevel: "CustomWindowLevelTool",
  // current mesurement
  Length: LengthTool.toolName,
  Angle: AngleTool.toolName,
  CobbAngle: CobbAngleTool.toolName,
  Spline: SplineROITool.toolName,
  FreeHand: PlanarFreehandROITool.toolName,
  Ellipse: EllipticalROITool.toolName,
  Rectangle: RectangleROITool.toolName,
  pencil: PlanarFreehandROITool.toolName,

  roll: PlanarRotateTool.toolName,
  crosshairs: 'CustomCrosshairsTool',
  threeDRotate: TrackballRotateTool.toolName,
};

type PolySegConversionOptions = {
  segmentIndices?: number[];
  segmentationId?: string;
  viewport?: Types.IStackViewport | Types.IVolumeViewport;
};

type OnDragUpdateCallback = (windowLevel : number, windowWidth : number) => void;
interface CustomWindowLevelConfiguration {
  onDragUpdate: OnDragUpdateCallback;
}


type WindowLevelDragPayload = {
  windowLevel: number;
  windowWidth: number;
  sourceViewportId: string;
  onDragUpdate?: OnDragUpdateCallback;
  forceCommit?: boolean;
};

let cornerstoneWindowLevelRenderRaf: number | null = null;
let cornerstoneWindowLevelCommitTimer: number | null = null;
let cornerstoneWindowLevelPendingPayload: WindowLevelDragPayload | null = null;
let cornerstoneWindowLevelPendingCommit: WindowLevelDragPayload | null = null;
let cornerstoneWindowLevelLastCommitAt = 0;

const CORNERSTONE_WINDOW_LEVEL_COMMIT_INTERVAL_MS = 48;

function scheduleCornerstoneVrWindowLevelUpdate(windowLevel: number, windowWidth: number) {
  scheduleRender('VR', {
    type: 'updateWindowLevel',
    windowWidth,
    windowLevel,
  });
}

function getVOIRangeFromWindowLevelWidth(windowLevel: number, windowWidth: number) {
  const halfWindowWidth = Math.max(1, windowWidth) * 0.5;
  return {
    lower: windowLevel - halfWindowWidth,
    upper: windowLevel + halfWindowWidth,
  };
}

function applyCornerstoneViewportsWindowLevelWidthLive(
  windowLevel: number,
  windowWidth: number,
) {
  const newVOIRange = getVOIRangeFromWindowLevelWidth(windowLevel, windowWidth);

  getEnabledElements().forEach(({ viewport }) => {
    const vpProps = viewport.getProperties?.();
    const voiRange = vpProps?.voiRange;
    if (!voiRange) return;
    if (voiRange.upper === newVOIRange.upper && voiRange.lower === newVOIRange.lower) return;

    viewport.setProperties?.({ voiRange: newVOIRange });
    viewport.render?.();
  });
}

function flushCornerstoneWindowLevelCommit(force = false) {
  const payload = cornerstoneWindowLevelPendingCommit;
  if (!payload?.onDragUpdate) return;

  const now = performance.now();
  const elapsed = now - cornerstoneWindowLevelLastCommitAt;
  if (!force && elapsed < CORNERSTONE_WINDOW_LEVEL_COMMIT_INTERVAL_MS) {
    if (cornerstoneWindowLevelCommitTimer === null) {
      cornerstoneWindowLevelCommitTimer = window.setTimeout(() => {
        cornerstoneWindowLevelCommitTimer = null;
        flushCornerstoneWindowLevelCommit(true);
      }, CORNERSTONE_WINDOW_LEVEL_COMMIT_INTERVAL_MS - elapsed);
    }
    return;
  }

  if (cornerstoneWindowLevelCommitTimer !== null) {
    window.clearTimeout(cornerstoneWindowLevelCommitTimer);
    cornerstoneWindowLevelCommitTimer = null;
  }

  cornerstoneWindowLevelPendingCommit = null;
  cornerstoneWindowLevelLastCommitAt = now;
  payload.onDragUpdate(payload.windowLevel, payload.windowWidth);
  scheduleCornerstoneVrWindowLevelUpdate(payload.windowLevel, payload.windowWidth);
}

function scheduleCornerstoneWindowLevelLiveUpdate(payload: WindowLevelDragPayload) {
  cornerstoneWindowLevelPendingPayload = payload;
  cornerstoneWindowLevelPendingCommit = payload;

  if (cornerstoneWindowLevelRenderRaf !== null) return;

  cornerstoneWindowLevelRenderRaf = window.requestAnimationFrame(() => {
    cornerstoneWindowLevelRenderRaf = null;
    const next = cornerstoneWindowLevelPendingPayload;
    cornerstoneWindowLevelPendingPayload = null;
    if (!next) return;

    applyCornerstoneViewportsWindowLevelWidthLive(next.windowLevel, next.windowWidth);
    publishImageLayerStackLiveWindowingPolicy(
      { windowLevel: next.windowLevel, windowWidth: next.windowWidth },
      `cornerstone-${next.sourceViewportId}`,
    );
    flushCornerstoneWindowLevelCommit(next.forceCommit === true);
  });
}

function flushCornerstoneWindowLevelLiveUpdate() {
  const pending = cornerstoneWindowLevelPendingPayload;
  if (pending) {
    cornerstoneWindowLevelPendingPayload = null;
    applyCornerstoneViewportsWindowLevelWidthLive(pending.windowLevel, pending.windowWidth);
    publishImageLayerStackLiveWindowingPolicy(
      { windowLevel: pending.windowLevel, windowWidth: pending.windowWidth },
      `cornerstone-${pending.sourceViewportId}`,
    );
    cornerstoneWindowLevelPendingCommit = { ...pending, forceCommit: true };
  }

  flushCornerstoneWindowLevelCommit(true);
}

class CustomStackScrollTool extends StackScrollTool {
  static toolName = 'CustomStackScrollTool';
  declare run : ()=>void;
  constructor( toolProps :ToolTypes.PublicToolProps, defaultToolProps:ToolTypes.ToolProps){
  super(toolProps, defaultToolProps);
    this.run = ()=>{
      const toolGroup = ToolGroupManager.getToolGroup(this.toolGroupId);
      const crosshairs = toolGroup?.getToolInstance(TOOL_NAME_MAP['crosshairs']);
      if (crosshairs) {
        const mode = (toolGroup as any)?.toolOptions?.[CustomCrosshairsTool.toolName]?.mode;
        let world: Types.Point3 | undefined;
        let name: string | undefined;

        if (mode === 'Active') {
          world = crosshairs.toolCenter;
        }else{
          const viewportIds = toolGroup?.getViewportIds();
          if (!viewportIds || viewportIds.length !== 1) return;

          const engine = getEngine(mainEngineId);
          const viewportId = viewportIds[0];

          const viewport = engine.getViewport(viewportId) as VolumeViewport3D;
          if (!viewport) return;

          const { focalPoint } = viewport.getCamera();
          if (!focalPoint) return;
          world = focalPoint;
          name = viewportId;
        }
        const arg = { type: 'focus', world, name };
        scheduleRender('Surface3D', arg);
        scheduleRender('VR', arg);
      }
    }
  }
  _dragCallback(evt:ToolTypes.EventTypes.InteractionEventType) {
    super._dragCallback(evt);
    this.run();
  }
  _scroll(evt: cornerstoneTools.Types.EventTypes.MouseWheelEventType): void {
      super._scroll(evt);
      this.run();
  }
  _scrollDrag(evt: cornerstoneTools.Types.EventTypes.InteractionEventType): void {
      super._scrollDrag(evt);
      this.run();
  }
}

class CustomWindowLevelTool extends BaseTool{
  static toolName = 'CustomWindowLevelTool';
  public declare configuration: CustomWindowLevelConfiguration & Record<string, any>;
  private liveVOIRange: { lower: number; upper: number } | null = null;
  private liveViewportId: string | null = null;
  private resetLiveDragTimer: number | null = null;

  constructor(
    toolProps :ToolTypes.PublicToolProps= {},
    defaultToolProps:ToolTypes.ToolProps = { supportedInteractionTypes: ['Mouse', 'Touch'] }
  ) {
    super(toolProps, defaultToolProps);

    this.configuration = Object.assign(this.configuration || {}, {
      onDragUpdate: () => { console.log('Configuration is not yet set via setToolConfiguration.'); }
      // onDragUpdate: () => toolProps.configuration?.onDragUpdate || (() => { })
    }) as CustomWindowLevelConfiguration & Record<string, any>
  }

  private clearResetLiveDragTimer() {
    if (this.resetLiveDragTimer !== null) {
      window.clearTimeout(this.resetLiveDragTimer);
      this.resetLiveDragTimer = null;
    }
  }

  private resetLiveDragState() {
    this.clearResetLiveDragTimer();
    this.liveVOIRange = null;
    this.liveViewportId = null;
  }

  private scheduleResetLiveDragState() {
    this.clearResetLiveDragTimer();
    this.resetLiveDragTimer = window.setTimeout(() => {
      this.resetLiveDragState();
    }, 180);
  }

  private flushLiveDragState() {
    flushCornerstoneWindowLevelLiveUpdate();
    this.resetLiveDragState();
  }

  mouseDragCallback(evt:ToolTypes.EventTypes.InteractionEventType) {
    evt.stopImmediatePropagation();

    const { deltaPoints, viewportId } = evt.detail;
    const viewport  = getEngine(mainEngineId).getViewport(viewportId) as VolumeViewport3D;

    if (!this.liveVOIRange || this.liveViewportId !== viewportId) {
      const vpProps = viewport.getProperties().voiRange;
      if(!vpProps) return;
      this.liveVOIRange = { lower: vpProps.lower, upper: vpProps.upper };
      this.liveViewportId = viewportId;
    }

    const deltaX = deltaPoints.canvas[0];
    const deltaY = deltaPoints.canvas[1];

    const newVOIRange = {
      lower: this.liveVOIRange.lower + deltaY - deltaX,
      upper: this.liveVOIRange.upper + deltaY + deltaX,
    };
    //반전매력
    if (newVOIRange.lower > newVOIRange.upper) return;

    this.liveVOIRange = newVOIRange;
    this.scheduleResetLiveDragState();

    const newWW = newVOIRange.upper - newVOIRange.lower;
    const newWL = (newVOIRange.upper + newVOIRange.lower) *0.5;
    if(this.configuration.onDragUpdate){
      scheduleCornerstoneWindowLevelLiveUpdate({
        windowLevel: newWL,
        windowWidth: newWW,
        sourceViewportId: viewportId,
        onDragUpdate: this.configuration.onDragUpdate,
      });
    }
    // console.log(newVOIRange)
  }

  mouseUpCallback() {
    this.flushLiveDragState();
  }

  mouseClickCallback() {
    this.flushLiveDragState();
  }
}

class CustomCrosshairsTool extends CrosshairsTool {
  static toolName = 'CustomCrosshairsTool';

  activeOperation = 0;
  private lastRightClickAt = 0;
  private readonly rightDoubleClickThresholdMs = 300;
  private readonly rightDoubleClickCleanups: (() => void)[] = [];

  constructor( toolProps :ToolTypes.PublicToolProps, defaultToolProps:ToolTypes.ToolProps){
    super(toolProps, defaultToolProps);

    const originalHandle = this.handleSelectedCallback;
    const originalDrag = this._dragCallback;
    const originalEnd = this._endCallback;

    const originalRenderAnnotation = this.renderAnnotation;

    this.renderAnnotation = (enabledElement, svgDrawingHelper): boolean => {
      if (!configStore.get().EditMode) {
        return originalRenderAnnotation.call(
          this,
          enabledElement,
          svgDrawingHelper,
        );
      }

      const annotations = this._getAnnotations(enabledElement);

      const backups = annotations.map((annotation) => {
        const handles = (annotation as any).data?.handles;

        const backup = {
          annotation,
          rotationPoints: handles?.rotationPoints,
          slabThicknessPoints: handles?.slabThicknessPoints,
        };

        if (handles) {
          handles.rotationPoints = [];
          handles.slabThicknessPoints = [];
        }

        return backup;
      });

      const rendered = originalRenderAnnotation.call(
        this,
        enabledElement,
        svgDrawingHelper,
      );

      backups.forEach(({ annotation, rotationPoints, slabThicknessPoints }) => {
        const handles = (annotation as any).data?.handles;
        if (!handles) return;

        handles.rotationPoints = rotationPoints;
        handles.slabThicknessPoints = slabThicknessPoints;
      });

      return rendered;
    };
    this.handleSelectedCallback = (evt: ToolTypes.EventTypes.InteractionEventType, annotation: ToolTypes.Annotation) : void =>{
      //메뉴얼모드일때 기능막기
      if(configStore.get().EditMode) return;
      originalHandle(evt,annotation);
      this.activeOperation = (annotation as any).data.handles.activeOperation;
    }
    this._dragCallback = (evt: ToolTypes.EventTypes.InteractionEventType): void => {
      //메뉴얼모드일때 기능막기
      if(configStore.get().EditMode) return;
      originalDrag(evt);
      //rot
      if(this.activeOperation === 2){
        // 반드시 3개임. 그래야만하고.
        let xAxis: Types.Point3|undefined,yAxis: Types.Point3|undefined,zAxis : Types.Point3|undefined;
        this._getViewportsInfo().map(({viewportId})=>{
          const {viewPlaneNormal}  = getEnabledElementByViewportId(viewportId).viewport.getCamera();
          switch(viewportId){
            case 'Coronal':
              yAxis = viewPlaneNormal;
              break;
            case 'Sagittal':
              xAxis = viewPlaneNormal;
              break;
            case 'Axial':
              zAxis = viewPlaneNormal;
              break;
          }
          if(xAxis && yAxis && zAxis){
            scheduleRender('Surface3D', { type: 'rotate', xAxis, yAxis, zAxis });
            scheduleRender('VR',{type:'rotate',xAxis,yAxis,zAxis})
          }
        })
      }else if(this.activeOperation === 3){
        // originalDrag(evt);
      }else if(this.activeOperation === 0){
        const arg = {type: 'focus',world: this.toolCenter,};
        scheduleRender('Surface3D', arg);
        scheduleRender('VR', arg);
      }
      // super._dragCallback(evt);
    }
    this._endCallback =  (evt: ToolTypes.EventTypes.InteractionEventType): void => {
      originalEnd(evt);
      if (this.activeOperation === 0 && this.toolCenter) {
        const arg = {type: 'focus',world: this.toolCenter,};
        centerDicomViewportsOnWorld(this.toolCenter as Types.Point3);
        scheduleRender('Surface3D', arg);
        scheduleRender('VR', arg);
      }
      this.activeOperation = 0;
    }
  }
  onSetToolActive(): void {
    super.onSetToolActive();
    this.attachRightDoubleClickListeners();
  }

  onSetToolEnabled(): void {
    super.onSetToolEnabled();
    this.attachRightDoubleClickListeners();
  }

  onSetToolDisabled(): void {
    this.cleanupRightDoubleClickListeners();
    super.onSetToolDisabled();
  }

  private cleanupRightDoubleClickListeners(): void {
    this.rightDoubleClickCleanups.splice(0).forEach((cleanup) => cleanup());
  }

  private attachRightDoubleClickListeners(): void {
    this.cleanupRightDoubleClickListeners();

    this._getViewportsInfo().forEach(({ viewportId }) => {
      const enabledElement = getEnabledElementByViewportId(viewportId);
      const element = enabledElement?.viewport?.element;
      if (!element) return;

      const onMouseDown = (event: MouseEvent) => {
        this.handleRightMouseDown(event, enabledElement, element);
      };

      element.addEventListener('mousedown', onMouseDown, true);
      this.rightDoubleClickCleanups.push(() => {
        element.removeEventListener('mousedown', onMouseDown, true);
      });
    });
  }

  private handleRightMouseDown(
    event: MouseEvent,
    enabledElement: Types.IEnabledElement,
    element: HTMLDivElement,
  ): void {
    if (!this.isRightDoubleClick(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();


    const rect = element.getBoundingClientRect();
    const canvas: Types.Point2 = [
      event.clientX - rect.left,
      event.clientY - rect.top,
    ];
    const world =  enabledElement.viewport.canvasToWorld(canvas);

    this._jump(enabledElement, world);
    this.setToolCenter(world);

    const arg = { type: 'focus', world };
    scheduleRender('Surface3D', arg);
    scheduleRender('VR', arg);
  }

  private isRightDoubleClick(event: MouseEvent): boolean {
    if (event.button !== 2) return false;

    const now = performance.now();
    const isDoubleClick =
      now - this.lastRightClickAt <= this.rightDoubleClickThresholdMs;
    this.lastRightClickAt = isDoubleClick ? 0 : now;
    return isDoubleClick;
  }
}

// const getFilesApi = async () => {
//   const BASE_URL = await loadConfig();
//   return `${BASE_URL}/api/v1/files`;
// }

function getEngine(renderingEngineId: string) {
  let engine = getRenderingEngine(renderingEngineId);
  if (!engine) {
    engine = new RenderingEngine(renderingEngineId);
  }
  return engine;
}
function getDefaultViewportOrientation(viewportId: PanelComponentKeyType) {
  const EPS = 1e-6;
  let orientation: Types.OrientationVectors = {
    viewPlaneNormal: [-EPS, -EPS, -1 + EPS] as Types.Point3,
    viewUp: [-EPS, 1 + EPS, -EPS] as Types.Point3,
  } as Types.OrientationVectors;

  switch (viewportId) {
    case 'Coronal':
      orientation = {
        viewPlaneNormal: [-EPS, -1 + EPS, -EPS] as Types.Point3,
        viewUp: [-EPS, -EPS, -1 + EPS] as Types.Point3,
      } as Types.OrientationVectors;
      break;
    case 'Sagittal':
      orientation = {
        viewPlaneNormal: [1 + EPS, -EPS, -EPS] as Types.Point3,
        viewUp: [-EPS, -EPS, -1 + EPS] as Types.Point3,
      } as Types.OrientationVectors;
      break;
  }
  return orientation;
}
function getPublicViewportInput(
  viewportId: PanelComponentKeyType,
  element: HTMLDivElement,
) {
  const orientation = getDefaultViewportOrientation(viewportId);
  const interpolationType = utilities.clamp(
    getConfiguration().gpuTier.tier - 1,
    0,
    2,
  );
  return {
    viewportId: viewportId,
    type: Enums.ViewportType.ORTHOGRAPHIC,
    element: element,
    defaultOptions: {
      orientation: orientation || (viewportId.toLowerCase() as OrientationAxis),
      background: [0.05, 0.05, 0.05] as Types.RGB,
      displayArea: {
        type: 'SCALE',
        imageArea: [1.0, 1.0],
        storeAsInitialCamera: true,
        interpolationType,
      },
      parallelProjection: true,
    },
  } as Types.PublicViewportInput;
}
export function getViewport(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as Types.IVolumeViewport;
  if (!viewport) {
    console.error('Viewport is none');
  }
  return viewport;
}

function isDicomZoomSyncViewport(
  viewportId: string,
): viewportId is PanelComponentKeyType {
  return DICOM_ZOOM_SYNC_VIEWPORT_IDS.includes(
    viewportId as PanelComponentKeyType,
  );
}

function isFinitePositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function getChamberFitParallelScale({
  imageSize,
  elementWidth,
  elementHeight,
  zoomScale = 0.8,
}: {
  imageSize?: number;
  elementWidth: number;
  elementHeight: number;
  zoomScale?: number;
}) {
  if (
    !isFinitePositiveNumber(imageSize) ||
    !isFinitePositiveNumber(elementWidth) ||
    !isFinitePositiveNumber(elementHeight)
  )
    return undefined;

  const safeZoomScale = isFinitePositiveNumber(zoomScale) ? zoomScale : 0.8;
  const elementAspect = elementWidth / elementHeight;
  const fitWorldHeight =
    elementAspect > 1 ? imageSize : imageSize * (elementHeight / elementWidth);

  return Math.max((fitWorldHeight * safeZoomScale) / 2, 1e-3);
}

function isFinitePoint3(value: unknown): value is Types.Point3 {
  if (!value || typeof value !== 'object') return false;
  const point = value as ArrayLike<unknown>;
  return (
    typeof point.length === 'number' &&
    point.length >= 3 &&
    typeof point[0] === 'number' &&
    Number.isFinite(point[0]) &&
    typeof point[1] === 'number' &&
    Number.isFinite(point[1]) &&
    typeof point[2] === 'number' &&
    Number.isFinite(point[2])
  );
}

function getEnabledDicomZoomViewportIds() {
  const engine = getEngine(mainEngineId);
  return DICOM_ZOOM_SYNC_VIEWPORT_IDS.filter((viewportId) => {
    const viewport = engine.getViewport(viewportId) as
      | Types.IVolumeViewport
      | undefined;
    return !!viewport?.getImageData?.();
  });
}

function shouldSuppressDicomZoomSync(
  viewportId: string,
  parallelScale: number,
) {
  const expectedScale = suppressedDicomZoomSyncScales.get(viewportId);
  if (expectedScale === undefined) return false;

  if (Math.abs(expectedScale - parallelScale) <= DICOM_ZOOM_SYNC_EPSILON) {
    suppressedDicomZoomSyncScales.delete(viewportId);
    return true;
  }

  suppressedDicomZoomSyncScales.delete(viewportId);
  return false;
}

function syncDicomZoomViewports({
  sourceViewportId,
  previousParallelScale,
  nextParallelScale,
}: {
  sourceViewportId: string;
  previousParallelScale?: number;
  nextParallelScale?: number;
}) {
  if (!isDicomZoomSyncViewport(sourceViewportId)) return;
  if (
    !isFinitePositiveNumber(previousParallelScale) ||
    !isFinitePositiveNumber(nextParallelScale)
  )
    return;

  const scaleRatio = nextParallelScale / previousParallelScale;
  if (
    !Number.isFinite(scaleRatio) ||
    Math.abs(scaleRatio - 1) <= DICOM_ZOOM_SYNC_EPSILON
  )
    return;

  const engine = getEngine(mainEngineId);
  DICOM_ZOOM_SYNC_VIEWPORT_IDS.forEach((targetViewportId) => {
    if (targetViewportId === sourceViewportId) return;

    const targetViewport = engine.getViewport(
      targetViewportId,
    ) as Types.IVolumeViewport | undefined;
    if (!targetViewport) return;

    const targetCamera = targetViewport.getCamera();
    if (!targetCamera.parallelProjection) return;

    const currentScale = targetCamera.parallelScale ?? 1.0;
    if (!isFinitePositiveNumber(currentScale)) return;

    const syncedScale = Math.min(
      Math.max(currentScale * scaleRatio, MIN_PARALLEL_SCALE),
      MAX_PARALLEL_SCALE,
    );
    if (
      !Number.isFinite(syncedScale) ||
      Math.abs(syncedScale - currentScale) <= DICOM_ZOOM_SYNC_EPSILON
    )
      return;

    suppressedDicomZoomSyncScales.set(targetViewportId, syncedScale);
    targetCamera.parallelScale = syncedScale;
    targetViewport.setCamera(targetCamera);
    targetViewport.render();
  });
}

export function syncDicomZoomToAxial() {
  const engine = getEngine(mainEngineId);
  const axialViewport = engine.getViewport('Axial') as
    | VolumeViewport3D
    | undefined;
  if (!axialViewport || !axialViewport.getImageData()) return;

  const axialZoom = axialViewport.getZoom();
  if (!isFinitePositiveNumber(axialZoom)) return;

  isApplyingDicomZoomSync = true;
  try {
    DICOM_ZOOM_SYNC_VIEWPORT_IDS.forEach((targetViewportId) => {
      if (targetViewportId === 'Axial') return;

      const targetViewport = engine.getViewport(targetViewportId) as
        | VolumeViewport3D
        | undefined;
      if (!targetViewport || !targetViewport.getImageData()) return;

      const currentZoom = targetViewport.getZoom();
      if (
        Number.isFinite(currentZoom) &&
        Math.abs(currentZoom - axialZoom) <= DICOM_ZOOM_SYNC_EPSILON
      )
        return;

      targetViewport.setZoom(axialZoom);
      const syncedScale = targetViewport.getCamera().parallelScale;
      if (isFinitePositiveNumber(syncedScale)) {
        suppressedDicomZoomSyncScales.set(targetViewportId, syncedScale);
      }
      targetViewport.render();
    });
  } finally {
    isApplyingDicomZoomSync = false;
  }
}

function toPoint3(value: ArrayLike<number> | undefined): Types.Point3 | null {
  if (!value || value.length < 3) return null;
  const point = [Number(value[0]), Number(value[1]), Number(value[2])] as Types.Point3;
  return point.every((v) => Number.isFinite(v)) ? point : null;
}

function subtractPoint3(a: Types.Point3, b: Types.Point3): Types.Point3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]] as Types.Point3;
}

function addPoint3(a: Types.Point3, b: Types.Point3): Types.Point3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]] as Types.Point3;
}

function pointDistance(a: Types.Point3, b: Types.Point3) {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function dotPoint3(a: Types.Point3, b: Types.Point3) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function scalePoint3ByScalar(a: Types.Point3, scale: number): Types.Point3 {
  return [a[0] * scale, a[1] * scale, a[2] * scale] as Types.Point3;
}

function projectDeltaToCameraPlane(
  delta: Types.Point3,
  camera: Types.ICamera,
): Types.Point3 | null {
  const normal = toPoint3(camera.viewPlaneNormal);
  if (!normal) return null;

  const normalLength2 = dotPoint3(normal, normal);
  if (!Number.isFinite(normalLength2) || normalLength2 <= 1e-8) return null;

  const normalComponent = dotPoint3(delta, normal) / normalLength2;
  const projected = subtractPoint3(delta, scalePoint3ByScalar(normal, normalComponent));
  return pointDistance(projected, [0, 0, 0] as Types.Point3) > DICOM_PAN_SYNC_EPSILON
    ? projected
    : null;
}

function isSamePoint3(a: Types.Point3, b: Types.Point3, eps = DICOM_PAN_SYNC_EPSILON) {
  return pointDistance(a, b) <= eps;
}

function shouldSuppressDicomPanSync(
  viewportId: string,
  camera: Types.ICamera,
) {
  const expected = suppressedDicomPanSyncCameras.get(viewportId);
  if (!expected) return false;

  const focalPoint = toPoint3(camera.focalPoint);
  const position = toPoint3(camera.position);
  suppressedDicomPanSyncCameras.delete(viewportId);
  if (!focalPoint || !position) return false;

  return (
    isSamePoint3(focalPoint, expected.focalPoint) &&
    isSamePoint3(position, expected.position)
  );
}

function isInPlaneCameraTranslation({
  previousCamera,
  camera,
}: {
  previousCamera: Types.ICamera;
  camera: Types.ICamera;
}) {
  const previousFocalPoint = toPoint3(previousCamera.focalPoint);
  const nextFocalPoint = toPoint3(camera.focalPoint);
  const previousPosition = toPoint3(previousCamera.position);
  const nextPosition = toPoint3(camera.position);
  const normal = toPoint3(camera.viewPlaneNormal);
  if (!previousFocalPoint || !nextFocalPoint || !previousPosition || !nextPosition || !normal) {
    return false;
  }

  const focalDelta = subtractPoint3(nextFocalPoint, previousFocalPoint);
  const positionDelta = subtractPoint3(nextPosition, previousPosition);
  const movementLength = Math.hypot(focalDelta[0], focalDelta[1], focalDelta[2]);
  if (movementLength <= DICOM_PAN_SYNC_EPSILON) return false;

  const normalLength = Math.hypot(normal[0], normal[1], normal[2]) || 1;
  const normalComponent = Math.abs(
    (focalDelta[0] * normal[0] + focalDelta[1] * normal[1] + focalDelta[2] * normal[2]) /
      normalLength,
  );

  if (normalComponent > Math.max(0.01, movementLength * 0.05)) {
    return false;
  }

  return pointDistance(focalDelta, positionDelta) <= Math.max(0.05, movementLength * 0.1);
}

function getCameraPanCanvasDelta({
  viewport,
  previousCamera,
  camera,
}: {
  viewport: Types.IViewport;
  previousCamera: Types.ICamera;
  camera: Types.ICamera;
}): [number, number] | null {
  const previousFocalPoint = toPoint3(previousCamera.focalPoint);
  const nextFocalPoint = toPoint3(camera.focalPoint);
  if (!previousFocalPoint || !nextFocalPoint) return null;
  if (typeof (viewport as any).worldToCanvas !== 'function') return null;

  const previousCanvas = (viewport as any).worldToCanvas(previousFocalPoint) as [number, number];
  const nextCanvas = (viewport as any).worldToCanvas(nextFocalPoint) as [number, number];
  if (!previousCanvas || !nextCanvas) return null;

  const dx = nextCanvas[0] - previousCanvas[0];
  const dy = nextCanvas[1] - previousCanvas[1];
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
  if (Math.hypot(dx, dy) <= 0.01) return null;
  return [dx, dy];
}

function applyCanvasPanDeltaToViewport(
  viewport: Types.IViewport,
  canvasDelta: [number, number],
) {
  if (
    typeof (viewport as any).worldToCanvas !== 'function' ||
    typeof (viewport as any).canvasToWorld !== 'function'
  ) {
    return false;
  }

  const camera = viewport.getCamera();
  const focalPoint = toPoint3(camera.focalPoint);
  const position = toPoint3(camera.position);
  if (!focalPoint || !position) return false;

  const focalCanvas = (viewport as any).worldToCanvas(focalPoint) as [number, number];
  if (!focalCanvas) return false;

  const nextFocalPoint = (viewport as any).canvasToWorld([
    focalCanvas[0] + canvasDelta[0],
    focalCanvas[1] + canvasDelta[1],
  ]) as Types.Point3 | undefined;
  const nextFocal = toPoint3(nextFocalPoint);
  if (!nextFocal) return false;

  const worldDelta = subtractPoint3(nextFocal, focalPoint);
  const nextPosition = addPoint3(position, worldDelta);
  if (pointDistance(focalPoint, nextFocal) <= DICOM_PAN_SYNC_EPSILON) return false;

  const viewportId = (viewport as any).id as string | undefined;
  if (viewportId) {
    suppressedDicomPanSyncCameras.set(viewportId, {
      focalPoint: nextFocal,
      position: nextPosition,
    });
  }
  viewport.setCamera({
    ...camera,
    focalPoint: nextFocal,
    position: nextPosition,
  });
  viewport.render();
  return true;
}


function applyWorldPanDeltaToViewport(
  viewport: Types.IViewport,
  sourceWorldDelta: Types.Point3,
) {
  const camera = viewport.getCamera();
  const focalPoint = toPoint3(camera.focalPoint);
  const position = toPoint3(camera.position);
  if (!focalPoint || !position) return false;

  const targetDelta = projectDeltaToCameraPlane(sourceWorldDelta, camera);
  if (!targetDelta) return false;

  const nextFocal = addPoint3(focalPoint, targetDelta);
  const nextPosition = addPoint3(position, targetDelta);
  if (pointDistance(focalPoint, nextFocal) <= DICOM_PAN_SYNC_EPSILON) return false;

  const viewportId = (viewport as any).id as string | undefined;
  if (viewportId) {
    suppressedDicomPanSyncCameras.set(viewportId, {
      focalPoint: nextFocal,
      position: nextPosition,
    });
  }

  viewport.setCamera({
    ...camera,
    focalPoint: nextFocal,
    position: nextPosition,
  });
  viewport.render();
  return true;
}

function syncDicomPanViewports({
  sourceViewportId,
  sourceViewport,
  previousCamera,
  camera,
}: {
  sourceViewportId: string;
  sourceViewport: Types.IViewport;
  previousCamera: Types.ICamera;
  camera: Types.ICamera;
}) {
  if (!isDicomZoomSyncViewport(sourceViewportId)) return;
  if (!isInPlaneCameraTranslation({ previousCamera, camera })) return;

  const previousFocalPoint = toPoint3(previousCamera.focalPoint);
  const nextFocalPoint = toPoint3(camera.focalPoint);
  if (!previousFocalPoint || !nextFocalPoint) return;

  const sourceWorldDelta = subtractPoint3(nextFocalPoint, previousFocalPoint);
  if (pointDistance(sourceWorldDelta, [0, 0, 0] as Types.Point3) <= DICOM_PAN_SYNC_EPSILON) return;

  const engine = getEngine(mainEngineId);
  isApplyingDicomPanSync = true;
  try {
    DICOM_ZOOM_SYNC_VIEWPORT_IDS.forEach((targetViewportId) => {
      if (targetViewportId === sourceViewportId) return;
      const targetViewport = engine.getViewport(targetViewportId) as Types.IViewport | undefined;
      if (!targetViewport) return;
      applyWorldPanDeltaToViewport(targetViewport, sourceWorldDelta);
    });
  } finally {
    isApplyingDicomPanSync = false;
  }
}

export function centerDicomViewportOnWorld(
  viewportId: PanelComponentKeyType,
  world: Types.Point3,
) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as Types.IViewport | undefined;
  if (!viewport) return;

  const camera = viewport.getCamera();
  const focalPoint = toPoint3(camera.focalPoint);
  const position = toPoint3(camera.position);
  const target = toPoint3(world);
  if (!focalPoint || !position || !target) return;

  // 중요:
  // Ctrl+좌클릭 selection은 CrosshairsTool이 slice/axis 위치를 이미 갱신한다.
  // 여기서 world 전체 delta를 focalPoint에 그대로 적용하면 viewPlaneNormal 방향까지
  // 같이 움직이면서 selection 결과와 focus-center 동작이 서로 충돌할 수 있다.
  // 따라서 중앙 정렬은 target world를 현재 viewport plane 위로 투영한 in-plane delta만
  // 적용한다. slice/axis 좌표는 jumpToWorld/CrosshairsTool이 담당한다.
  const rawDelta = subtractPoint3(target, focalPoint);
  const planeDelta = projectDeltaToCameraPlane(rawDelta, camera);
  if (!planeDelta) return;
  if (pointDistance(planeDelta, [0, 0, 0] as Types.Point3) <= DICOM_PAN_SYNC_EPSILON) return;

  const nextFocalPoint = addPoint3(focalPoint, planeDelta);
  const nextPosition = addPoint3(position, planeDelta);
  suppressedDicomPanSyncCameras.set(viewportId, {
    focalPoint: nextFocalPoint,
    position: nextPosition,
  });
  viewport.setCamera({
    ...camera,
    focalPoint: nextFocalPoint,
    position: nextPosition,
  });
  viewport.render();
}

function runWithoutDicomPanZoomSync(action: () => void) {
  const previousPanSync = isApplyingDicomPanSync;
  const previousZoomSync = isApplyingDicomZoomSync;
  isApplyingDicomPanSync = true;
  isApplyingDicomZoomSync = true;
  try {
    action();
  } finally {
    isApplyingDicomPanSync = previousPanSync;
    isApplyingDicomZoomSync = previousZoomSync;
  }
}

export function centerDicomViewportsOnWorld(world: Types.Point3) {
  cancelPendingDicomFitToScreen();
  runWithoutDicomPanZoomSync(() => {
    DICOM_ZOOM_SYNC_VIEWPORT_IDS.forEach((viewportId) => {
      centerDicomViewportOnWorld(viewportId, world);
    });
  });
}

export function syncDicomPanToAxial() {
  if (!getViewportIdsEnabled(DICOM_ZOOM_SYNC_VIEWPORT_IDS)) return;

  const engine = getEngine(mainEngineId);
  const axialViewport = engine.getViewport('Axial') as
    | Types.IViewport
    | undefined;
  if (!axialViewport) return;

  const axialFocalPoint = toPoint3(axialViewport.getCamera().focalPoint);
  if (!axialFocalPoint) return;

  isApplyingDicomPanSync = true;
  try {
    DICOM_ZOOM_SYNC_VIEWPORT_IDS.forEach((targetViewportId) => {
      if (targetViewportId === 'Axial') return;

      const targetViewport = engine.getViewport(targetViewportId) as
        | Types.IViewport
        | undefined;
      if (!targetViewport) return;

      const targetCamera = targetViewport.getCamera();
      const targetFocalPoint = toPoint3(targetCamera.focalPoint);
      const targetPosition = toPoint3(targetCamera.position);
      if (!targetFocalPoint || !targetPosition) return;

      const deltaToAxialFocal = subtractPoint3(
        axialFocalPoint,
        targetFocalPoint,
      );
      const targetPlaneDelta = projectDeltaToCameraPlane(
        deltaToAxialFocal,
        targetCamera,
      );
      if (!targetPlaneDelta) return;

      const nextFocalPoint = addPoint3(targetFocalPoint, targetPlaneDelta);
      const nextPosition = addPoint3(targetPosition, targetPlaneDelta);
      if (pointDistance(targetFocalPoint, nextFocalPoint) <= DICOM_PAN_SYNC_EPSILON) {
        return;
      }

      suppressedDicomPanSyncCameras.set(targetViewportId, {
        focalPoint: nextFocalPoint,
        position: nextPosition,
      });

      targetViewport.setCamera({
        ...targetCamera,
        focalPoint: nextFocalPoint,
        position: nextPosition,
      });
      targetViewport.render();
    });
  } finally {
    isApplyingDicomPanSync = false;
  }
}


export function fitDicomZoomToAxial(zoomScale?: number) {
  const enabledViewportIds = getEnabledDicomZoomViewportIds();
  if (!enabledViewportIds.includes('Axial')) return;
  updateViewportZoom('Axial', 0, zoomScale);
  syncDicomZoomToAxial();
}

function fitDicomViewportListToScreen({
  syncToAxial,
  zoomScale,
}: {
  syncToAxial: boolean;
  zoomScale?: number;
}) {
  const enabledViewportIds = getEnabledDicomZoomViewportIds();
  if (enabledViewportIds.length === 0) return;

  if (syncToAxial && enabledViewportIds.includes('Axial')) {
    fitDicomZoomToAxial(zoomScale);
    return;
  }

  enabledViewportIds.forEach((viewportId) => {
    updateViewportZoom(viewportId as PanelComponentKeyType, 0, zoomScale);
  });
}

type FitDicomViewportsToScreenOptions = {
  syncToAxial?: boolean;
  zoomScale?: number;
  repeat?: boolean;
};

const DICOM_FIT_REPEAT_DELAYS = [180];
let pendingDicomFitFrame: number | null = null;
let pendingDicomFitTimers: number[] = [];
let pendingDicomFitOptions: Required<
  Omit<FitDicomViewportsToScreenOptions, 'zoomScale'>
> &
  Pick<FitDicomViewportsToScreenOptions, 'zoomScale'> = {
  syncToAxial: true,
  repeat: false,
  zoomScale: undefined,
};

function clearPendingDicomFitTimers() {
  pendingDicomFitTimers.forEach((timer) => window.clearTimeout(timer));
  pendingDicomFitTimers = [];
}

function clearPendingDicomFitFrame() {
  if (pendingDicomFitFrame === null) return;
  window.cancelAnimationFrame(pendingDicomFitFrame);
  pendingDicomFitFrame = null;
}

function cancelPendingDicomFitToScreen() {
  clearPendingDicomFitFrame();
  clearPendingDicomFitTimers();
}

function runDicomFitToScreen({
  syncToAxial,
  zoomScale,
}: {
  syncToAxial: boolean;
  zoomScale?: number;
}) {
  resizeEngine();
  fitDicomViewportListToScreen({ syncToAxial, zoomScale });
}

export function fitDicomViewportsToScreen({
  syncToAxial = true,
  zoomScale,
  repeat = false,
}: FitDicomViewportsToScreenOptions = {}) {
  pendingDicomFitOptions = {
    syncToAxial,
    zoomScale,
    repeat,
  };

  const fit = () => {
    pendingDicomFitFrame = null;
    runDicomFitToScreen(pendingDicomFitOptions);
  };

  clearPendingDicomFitFrame();
  clearPendingDicomFitTimers();

  pendingDicomFitFrame = window.requestAnimationFrame(() => {
    pendingDicomFitFrame = window.requestAnimationFrame(fit);
  });

  if (repeat) {
    const repeatOptions = pendingDicomFitOptions;
    pendingDicomFitTimers = DICOM_FIT_REPEAT_DELAYS.map((delay) =>
      window.setTimeout(() => {
        runDicomFitToScreen(repeatOptions);
      }, delay),
    );
  }
}

// 렌더링 엔진 생성 및 기타 초기화 작업...
export async function initializeCornerstone(
  afterInitialize: () => void,
  config: Cornerstone3DConfig,
) {
  //PolySeg 타입이 달라서 맞춰야
  const {
    canComputeRequestedRepresentation,
    computeContourData,
    computeLabelmapData,
    computeSurfaceData,
    updateSurfaceData,
    init,
  } = polySeg;

  //PolySeg 타입을 맞춘것
  const polySegAddOnAdapter: ReturnType<typeof getPolySeg> = {
    canComputeRequestedRepresentation(
      segmentationId: string,
      representationType: SegmentationRepresentations,
    ): boolean {
      return canComputeRequestedRepresentation(
        segmentationId,
        representationType as any,
      );
    },
    computeContourData,
    computeLabelmapData,
    computeSurfaceData,
    updateSurfaceData(
      segmentationId: string,
      options: PolySegConversionOptions,
    ): Promise<ToolTypes.SurfaceSegmentationData> {
      updateSurfaceData(segmentationId);
      return new Promise<ToolTypes.SurfaceSegmentationData>((resolve) => {
        console.log(resolve);
      });
    },
    init,
  };

  await csRenderInit(config);

  await csToolsInit({
    addons: {
      polySeg: polySegAddOnAdapter,
    },
  });

  // const niftiImageLoaderFn: Types.ImageLoaderFn = (
  //   imageId: string,
  //   options?: Record<string, unknown>
  // ) => {
  //   // 1) 기존 로더 호출
  //   const loaderObj = cornerstoneNiftiImageLoader(imageId);
  //   // 2) 반환할 객체를 ImageLoaderFn 형태로 변환
  //   return {
  //     // 원래 Promise<IImage> → then()으로 Record<string,unknown>로 매핑
  //     promise: loaderObj.promise.then((img: Types.IImage) => {
  //       // 필요한 경우 options를 활용한 추가 후처리도 가능
  //       // 예: if (options?.foo) { ... }
  //       return img as unknown as Record<string, unknown>;
  //     }),
  //     // cancelFn, decache는 그대로 전달
  //     cancelFn: loaderObj.cancelFn,
  //     decache: loaderObj.decache,
  //   };
  // };
  // imageLoader.registerImageLoader('nifti',niftiImageLoaderFn);
  init();
  afterInitialize();
}

export function clearCornerstoneResource(
  keepSeriesId?: string,
  deleteExceptIds?: string[], //현재 시리즈에서 지울 때 이 목록을 제외하고 지워라.
) {
  const isDeleteExceptIds = deleteExceptIds !== undefined;
  const volumeIds = [
    `PREVIEW_${keepSeriesId}`, // 프리뷰는 안지움
    `ORIGINAL_${keepSeriesId}`,
    `${keepSeriesId}.lawt`,
    `${keepSeriesId}.lawtHalf`,
    `${keepSeriesId}_labelmap`,
    `${keepSeriesId}_labelmapHalf`,
    `${keepSeriesId}.branches`,
    `${keepSeriesId}_branchesHalf`,
    `${keepSeriesId}_manual`,
    `${keepSeriesId}_regulator`,
  ];

  const segmentationIds = [
    `segmentation_${keepSeriesId}_labelmap`,
    `segmentation_${keepSeriesId}_labelmapHalf`,
    `segmentation_${keepSeriesId}_manual`,
    `segmentation_${keepSeriesId}_regulator`,
  ];
  const volumeIdSet = new Set(volumeIds);
  const segmentationIdSet = new Set(segmentationIds);
  const deleteExceptIdSet = new Set(deleteExceptIds ?? []);
  const shouldRemoveByTarget = (isTarget: boolean) =>
    isTarget === isDeleteExceptIds;
  const removeCachedVolume = (volumeId: string, imageIds: string[]) => {
    if (isDeleteExceptIds && deleteExceptIdSet.has(volumeId)) return;
    imageIds.forEach((imageId) => {
      cache.removeImageLoadObject(imageId, { force: true });
    });
    cache.removeVolumeLoadObject(volumeId);
  };

  const viewportIds = getEnabledElements().map((v) => v.viewportId);
  viewportIds.forEach((viewportId) => {
    segmentation.state
      .getSegmentationRepresentations(viewportId)
      .forEach((segRep) => {
        const isTarget = segmentationIdSet.has(segRep.segmentationId);
        if (!shouldRemoveByTarget(isTarget)) return;
        segmentation.state.removeLabelmapRepresentation(
          viewportId,
          segRep.segmentationId,
          true,
        );
      });
  });

  segmentation.state.getSegmentations().forEach((seg) => {
    const isTarget = segmentationIdSet.has(seg.segmentationId);
    if (!shouldRemoveByTarget(isTarget)) return;
    segmentation.removeSegmentation(seg.segmentationId);
  });
  // segmentation.removeAllSegmentationRepresentations();
  // segmentation.removeAllSegmentations();

  // ToolGroupManager.getAllToolGroups().forEach(toolgroup=>toolgroup.removeViewports(mainEngineId))

  // const engine = getEngine(mainEngineId);
  // getEnabledElements().forEach(({viewportId})=>engine.disableElement(viewportId))

  // cache.getVolumes().forEach(volume=>volume.removeFromCache())

  // getEnabledElements().forEach(element=>element.viewport.)

  if (!keepSeriesId) {
    cache.purgeCache();
  } else {
    const cachedVolumes = cache.getVolumes();
    if (isDeleteExceptIds) {
      console.log('날리기전.', cachedVolumes);
      cachedVolumes.forEach(({ volumeId, imageIds }) => {
        if (!volumeIdSet.has(volumeId)) return;
        removeCachedVolume(volumeId, imageIds);
      });
      console.log('날립니다.', cache.getVolumes());
    } else {
      // 스터디에서 시리즈 옮기는 상황이면, 프리뷰는 냄겨야해
      cachedVolumes.forEach(({ volumeId, imageIds }) => {
        if (volumeIdSet.has(volumeId) || volumeId.startsWith('PREVIEW_')) {
          return;
        }
        // 한번 오리진도 남겨보자
        // if (volumeId.startsWith('ORIGINAL_')) return;

        // // EF 땜에 Half도 남겨
        // if (volumeId.endsWith('_labelmapHalf')) return;

        removeCachedVolume(volumeId, imageIds);
      });
    }

    clearSegmentation(keepSeriesId, 'manual');
    clearSegmentation(keepSeriesId, 'regulator');
  }
  clearDownloadBufferQueue();
  clearSegmentManager();
  console.log(segmentationIds);
  console.log(
    immediateRawBufferPool,
    segmentation.state.getSegmentations(),
    segmentation.state.getSegmentationRepresentations('Axial'),
    segmentation.state.getSegmentationRepresentations('Coronal'),
    segmentation.state.getSegmentationRepresentations('Sagittal'),
    cache.getVolumes(),
  );
  console.log('cache.getBytesAvailable', cache.getBytesAvailable());
}

// //여기 좀 깔끔하게 해야해
// // 여기 지금 하나도 안될듯
// function updateCornerstoneConfiguration(config: Partial<Cornerstone3DConfig>) {
//   const engine = getRenderingEngine(mainEngineId);
//   if (!engine) {
//     console.warn('엔진이 없는데요? 설정을 못바꿔요');
//     return;
//   }
//   const nowConfig = getConfiguration();
//   console.log(getEngine(mainEngineId).getViewports(), {
//     ...nowConfig,
//     ...config,
//   });
//   // const engine = getEngine(mainEngineId);
//   const viewports = engine.getViewports() as VolumeViewport3D[];
//   const idElementVolumes = viewports.map((viewport) => {
//     const element = viewport.element;
//     const id = viewport.id;
//     const volumeId = viewport.getVolumeId();
//     const zoom = viewport.getZoom();
//     const properties = viewport.getProperties();
//     const blendMode = viewport.getDefaultActor().blendMode;
//     engine.disableElement(viewport.id);

//     return { id, element, volumeId, zoom, properties, blendMode };
//   });
//   setConfiguration({ ...nowConfig, ...config });

//   idElementVolumes.forEach(async (value) => {
//     const { id, element, volumeId, zoom, properties, blendMode } = value;
//     engine.enableElement(
//       getPublicViewportInput(
//         id as PanelComponentKeyType,
//         element as HTMLDivElement,
//       ),
//     );
//     const vp = engine.getViewport(
//       id as PanelComponentKeyType,
//     ) as Types.IVolumeViewport;
//     await setVolumesForViewports(
//       engine,
//       [
//         {
//           volumeId,
//           slabThickness: properties.slabThickness,
//           blendMode,
//         } as Types.IVolumeInput,
//       ],
//       [id],
//     );

//     // vp.render();
//     vp.setProperties(properties);
//     await updateViewportZoom(id as PanelComponentKeyType, zoom);

//     console.log('굉장히 레지하죠?');
//     registerToolGroupForViewport(id as PanelComponentKeyType);
//   });
// }

// export function setGPUAcceleration(flag: boolean) {
//   updateCornerstoneConfiguration({
//     rendering: {
//       ...initialCornerstone3DConfig.rendering,
//       useCPURendering: !flag,
//     },
//   });
// }
// export function setRenderingQuality(renderingQuality: RenderingQualityKeyType) {
//   updateCornerstoneConfiguration({
//     gpuTier: { tier: renderingQualityMapping[renderingQuality] },
//     rendering: {
//       ...initialCornerstone3DConfig.rendering,
//       preferSizeOverAccuracy: renderingQualityMapping[renderingQuality] === 1,
//     },
//   });
// }

// 이니셜과 기본 모니터 1을 생성함.
// 추후에 모니터 추가되는거 확인해봐야해

export function volumeCacheVolumeAddedEvent({
  evt,
  callback,
  callbackTag,
  forceCallCallback,
}: {
  evt: Event;
  callback: (volume: any) => void;
  callbackTag: string;
  forceCallCallback?: boolean;
}) {
  const cachedVolume = (evt as EventTypes.VolumeCacheVolumeAddedEvent).detail
    .volume;
  const { volumeId, volume } = cachedVolume;
  const callCallback =
    volumeId.split('_')[0] === callbackTag || forceCallCallback === true;
  console.log('왔능가?', volumeId, volume);

  if (volume) {
    const images = volume.getCornerstoneImages();
    const { imageData } = volume;
    const { voiLut } = volume.metadata;

    if (images.length) {
      let min = images[0].minPixelValue;
      let max = images[0].maxPixelValue;

      for (let i = 1; i < images.length; ++i) {
        if (images[i].minPixelValue < min) min = images[i].minPixelValue;
        if (images[i].maxPixelValue > max) max = images[i].maxPixelValue;
      }
      volume.additionalDetails = {
        ...volume.additionalDetails,
        HUMax: max,
        HUMin: min,
      };
    }
    // if (imageData && !!voiLut.length) {
    //   imageData.set({
    //     width: voiLut[0].windowWidth,
    //     level: voiLut[0].windowCenter,
    //   });
    // }
  }
  //위에꺼 하고 넣어줘야

  if (callCallback) {
    callback(volume);
  }
}

export function addCornerstoneCoreEvents({
  volumeCacheVolumeAdded,
}: {
  volumeCacheVolumeAdded: (evt: Event) => void;
}) {
  // 볼륨마다 min/max 계산해서 넣어둠
  eventTarget.addEventListener(
    Enums.Events.VOLUME_CACHE_VOLUME_ADDED,
    volumeCacheVolumeAdded,
  );
}
export function removeCornerstoneCoreEvents({
  volumeCacheVolumeAdded,
}: {
  volumeCacheVolumeAdded: (evt: Event) => void;
}) {
  eventTarget.removeEventListener(
    Enums.Events.VOLUME_CACHE_VOLUME_ADDED,
    volumeCacheVolumeAdded,
  );
}
export async function setup({
  renderingEngineId = mainEngineId,
  afterInitialize,
  config = initialCornerstone3DConfig,
}: {
  renderingEngineId?: string;
  afterInitialize: () => void;
  config?: Cornerstone3DConfig;
}) {
  await initializeCornerstone(afterInitialize, config);
  getEngine(renderingEngineId);
}

export function unregisterGeometryCacheAddedEvent(
  handler: (geometryId: string) => Promise<void>,
) {
  eventTarget.removeEventListener(
    Enums.Events.GEOMETRY_CACHE_GEOMETRY_ADDED,
    async (evt: CustomEvent) => {
      const { geometryId } = evt.detail.geometry;
      handler(geometryId);
    },
  );
}

export async function registerVolumeCacheAddedEvent(
  targetVolumeId: string,
  handler: (volumeId: string) => Promise<void>,
) {
  eventTarget.addEventListener(
    Enums.Events.IMAGE_VOLUME_LOADING_COMPLETED,
    async (evt: EventTypes.ImageVolumeLoadingCompletedEvent) => {
      const { volumeId } = evt.detail;
      if (volumeId === targetVolumeId) {
        handler(targetVolumeId);
        console.log('오이오잉11');
      }
    },
  );
}

export async function unregisterVolumeCacheAddedEvent(
  targetVolumeId: string,
  handler: (volumeId: string) => Promise<void>,
) {
  eventTarget.addEventListener(
    Enums.Events.IMAGE_VOLUME_LOADING_COMPLETED,
    async (evt: EventTypes.ImageVolumeLoadingCompletedEvent) => {
      // const {geometryId} = evt.detail.geometry
      // handler(geometryId);
      console.log('오이오잉22');
    },
  );
}

// export function initNiftiVolumeLoader(accessToken : string){
//   init({
//     beforeSend: (xhr, headers, url) => {
//       headers['Accept'] = '*/*';
//       headers['Authorization'] = `Bearer ${accessToken}`;
//       headers['Content-Type'] = 'application/octet-stream';
//       return headers;
//     }
//   });
// }

export async function run(renderingEngineId: string, viewportId: string) {}

export function viewportToImage(
  viewportId: string | undefined,
  type?: string,
  width?: number,
  height?: number,
) {
  if (!viewportId) return;
  const enabledElement = getEnabledElementByViewportId(viewportId);
  const viewport = enabledElement.viewport;
  const canvas = viewport.canvas as HTMLCanvasElement;

  const finalWidth = width || 512;
  const finalHeight = height || 512;

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = finalWidth;
  tempCanvas.height = finalHeight;

  const ctx = tempCanvas.getContext('2d');
  if (!ctx) return;

  ctx.drawImage(canvas, 0, 0, finalWidth, finalHeight);

  return tempCanvas.toDataURL(`image/${type}`);
}

export function deepClone(obj: unknown) {
  return utilities.deepClone(obj);
}
export function captureImage(
  viewportId: string | undefined,
  type?: string,
  width?: number,
  height?: number,
) {
  if (!viewportId) return;
  const enabledElement = getEnabledElementByViewportId(viewportId);
  const viewport = enabledElement.viewport;

  const offscreenMultiRenderWindow =
    getEngine(mainEngineId).offscreenMultiRenderWindow;
  const renderWindow = offscreenMultiRenderWindow.getRenderWindow();
  const renderer = offscreenMultiRenderWindow.getRenderer();
  if (!renderWindow) return;

  renderWindow.render();
  const openGLRenderWindow = offscreenMultiRenderWindow.getOpenGLRenderWindow();
  const { sx, sy, sWidth, sHeight } = viewport;
  const canvas = viewport.canvas as HTMLCanvasElement;
  const context = openGLRenderWindow.get3DContext();
  const offScreenCanvas = context.canvas;
  const onScreenContext = canvas.getContext('2d');
  const { width: dWidth, height: dHeight } = canvas;

  if (!onScreenContext) {
    console.error('2D 컨텍스트를 가져오지 못했습니다.');
    return null;
  }

  onScreenContext.drawImage(
    offScreenCanvas,
    sx,
    sy,
    sWidth,
    sHeight,
    0, //dx
    0, // dy
    dWidth,
    dHeight,
  );
  return canvas.toDataURL(`image/${type}`);
}
const manualTool = ['Brush', 'Eraser', 'FloodFill', 'Connect2D'];
export function activeExclusiveTools(toolName: string) {
  const toolGroupId = 'sharedToolGroup';
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    console.warn('툴그룹 없음:');
    return;
  }

  const prevToolName = toolGroup.getActivePrimaryMouseButtonTool();
  const newToolName = TOOL_NAME_MAP[toolName];

  let isManualTool = false;
  //모디애들은 따로 안넣고
  if (!newToolName) {
    // 여기서 확인하고
    isManualTool = manualTool.includes(toolName);
    if (!isManualTool) {
      console.warn(`도구 ID '${toolName}'에 해당하는 toolName이 없습니다.`);
      return;
    }
  }

    toolGroup.setToolPassive(prevToolName);
  if (isManualTool) {
    console.log('왔다', toolName);
    toolGroup.setViewportsCursorByToolName('');
    return;
  }

  if(isToolInteracting()){
    deactivateExclusiveTools({});
  }
  toolGroup.setToolActive(newToolName, {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
  });
}

export function isToolInteracting(): boolean {
  //드래그,측정 도구 활성화 유무 확인
  return state.isInteractingWithTool;
}

export function deactivateExclusiveTools({
  viewportId,
  isScrollTool = false,
}: {
  viewportId?: string;
  isScrollTool?: boolean;
}) {
  const toolGroupId = 'sharedToolGroup';
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (!toolGroup) {
    return;
  }

  if (!state.isInteractingWithTool) return;

  // 진행 중인 드래그/측정이 있으면 취소해서 화면 전환 시 크래시 방지
  const elements: HTMLElement[] = [];
  if (viewportId) {
    const enabledElement = getEnabledElementByViewportId(viewportId);
    if (!enabledElement) {
      console.warn('enabledElement 없음:', viewportId);
      return;
    }
    const element = enabledElement?.viewport?.element;
    if (element) elements.push(element);
  } else {
    const viewportsInfo = toolGroup.getViewportsInfo();
    viewportsInfo.forEach(({ viewportId }) => {
      const enabledElement = getEnabledElementByViewportId(viewportId);
      if (enabledElement) {
        const element = enabledElement?.viewport?.element;
        if (element) elements.push(element);
      }
    });
  }

  elements.forEach((el) => {
    cornerstoneTools.cancelActiveManipulations?.(el as HTMLDivElement);
  });

  if (isScrollTool) {
    toolGroup.setToolActive(CustomStackScrollTool.toolName, {
      bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
    });
  } else {
    const activeTool = toolGroup.getActivePrimaryMouseButtonTool();
    if (!activeTool) return;
    toolGroup.setToolPassive(activeTool);
    toolGroup.setToolActive(activeTool, {
      bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
    });
  }
}


export function transformViewport(
  viewportID: string,
  RenderingEngineID: string,
  key: string,
) {
  const renderingEngine = getRenderingEngine(RenderingEngineID);
  if (!renderingEngine) {
    console.warn('RenderingEngine not found:', RenderingEngineID);
    return;
  }

  const viewport = renderingEngine.getViewport(viewportID) as VolumeViewport3D;
  if (!viewport) {
    console.warn('Viewport not found:', viewportID);
    return;
  }

  if (key === 'flipHorizontal') {
    viewport.setViewPresentation({
      flipHorizontal: true,
    });
  } else if (key === 'flipVertical') {
    viewport.setViewPresentation({
      flipVertical: true,
    });
  } else if (key === 'rotateCW90') {
    const currentRotate = viewport.getRotation();
    viewport.setViewPresentation({
      rotation: currentRotate + 90,
    });
  } else if (key === 'rotateCCW90') {
    const currentRotate = viewport.getRotation();
    const next = (currentRotate - 90 + 360) % 360;
    viewport.setViewPresentation({
      rotation: next,
    });
  } else if (key === 'resetRotation') {
    const currentRotate = viewport.getRotation();
    viewport.setViewPresentation({
      rotation: 0,
    });
  }

  viewport.render();
}
export function VRToolForViewport() {
  const toolGroupId = 'VRTool';
  let labelMapTool = ToolGroupManager.getToolGroup(toolGroupId);

  if (!labelMapTool) {
    labelMapTool = ToolGroupManager.createToolGroup(toolGroupId);

    if (!labelMapTool) {
      console.error('labelMapTool 생성 실패');
      return;
    }

    addTool(TrackballRotateTool);
    labelMapTool.addTool(TrackballRotateTool.toolName);
    labelMapTool.setToolActive(TrackballRotateTool.toolName, {
      bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
    });

    addTool(PanTool);
    labelMapTool.addTool(PanTool.toolName);
    labelMapTool.setToolActive(PanTool.toolName, {
      bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
    });

    addTool(ZoomTool);

    labelMapTool.addTool(ZoomTool.toolName, {
      zoomToCenter: true,
    });
    labelMapTool.setToolActive(ZoomTool.toolName, {
      bindings: [
        {
          mouseButton: ToolEnums.MouseBindings.Wheel,
          modifierKey: ToolEnums.KeyboardBindings.Ctrl,
        },
        {
          mouseButton: ToolEnums.MouseBindings.Secondary,
          modifierKey: ToolEnums.KeyboardBindings.Shift,
        },
      ],
    });

    const zoom = labelMapTool.getToolInstance(ZoomTool.toolName);
    zoom._scroll = (evt: ToolTypes.EventTypes.MouseWheelEventType) => {
      console.log('이건 뭐고');
      const { wheel, element } = evt.detail;
      const { direction } = wheel;

      const enabledElement = getEnabledElement(element);
      if (!enabledElement) return;

      const { viewport } = enabledElement;
      const camera = viewport.getCamera();

      if (!camera.parallelProjection) return;

      const currentScale = camera.parallelScale ?? 1.0;

      // 줌 팩터 + 제한 범위 (선택)
      const zoomFactor = 0.1;
      const newScale = currentScale * (1 + zoomFactor * direction);
      const clampedScale = Math.min(
        Math.max(newScale, MIN_PARALLEL_SCALE),
        MAX_PARALLEL_SCALE,
      ); // 10~500 범위 제한

      camera.parallelScale = clampedScale;
      viewport.setCamera(camera);
      viewport.render();
    };
    zoom.mouseWheelCallback = zoom._scroll;

    addTool(LengthTool);
    labelMapTool.addTool(LengthTool.toolName);
    labelMapTool.setToolActive(LengthTool.toolName);

    addTool(AngleTool);
    labelMapTool.addTool(AngleTool.toolName);
    labelMapTool.setToolActive(AngleTool.toolName);

    addTool(CobbAngleTool);
    labelMapTool.addTool(CobbAngleTool.toolName);
    labelMapTool.setToolActive(CobbAngleTool.toolName);

    addTool(EllipticalROITool);
    labelMapTool.addTool(EllipticalROITool.toolName);
    labelMapTool.setToolActive(EllipticalROITool.toolName);
  }
  //고정된값
  labelMapTool.addViewport('VR', mainEngineId);
}

export function unregisterToolGroupForViewport(viewportId: string) {
  ['sharedToolGroup', 'ChamberToolGroup'].forEach((toolGroupId) => {
    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    if (toolGroup) {
      toolGroup.removeViewports(mainEngineId, viewportId);
    }
  });
}
export function registerToolGroupForChamber(viewportId: string,onDragUpdateCallback: OnDragUpdateCallback) {
  const toolGroupId = 'ChamberToolGroup';
  let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

    if (!toolGroup) {
      console.error('ToolGroup 생성 실패');
      return;
    }

    // addTool(StackScrollTool);
    // toolGroup.addTool(StackScrollTool.toolName);
    // toolGroup.setToolActive(StackScrollTool.toolName, {
    //   bindings: [
    //     { mouseButton: ToolEnums.MouseBindings.Primary },
    //     { mouseButton: ToolEnums.MouseBindings.Wheel }
    //   ],

    // });
    // toolGroup.setToolConfiguration(StackScrollTool.toolName,{ invert: true});

    // addTool(ZoomTool);
    // toolGroup.addTool(ZoomTool.toolName, {
    //   zoomToCenter: true,
    // });
    // const zoom = toolGroup.getToolInstance(ZoomTool.toolName);
    // zoom._scroll = (evt: ToolTypes.EventTypes.MouseWheelEventType) => {
    //   const { wheel, element } = evt.detail;
    //   const { direction } = wheel;
    //   const enabledElement = getEnabledElement(element);
    //   if (!enabledElement) return;
    //   const { viewport } = enabledElement;
    //   const camera = viewport.getCamera();
    //   if (!camera.parallelProjection) return;
    //   const currentScale = camera.parallelScale ?? 1.0;
    //   const zoomFactor = 0.1;
    //   const newScale = currentScale * (1 + zoomFactor * direction);
    //   camera.parallelScale = Math.min(Math.max(newScale, 10), 500);
    //   viewport.setCamera(camera);
    //   viewport.render();
    // };
    // zoom._drag = (evt: ToolTypes.EventTypes.MouseDragEventType) => {
    //   const { deltaPoints, element } = evt.detail;
    //   const dy = deltaPoints.page[1];

    //   const enabledElement = getEnabledElement(element);
    //   if (!enabledElement) return;
    //   const viewport = enabledElement.viewport as Types.IVolumeViewport;
    //   const camera = viewport.getCamera();
    //   if (!camera.parallelProjection) return;

    //   const currentScale = camera.parallelScale ?? 1.0;
    //   const zoomFactor = 0.1;
    //   const newScale = currentScale * (1 - zoomFactor * dy);
    //   camera.parallelScale = Math.min(Math.max(newScale, 10), 500);

    //   viewport.setCamera(camera);
    //   viewport.render();
    // };
    // zoom.mouseDragCallback = zoom._drag;
    // zoom.mouseWheelCallback = zoom._scroll;
    // toolGroup.setToolActive(ZoomTool.toolName, {
    //   bindings: [
    //     {
    //       mouseButton: ToolEnums.MouseBindings.Wheel,
    //       modifierKey: ToolEnums.KeyboardBindings.Ctrl
    //     },
    //     {
    //       mouseButton: ToolEnums.MouseBindings.Secondary,
    //       modifierKey: ToolEnums.KeyboardBindings.Shift
    //     }
    //   ],
    // });

    // addTool(PanTool);
    // toolGroup.addTool(PanTool.toolName);
    // toolGroup.setToolActive(PanTool.toolName, {
    //   bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
    // });

    addTool(CustomWindowLevelTool);
    toolGroup.addTool(CustomWindowLevelTool.toolName);
    toolGroup.setToolConfiguration(CustomWindowLevelTool.toolName, {
      onDragUpdate: onDragUpdateCallback
    });
    toolGroup.setToolActive(CustomWindowLevelTool.toolName, {
      bindings: [
        {
          mouseButton: ToolEnums.MouseBindings.Primary,
          modifierKey: ToolEnums.KeyboardBindings.Shift,
        },
        {
          mouseButton: ToolEnums.MouseBindings.Auxiliary,
        },
      ],
    });

    // addTool(PlanarRotateTool)
    // toolGroup.addTool(PlanarRotateTool.toolName);
    // toolGroup.setToolActive(PlanarRotateTool.toolName, {
    //   bindings:[
    //     {
    //       mouseButton: ToolEnums.MouseBindings.Secondary,
    //       modifierKey: ToolEnums.KeyboardBindings.Ctrl
    //     },
    //   ]
    // });
  }

  if (!toolGroup.getViewportIds().includes(viewportId)) {
    toolGroup.addViewport(viewportId, mainEngineId);
  }
}

export function registerToolGroupForViewport(viewportId: string,onDragUpdateCallback: OnDragUpdateCallback) {
  const toolGroupId = 'sharedToolGroup';
  let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

    if (!toolGroup) {
      console.error('ToolGroup 생성 실패');
      return;
    }

    addTool(CustomStackScrollTool);
    toolGroup.addTool(CustomStackScrollTool.toolName);
    toolGroup.setToolActive(CustomStackScrollTool.toolName, {
      bindings: [
        { mouseButton: ToolEnums.MouseBindings.Primary },
        { mouseButton: ToolEnums.MouseBindings.Wheel },
      ],
    });
    toolGroup.setToolConfiguration(CustomStackScrollTool.toolName, { invert: true });
    addTool(ZoomTool);
    toolGroup.addTool(ZoomTool.toolName, {
      zoomToCenter: true,
    });
    const zoom = toolGroup.getToolInstance(ZoomTool.toolName);
    zoom._scroll = (evt: ToolTypes.EventTypes.MouseWheelEventType) => {
      const { wheel, element } = evt.detail;
      const { direction } = wheel;
      const enabledElement = getEnabledElement(element);
      if (!enabledElement) return;
      const { viewport } = enabledElement;
      const camera = viewport.getCamera();
      if (!camera.parallelProjection) return;
      const currentScale = camera.parallelScale ?? 1.0;
      const zoomFactor = 0.1;
      const newScale = currentScale * (1 + zoomFactor * direction);
      camera.parallelScale = Math.min(
        Math.max(newScale, MIN_PARALLEL_SCALE),
        MAX_PARALLEL_SCALE,
      );
      viewport.setCamera(camera);
      viewport.render();
    };
    zoom._drag = (evt: ToolTypes.EventTypes.MouseDragEventType) => {
      const { deltaPoints, element } = evt.detail;
      const dy = deltaPoints.page[1];

      const enabledElement = getEnabledElement(element);
      if (!enabledElement) return;
      const viewport = enabledElement.viewport as Types.IVolumeViewport;
      const camera = viewport.getCamera();
      if (!camera.parallelProjection) return;

      const currentScale = camera.parallelScale ?? 1.0;
      const zoomFactor = 0.001;
      const newScale = currentScale * (1 - zoomFactor * -dy);
      camera.parallelScale = Math.min(
        Math.max(newScale, MIN_PARALLEL_SCALE),
        MAX_PARALLEL_SCALE,
      );

      viewport.setCamera(camera);
      viewport.render();
    };
    zoom.mouseDragCallback = zoom._drag;
    zoom.mouseWheelCallback = zoom._scroll;
    toolGroup.setToolActive(ZoomTool.toolName, {
      bindings: [
        {
          mouseButton: ToolEnums.MouseBindings.Wheel,
          modifierKey: ToolEnums.KeyboardBindings.Ctrl,
        },
        {
          mouseButton: ToolEnums.MouseBindings.Secondary,
          modifierKey: ToolEnums.KeyboardBindings.Shift,
        },
        {
          mouseButton: ToolEnums.MouseBindings.Primary_And_Secondary,
        }
      ],
    });

    addTool(PanTool);
    toolGroup.addTool(PanTool.toolName);
    toolGroup.setToolActive(PanTool.toolName, {
      bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
    });

    addTool(CustomWindowLevelTool);
    toolGroup.addTool(CustomWindowLevelTool.toolName);
    toolGroup.setToolConfiguration(CustomWindowLevelTool.toolName, {
      onDragUpdate: onDragUpdateCallback
    });
    toolGroup.setToolActive(CustomWindowLevelTool.toolName, {
      bindings: [
        {
          mouseButton: ToolEnums.MouseBindings.Primary,
          modifierKey: ToolEnums.KeyboardBindings.Shift,
        },
        {
          mouseButton: ToolEnums.MouseBindings.Auxiliary,
        },
      ],
    });

    // addTool(PlanarRotateTool)
    // toolGroup.addTool(PlanarRotateTool.toolName);
    // toolGroup.setToolActive(PlanarRotateTool.toolName, {
    //   bindings:[
    //     {
    //       mouseButton: ToolEnums.MouseBindings.Secondary,
    //       modifierKey: ToolEnums.KeyboardBindings.Ctrl
    //     },
    //   ]
    // });

    addTool(LengthTool);
    toolGroup.addTool(LengthTool.toolName);
    toolGroup.setToolActive(LengthTool.toolName);

    addTool(AngleTool);
    toolGroup.addTool(AngleTool.toolName);
    toolGroup.setToolActive(AngleTool.toolName);

    addTool(CobbAngleTool);
    toolGroup.addTool(CobbAngleTool.toolName);
    toolGroup.setToolActive(CobbAngleTool.toolName);

    addTool(EllipticalROITool);
    toolGroup.addTool(EllipticalROITool.toolName);
    toolGroup.setToolActive(EllipticalROITool.toolName);

    addTool(PlanarFreehandROITool);
    toolGroup.addTool(PlanarFreehandROITool.toolName);
    toolGroup.setToolActive(PlanarFreehandROITool.toolName);

    addTool(RectangleROITool);
    toolGroup.addTool(RectangleROITool.toolName);
    toolGroup.setToolActive(RectangleROITool.toolName);

    addTool(SplineROITool);
    toolGroup.addTool(SplineROITool.toolName);
    toolGroup.setToolActive(SplineROITool.toolName);

    // addTool(TrackballRotateTool);
    // toolGroup.addTool(TrackballRotateTool.toolName);
    // toolGroup.setToolActive(TrackballRotateTool.toolName);

    // segmentation 관련
    // addTool(cornerstoneTools.BrushTool);
    // toolGroup.addToolInstance('CircularBrush',cornerstoneTools.BrushTool.toolName,{
    //   activeStrategy: 'FILL_INSIDE_CIRCLE'
    // });
    // toolGroup.addToolInstance('ThresholdCircleBrush',cornerstoneTools.BrushTool.toolName,{
    //   activeStrategy: 'THRESHOLD_INSIDE_CIRCLE',
    // });
    // toolGroup.addToolInstance('EraserBrush',cornerstoneTools.BrushTool.toolName,{
    //   activeStrategy: 'ERASE_INSIDE_CIRCLE',
    // });

    addTool(CustomCrosshairsTool);
    toolGroup.addTool(CustomCrosshairsTool.toolName, {
      getReferenceLineColor: (vpId: any) =>
        viewportColors[vpId as 'Axial' | 'Coronal' | 'Sagittal'],
      getReferenceLineControllable: (vpId: any) => vpId !== ViewportId.LABELMAP,
      getReferenceLineDraggableRotatable: (vpId: any) =>
        vpId !== ViewportId.LABELMAP,
      getReferenceLineSlabThicknessControlsOn: (vpId: any) =>
        vpId !== ViewportId.LABELMAP,
    });
    //toolGroup.setToolEnabled(CustomCrosshairsTool.toolName);

    // const crosshairs = toolGroup.getToolInstance(CustomCrosshairsTool.toolName) as CustomCrosshairsTool;

    // let isSlabMode = false;
    // const originalHandle = crosshairs.handleSelectedCallback.bind(crosshairs);
    // crosshairs.handleSelectedCallback = function (evt: any, annotation: any) {
    //   originalHandle(evt, annotation);
    //   const op = (annotation as any).data.handles.activeOperation;
    //   isSlabMode = op === 3;
    //   console.log('activeOperation:', op, 'isSlabMode:', isSlabMode);
    // };

    // const originalDrag = crosshairs._dragCallback.bind(crosshairs);
    // crosshairs._dragCallback = function (
    //   evt: ToolTypes.EventTypes.InteractionEventType,
    // ) {
    //   originalDrag(evt);
    //   //const { deltaPoints, currentPoints, element  } = evt.detail;
    //   if (!isSlabMode) {
    //     requestAnimationFrame(() => {
    //       scheduleRender('Surface3D', {
    //         type: 'focus',
    //         world: crosshairs.toolCenter,
    //       });
    //       scheduleRender('VR', { type: 'focus', world: crosshairs.toolCenter });
    //     });
    //   }

    // };

    // const originalEnd = crosshairs._endCallback.bind(crosshairs);
    // crosshairs._endCallback = function (
    //   evt: ToolTypes.EventTypes.InteractionEventType,
    // ) {
    //   const { event } = evt.detail;
    //   isSlabMode = false;
    //   return originalEnd(evt);
    // };
  }

  let viewportIds = toolGroup.getViewportIds();
  if (!viewportIds.some((id) => id === viewportId)) {
    toolGroup.addViewport(viewportId, mainEngineId);
  }

  if (viewportIds && viewportIds.length >= 2) {
    toolGroup.setToolEnabled(CustomCrosshairsTool.toolName);
    toolGroup.setToolActive(CustomCrosshairsTool.toolName, {
      bindings: [
        {
          mouseButton: ToolEnums.MouseBindings.Primary,
          modifierKey: ToolEnums.KeyboardBindings.Ctrl,
        },
      ],
    });
  } else {
    toolGroup.setToolDisabled(CustomCrosshairsTool.toolName);
  }
}

export function toggleCrosshairsTool(visible: boolean) {
  const toolGroupId = 'sharedToolGroup';
  let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (!toolGroup) {
    return;
  }
  let viewportIds = toolGroup.getViewportIds();
  if (viewportIds && viewportIds.length >= 2) {
    if (visible) {
      toolGroup.setToolEnabled(CustomCrosshairsTool.toolName);
      toolGroup.setToolActive(CustomCrosshairsTool.toolName, {
        bindings: [
          {
            mouseButton: ToolEnums.MouseBindings.Primary,
            modifierKey: ToolEnums.KeyboardBindings.Ctrl,
          },
        ],
      });
    } else {
      toolGroup.setToolDisabled(CustomCrosshairsTool.toolName);
    }
  }
}

// blendMode가 숫자로 들어와서
function thicknessModeType(blendMode?: number): ThicknessMethodType {
  switch (blendMode) {
    case 2:
      return 'MinIP';
    case 3:
      return 'Average';
    default:
    case 1:
      return 'MIP';
  }
}

export function getHuData(viewportId: string): Int16Array | null {
  const enabledElement = getEnabledElementByViewportId(viewportId);
  const viewport = enabledElement.viewport;
  const volumeActor = viewport.getDefaultActor()?.actor;
  if (!volumeActor) {
    console.warn('No volume actor found.');
    return null;
  }

  const imageData = volumeActor.getMapper()?.getInputData();
  if (!imageData) {
    console.warn('No imageData found.');
    return null;
  }

  const voxelManager = imageData.get('voxelManager')?.voxelManager;
  if (!voxelManager) {
    console.warn('No voxelManager found in imageData.');
    return null;
  }

  return voxelManager.getCompleteScalarDataArray();
}

export function resizeViewport(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId);
  if (!vp) return;
  // const zoom = vp.getZoom();
  // vp.setZoom(zoom);
  console.log(vp);
  engine.resize(true, false);
}

export function resizeEngine(renderingEngineId: string = mainEngineId) {
  //초기화 안했니?
  if (!isCornerstoneInitialized()) return;
  const engine = getEngine(renderingEngineId);
  if(!engine) {
    console.log('engine 없는데 왜 시도하뉘?', engine);
    return;
  };
  const configs = engine.getViewports().map((vp) => ({
    id: vp.id,
    camera: vp.getCamera?.(),
  }));
  engine.resize(true, true);

  configs.forEach(({ id, camera }) => {
    const vp = engine.getViewport(id);
    if (camera) {
      vp.setCamera(camera);
    }
  });

  engine.render();
}

//확장
export function resetView(
  viewportId: PanelComponentKeyType,
  switchConfigs: { switchSynchronizingPanel: boolean },
  renderingEngineId: string = mainEngineId,
) {
  const engine = getEngine(renderingEngineId);
  console.log('왜리셋');
  if (switchConfigs.switchSynchronizingPanel) {
    const viewport = engine.getViewport(viewportId);
    viewport.resetCamera();
  } else {
    const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
    toolGroup!.setToolDisabled(CustomCrosshairsTool.toolName);

    const viewport = engine.getViewport(viewportId);
    viewport.resetCamera();
    toolGroup!.setToolActive(CustomCrosshairsTool.toolName);
  }
}

export function initialcolorandopacity(
  analysisType: string,
  target: any,
  newSegmentation: any,
) {
  const arg = {
    type: 'colorchange',
    target: target,
    newSegmentation: newSegmentation,
  };

  // for (let i = 0; i < 3; i++) {
  //   scheduleRender(`CrossSection.0.${i}`, arg);
  //   scheduleRender(`CrossSection.1.${i}`, arg);
  //   scheduleRender(`CrossSection.2.${i}`, arg);
  //   scheduleRender(`CPR.${i}.straightened`, arg);
  // }

  //if (target !== 'aorta' && target !== 'avc') {
  if (
    analysisType === 'plaque' ||
    analysisType === 'stenosis' ||
    analysisType === 'isr'
  ) {
    scheduleRender('CPR.0.stretched', arg);
    scheduleRender('CPR.0.straightened', arg);
    scheduleRender('CrossSection.0.0', arg);
    scheduleRender('CrossSection.0.1', arg);
    scheduleRender('CrossSection.0.2', arg);
  }
  if (analysisType === 'fai') {
    for (let i = 0; i < 3; i++) {
      scheduleRender(`CrossSection.0.${i}`, arg);
      scheduleRender(`CrossSection.1.${i}`, arg);
      scheduleRender(`CrossSection.2.${i}`, arg);
      scheduleRender(`CPR.${i}.straightened`, arg);
    }
  }
  //}
  if (analysisType === 'aorticValveCalcium') {
    scheduleRender('CPR.0.straightened', arg);
    scheduleRender('CrossSection.0.0', arg);
  }
  if (analysisType === 'segmentation') {
    if (
      // arg.target === 'aorta' ||
      arg.target === 'coronary' ||
      arg.target === 'calcium'
    ) {
      for (let i = 0; i < 3; i++) {
        scheduleRender(`CrossSection.0.${i}`, arg);
        scheduleRender(`CrossSection.1.${i}`, arg);
        scheduleRender(`CrossSection.2.${i}`, arg);
        scheduleRender(`CPR.${i}.straightened`, arg);
      }
    }
  }
}
export function resetAnother(analysisType: string) {
  if (
    analysisType === 'plaque' ||
    analysisType === 'stenosis' ||
    analysisType === 'isr'
  ) {
    scheduleRender('CPR.0.stretched', { type: 'reset' });
    scheduleRender('CPR.0.straightened', { type: 'reset' });
    scheduleRender('CrossSection.0.0', { type: 'reset' });
    scheduleRender('CrossSection.0.1', { type: 'reset' });
    scheduleRender('CrossSection.0.2', { type: 'reset' });
    // if (analysisType === 'isr' || analysisType === 'stenosis') {
    //   scheduleRender('Report', { type: 'reset' });
    // }
  }
  if (analysisType === 'fai') {
    for (let i = 0; i < 3; i++) {
      scheduleRender(`CrossSection.0.${i}`, { type: 'reset' });
      scheduleRender(`CrossSection.1.${i}`, { type: 'reset' });
      scheduleRender(`CrossSection.2.${i}`, { type: 'reset' });
      scheduleRender(`CPR.${i}.straightened`, { type: 'reset' });
    }
  }
  if (analysisType === 'aorticValveCalcium') {
    scheduleRender('CPR.0.straightened', { type: 'reset' });
    scheduleRender('CrossSection.0.0', { type: 'reset' });
  }
  if (analysisType === 'chamber') {
    scheduleRender('Direction', { type: 'reset' });
  }
  scheduleRender('VR', { type: 'reset' });
  scheduleRender('Surface3D', { type: 'reset' });
}

//여기가 반전되는곳을 처리해야하는 부분
export function setViewportNegative(
  viewportId: PanelComponentKeyType,
  isNegative: boolean,
) {
  const enabledElement = getEnabledElementByViewportId(viewportId);
  const viewport = enabledElement.viewport;
  const { invert } = viewport.getProperties();
  viewport.setProperties({ invert: !invert });
  viewport.render();
}

export function getViewportNegative(viewportId: PanelComponentKeyType) {
  const enabledElement = getEnabledElementByViewportId(viewportId);
  const viewport = enabledElement.viewport;
  if (!viewport) return false;
  return viewport.getProperties()?.invert ?? false;
}

export function setViewportsNegative(
  invert: boolean,
) {
  getEnabledElements().forEach(({viewport})=>{
    viewport.setProperties({ invert: invert });
    viewport.render();
  })
}

export function getViewportsNegative() {
  return getEnabledElements().map(({viewport})=>viewport.getProperties()?.invert??false).some(v=>v);
}
// // 화면띄워주기용 계싼들
// export function getVolumeDimensions(viewportId: PanelComponentKeyType) {
//   const engine = getEngine(mainEngineId);
//   const viewport = engine.getViewport(viewportId) as Types.IVolumeViewport;
//   if (!viewport) return;

//   const imageData = viewport.getImageData();
//   if (!imageData) return;

//   return imageData.dimensions;
// }
// export function getSliceIndex(viewportId: PanelComponentKeyType) {
//   const engine = getEngine(mainEngineId);
//   const viewport = engine.getViewport(viewportId) as Types.IVolumeViewport;
//   if (!viewport) return;

//   return viewport.getSliceIndex();
// }

export function getVolumeWorldPointInfo(
  viewportId: PanelComponentKeyType,
  clientX: number,
  clientY: number,
) {
  let worldPointInfoResult: {
    worldPosition?: Types.Point3;
    position?: Types.Point3;
    value?: number;
  } = {
    worldPosition: undefined,
    position: undefined,
    value: undefined,
  };
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as Types.IVolumeViewport;
  if (!viewport) return worldPointInfoResult;

  const element = viewport.element;
  if (!element) return worldPointInfoResult;

  const rect = element.getBoundingClientRect();
  const canvasX = clientX - rect.left;
  const canvasY = clientY - rect.top;

  const world = viewport.canvasToWorld([canvasX, canvasY]);
  const imageData = viewport.getImageData();
  if (!imageData) return worldPointInfoResult;

  if (!isInBounds(world, viewport.getBounds())) return worldPointInfoResult;

  const origin = imageData.origin;
  const spacing = imageData.spacing;

  worldPointInfoResult.position = world.map((v: number, i: number) =>
    Math.abs(Math.round((v - origin[i]) / spacing[i])),
  ) as Types.Point3;
  worldPointInfoResult.value = viewport.getIntensityFromWorld(world);
  worldPointInfoResult.worldPosition = world;

  return worldPointInfoResult;
}

export function getVolumeMinMax(seriesId: string, half: boolean) {
  const cachedVolume = getVolumeFromSeriesId(seriesId, half); //cache.getVolume(`ORIGINAL_${seriesId}`);
  if (!cachedVolume) return [0, 0];
  if (!cachedVolume.additionalDetails) return [0, 0];
  return [
    cachedVolume.additionalDetails['HUMin'] as number,
    cachedVolume.additionalDetails['HUMax'] as number,
  ];
}

// CT 화면 바뀔때마다 돌려가면서 표현값 바꿀거임
// 표현값이 먼저 바뀌어버리면?
// sync상태면 어칼까?
export function onImageRendered(
  e: Event,
  ref: React.RefObject<PanelState>,
  handleUpdatePanelState: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void,
) {
  const { renderingEngineId, viewportId, viewportStatus } = (
    e as EventTypes.ImageRenderedEvent
  ).detail as EventTypes.ImageRenderedEventDetail;

  if (
    viewportStatus == Enums.ViewportStatus.NO_DATA ||
    viewportStatus == Enums.ViewportStatus.LOADING
  )
    return;
  if (!ref.current) return;

  const engine = getEngine(renderingEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  if (!vp) return;

  const { slabThickness } = vp.getProperties();
  const blendMode = vp.getDefaultActor().blendMode;
  // 뷰포의 두배와 내 상태를 비교하도록
  const slabThickness2X = slabThickness ? slabThickness * 2 : slabThickness;
  // console.log(slabThickness2X,prop.slabThickness,ref.current.thickness)
  if (!slabThickness2X && ref.current.thickness) {
    vp.setSlabThickness(ref.current.thickness * 0.5);
  } else if (slabThickness2X) {
    // console.log(ref.current.thickness ,slabThickness2X);
    if (slabThickness2X > 10) {
      handleUpdatePanelState('thickness', 10);
    } else if (ref.current.thickness != slabThickness2X) {
      handleUpdatePanelState('thickness', slabThickness2X);
    }
  }
  if(configStore.get().EditMode){
    vp.setSlabThickness(defaultSliceThickness);
  }

  if (!blendMode && ref.current.thicknessMethod) {
    switch (ref.current.thicknessMethod) {
      case 'Average':
        vp.setBlendMode(Enums.BlendModes.AVERAGE_INTENSITY_BLEND);
        break;
      case 'MinIP':
        vp.setBlendMode(Enums.BlendModes.MINIMUM_INTENSITY_BLEND);
        break;
      case 'MIP':
      default:
        vp.setBlendMode(Enums.BlendModes.MAXIMUM_INTENSITY_BLEND);
        break;
    }
  } else {
    const thicknessMode = thicknessModeType(blendMode);
    if (ref.current.thicknessMethod != thicknessMode) {
      handleUpdatePanelState('thicknessMethod', thicknessMode);
    }
  }
}

export function dot(a: Types.Point3 | number[], b: Types.Point3 | number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
export function cross(a: Types.Point3, b: Types.Point3): Types.Point3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
export function scalePoint3(a: Types.Point3, s: Types.Point3): Types.Point3 {
  return [a[0] * s[0], a[1] * s[1], a[2] * s[2]] as Types.Point3;
}
function distance(a: Types.Point3, b: Types.Point3): number {
  return ((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2) ** 0.5;
}

export function length(a: Types.Point3): number {
  return Math.sqrt(a[0] ** 2 + a[1] ** 2 + a[2] ** 2);
}
export function normalize(a: Types.Point3): Types.Point3 {
  const len = length(a);
  if (Math.abs(len - 1) < 1e-6 || len === 0) return a;
  return [a[0] / len, a[1] / len, a[2] / len];
}

const DIR_VECTORS = {
  L: [-1, 0, 0], // Left
  R: [1, 0, 0],  // Right
  P: [0, -1, 0],  // Posterior
  A: [0, 1, 0], // Anterior
  H: [0, 0, -1], // Superior (Head)
  F: [0, 0, 1],  // Inferior (Feet)
};

function getLabels(vec: number[], count = 3, threshold = 0.3): string {
  const sims = Object.entries(DIR_VECTORS)
    .map(([label, v]) => ({ label, val: dot(vec, v) }))
    .sort((a, b) => Math.abs(b.val) - Math.abs(a.val));
  return sims
    .filter(s => s.val > threshold)
    .slice(0, count)
    .map(s => s.label)
    .join('');
}

export function getAnatomicDirectionsArray(
  // plane: string,
  // normal: number[],
  up: number[],
  right: number[],
  labelCount = 3,
  threshold = 0.3
): [string, string, string, string] {
  // const up = normalize(viewUp);
  // const normal = normalize(viewPlaneNormal);
  // const right = normalize(cross(up, normal));
  const down = up.map(v => -v);
  const left = right.map(v => -v);

  return [
    getLabels(up, labelCount, threshold),    // top
    getLabels(right, labelCount, threshold), // right
    getLabels(down, labelCount, threshold),  // bottom
    getLabels(left, labelCount, threshold),  // left
  ];
}

export function getViewportCameraDirection(
  camera: Pick<Partial<Types.ICamera>, 'viewUp' | 'viewPlaneNormal'>,
): DirectionType | undefined {
  const { viewUp, viewPlaneNormal } = camera;
  if (!viewUp || !viewPlaneNormal) return undefined;

  const right = getOrthoNormal(viewUp, viewPlaneNormal);
  return getAnatomicDirectionsArray(viewUp, right);
}

function setDirection({
  viewportId,
  previousCamera,
  camera,
  setter,
}: {
  viewportId: string;
  previousCamera: Types.ICamera;
  camera: Types.ICamera;
  setter: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void;
}) {
  if (
    camera.viewPlaneNormal &&
    camera.viewUp &&
    (!utilities.isEqual(
      previousCamera.viewPlaneNormal,
      camera.viewPlaneNormal,
    ) ||
      !utilities.isEqual(previousCamera.viewUp, camera.viewUp))
  ) {
    let xAxis: Types.Point3, yAxis: Types.Point3, zAxis: Types.Point3;
    const right = getOrthoNormal(camera.viewUp, camera.viewPlaneNormal);
    switch (viewportId) {
      case 'Coronal':
        zAxis = camera.viewUp;
        yAxis = camera.viewPlaneNormal;
        xAxis = right;

        break;
      case 'Sagittal':
        zAxis = camera.viewUp;
        xAxis = camera.viewPlaneNormal;
        yAxis = right;
        break;
      case 'Axial':
      default:
        zAxis = camera.viewPlaneNormal;
        yAxis = camera.viewUp;
        xAxis = right;
        break;
    }
    // scheduleRender('Surface3D', { type: 'rotate', xAxis, yAxis, zAxis });
    // scheduleRender('VR',{type:'rotate',xAxis,yAxis,zAxis})
    setter('direction', getAnatomicDirectionsArray(camera.viewUp, right));
  }
}
export function requestPixelsPerMM({
  viewportId,
  viewport,
  handleUpdatePanelState,
}: {
  viewportId: PanelComponentKeyType;
  viewport?: Types.IViewport;
  handleUpdatePanelState?: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void;
}) {
  let vp = viewport;
  if (!vp) {
    const engine = getEngine(mainEngineId);
    vp = engine.getViewport(viewportId);
  }
  if (!vp) return 1;
  const mmPerPixel = distance(
    vp.canvasToWorld([0, 0]),
    vp.canvasToWorld([0, 1]),
  );

  const pixelsPerMM = mmPerPixel != 0 ? 1 / mmPerPixel : 0;
  if (handleUpdatePanelState) {
    handleUpdatePanelState('pixelsPerMM', pixelsPerMM);
  }
  return pixelsPerMM;
}

export function onCameraModified(
  e: Event,
  handleUpdatePanelState: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void,
  options: { syncDicomZoom?: boolean; syncDicomPan?: boolean } = {},
) {
  const { previousCamera, camera, viewportId, renderingEngineId, element } = (
    e as EventTypes.CameraModifiedEvent
  ).detail;
  const viewport = getEngine(renderingEngineId).getViewport(viewportId);

  if (!viewport) return;
  setDirection({
    viewportId,
    previousCamera,
    camera,
    setter: handleUpdatePanelState,
  });
  if (viewport.viewportStatus !== 'rendered') return;
  if (!camera.parallelScale || isNaN(camera.parallelScale)) return;

  // position 디바운스
  if (!utilities.isEqual(previousCamera.position, camera.position)) {
    if ((onCameraModified as any)._positionTimer) {
      clearTimeout((onCameraModified as any)._positionTimer);
    }

    (onCameraModified as any)._positionTimer = setTimeout(() => {
      handleUpdatePanelState('position', camera.position);
    }, 50);
  }

  // zoom
  if (previousCamera.parallelScale !== camera.parallelScale) {
    const zoom = viewport.getZoom();
    if (!isNaN(zoom)) {
      // console.log('d얼탱x',previousCamera,camera)
      handleUpdatePanelState('zoom', zoom);
      // handleUpdatePanelState('pixelsPerMM',undefined)
      requestPixelsPerMM({
        viewportId: viewportId as PanelComponentKeyType,
        viewport,
        handleUpdatePanelState,
      });
    }
    const suppressDicomZoomSync =
      isApplyingDicomZoomSync ||
      shouldSuppressDicomZoomSync(viewportId, camera.parallelScale);
    if (options.syncDicomZoom && !suppressDicomZoomSync) {
      syncDicomZoomViewports({
        sourceViewportId: viewportId,
        previousParallelScale: previousCamera.parallelScale,
        nextParallelScale: camera.parallelScale,
      });
    }
  }

  const suppressDicomPanSync =
    isApplyingDicomPanSync || shouldSuppressDicomPanSync(viewportId, camera);
  const canSyncDicomPan =
    options.syncDicomPan === true &&
    !suppressDicomPanSync;
  if (canSyncDicomPan) {
    syncDicomPanViewports({
      sourceViewportId: viewportId,
      sourceViewport: viewport,
      previousCamera,
      camera,
    });
  }

  // slice move
  if (
    !previousCamera.position ||
    (camera.position &&
      !utilities.isEqual(previousCamera.position, camera.position))
  ) {
    //반대로 해볼까여
    handleUpdatePanelState(
      'sliceIndex',
      viewport.getNumberOfSlices() - viewport.getSliceIndex() - 1,
    );
  }
}

function onMouseMove(e: Event) {
  const { currentPoints, viewportId } = (
    e as ToolTypes.EventTypes.MouseMoveEventType
  ).detail;
  const { world } = currentPoints;
  const vp = getViewport(viewportId as PanelComponentKeyType);
  const imageData = vp.getImageData();
  if (!imageData) return;
  const { origin, spacing } = imageData;

  if (!isInBounds(world, vp.getBounds())) return;
  const xyz = world.map(
    (v: number, i: number) => (v - origin[i]) / spacing[i],
    // Math.abs(Math.round((v - origin[i]) / spacing[i])) + 1,
  ) as Types.Point3;
  // scheduleRender(`${viewportId}`,{type:'mouseMove',pointInfo:[...xyz,vp.getIntensityFromWorld(world)]})
  return [...xyz, vp.getIntensityFromWorld(world)] as Types.Point4;
}

export function onVOIModified(e: Event) {
  // const { detail } = e as EventTypes.VoiModifiedEvent;
  // if (!detail.range) return;
  // const { upper, lower } = detail.range;
  // const newWW = upper - lower;
  // const newWL = (upper + lower) * 0.5;
  // const newnewWW = detail.invert ? -newWW : newWW;
  // // scheduleRender(detail.viewportId, {
  // //   type: 'VOIModified',
  // //   windowWidth: newWW,
  // //   windowLevel: newWL,
  // // });
  // scheduleRender(`VR`, {
  //   type: 'updateWindowLevel',
  //   level: newWL,
  //   window: newWW,
  //   negativeMode: detail.invert,
  // });
  // configStore.setData({commonWindow: {
  //   first: false,
  //   commonWindowLevel: newWL,
  //   commonWindowWidth: newnewWW
  // }})
  // scheduleRender(`CPR.0.stretched`, {
  //   type: 'updateWindowLevel',
  //   level: newWL,
  //   window: newnewWW,
  // });
  // for (let key = 0; key < 3; key++) {
  //   scheduleRender(`CPR.${key}.straightened`, {
  //     type: 'updateWindowLevel',
  //     level: newWL,
  //     window: newnewWW,
  //   });
  //   scheduleRender(`CrossSection.${key}.0`, {
  //     type: 'updateWindowLevel',
  //     level: newWL,
  //     window: newnewWW,
  //   });
  //   scheduleRender(`CrossSection.${key}.1`, {
  //     type: 'updateWindowLevel',
  //     level: newWL,
  //     window: newnewWW,
  //   });
  //   scheduleRender(`CrossSection.${key}.2`, {
  //     type: 'updateWindowLevel',
  //     level: newWL,
  //     window: newnewWW,
  //   });
  // }
}

// function onCrosshairToolCenterChanged(e: any) {
//   const { detail } = e as CustomEvent;
//   const { toolCenter } = detail;
//   if (isNaN(toolCenter[0]) || isNaN(toolCenter[1]) || isNaN(toolCenter[2]))
//    return;
//   //좌표계산 다시 해서 넣을예정
//   requestAnimationFrame(() => {
//    scheduleRender('Surface3D', { type: 'focus', world: toolCenter });
//    scheduleRender('VR', { type: 'focus', world: toolCenter });
//   });
// }
export function handleVoiClampEvent({
  element,
  selectedSeriesId,
  lastValidVoiRangeMap,
}: {
  element: HTMLDivElement;
  selectedSeriesId: string | undefined,
  lastValidVoiRangeMap: Map<string, { lower: number; upper: number }>,
}) {
  if(false){
    element.addEventListener(Enums.Events.VOI_MODIFIED, onVOIModified);
    return;
  }
}

export function registerEvents({
  element,
  imageRendered,
  handleMouseMove,
  cameraModified,
}: {
  element: HTMLDivElement;
  imageRendered: (evt: Event) => void;
  handleMouseMove: (point4?: Types.Point4) => void;
  cameraModified: (evt: Event) => void;
}) {
  element.addEventListener(Enums.Events.IMAGE_RENDERED, imageRendered);
  // element.addEventListener(Enums.Events.VOI_MODIFIED, onVOIModified);
  element.addEventListener(Enums.Events.CAMERA_MODIFIED, cameraModified);
  // element.addEventListener(Enums.Events.CAMERA_RESET,(e)=>{
  //   console.log(e as EventTypes.CameraResetEvent)
  // })
  element.addEventListener(ToolEnums.Events.MOUSE_MOVE, (e) =>
    handleMouseMove(onMouseMove(e)),
  );
  // eventTarget.addEventListener(ToolEnums.Events.SEGMENTATION_REPRESENTATION_MODIFIED,(e:Event)=>console.log(e))
  // eventTarget.addEventListener(
  //   ToolEnums.Events.CROSSHAIR_TOOL_CENTER_CHANGED,
  //   onCrosshairToolCenterChanged,
  // );
}
export function unregisterEvents({
  element,
  imageRendered,
  handleMouseMove,
  cameraModified,
}: {
  element: HTMLDivElement;
  imageRendered: (evt: Event) => void;
  handleMouseMove: (point4?: Types.Point4) => void;
  cameraModified: (evt: Event) => void;
}) {
  element.removeEventListener(Enums.Events.IMAGE_RENDERED, imageRendered);
  // element.removeEventListener(Enums.Events.VOI_MODIFIED, onVOIModified);
  element.removeEventListener(Enums.Events.CAMERA_MODIFIED, cameraModified);
  element.removeEventListener(ToolEnums.Events.MOUSE_MOVE, (e) =>
    handleMouseMove(onMouseMove(e)),
  );
  // eventTarget.removeEventListener(ToolEnums.Events.SEGMENTATION_REPRESENTATION_MODIFIED,(e:Event)=>console.log(e))
  // eventTarget.removeEventListener(
  //   ToolEnums.Events.CROSSHAIR_TOOL_CENTER_CHANGED,
  //   onCrosshairToolCenterChanged,
  // );
}

export function registerImageRendered(
  element: HTMLDivElement,
  handler: (evt: Event) => void,
) {
  // if(handler.current)
  element.addEventListener(Enums.Events.IMAGE_RENDERED, handler);
}
export function unregisterImageRendered(
  element: HTMLDivElement,
  handler: (evt: Event) => void,
) {
  // if(handler.current)
  element.removeEventListener(Enums.Events.IMAGE_RENDERED, handler);
}

export function getViewportThickness(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  return vp.getSlabThickness();
}
export function getViewportThicknessMethod(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  return thicknessModeType(vp.getDefaultActor().blendMode);
}
export function getViewportZoom(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  return vp.getZoom();
}
export function getViewportWindowLevel(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  const voiRange = vp.getProperties().voiRange;
  if (voiRange) {
    const { upper, lower } = voiRange;
    return (upper + lower) * 0.5;
  }
  return 0;
}
export function getViewportWindowWidth(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  const voiRange = vp.getProperties().voiRange;
  if (voiRange) {
    const { upper, lower } = voiRange;
    return upper - lower;
  }
  return 0;
}

export function resetCrosshairHandles() {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  const tool = toolGroup?.getToolInstance?.(
    CustomCrosshairsTool.toolName,
  ) as CustomCrosshairsTool | undefined;

  if (!tool) return;

  tool._getViewportsInfo().forEach(({ viewportId }) => {
    const enabledElement = getEnabledElementByViewportId(viewportId);
    updateViewportThickness(viewportId, defaultSliceThickness);
  });
}

export async function updateViewportThickness(
  viewportId: PanelComponentKeyType,
  thicknessHalf: number,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  if (!vp) return;
  if (vp.getSlabThickness() === thicknessHalf) return;

  vp.setSlabThickness(thicknessHalf);
  vp.render();
}
export async function updateViewportThicknessMethod(
  viewportId: PanelComponentKeyType,
  thicknessMethod: ThicknessMethodType,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  if (!vp) return;
  const defaultActor = (vp as any).getDefaultActor?.();
  if (!defaultActor) {
    return;
  }
  if (thicknessMethod === thicknessModeType(vp.getDefaultActor().blendMode))
    return;

  switch (thicknessMethod) {
    case 'Average':
      vp.setBlendMode(Enums.BlendModes.AVERAGE_INTENSITY_BLEND);
      break;
    case 'MIP':
      vp.setBlendMode(Enums.BlendModes.MAXIMUM_INTENSITY_BLEND);
      break;
    case 'MinIP':
      vp.setBlendMode(Enums.BlendModes.MINIMUM_INTENSITY_BLEND);
      break;
  }
  vp.render();
}

// 스케일 1 기준으로 계산.
// 코드가 어떤 의도지..
export function resetViewport(refresh?: boolean, syncDicomZoom = true) {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  if (!toolGroup) return;
  const viewportIds = toolGroup.getViewportIds();

  viewportIds.forEach((id) => {
    const viewport = getEnabledElementByViewportId(
      id as PanelComponentKeyType,
    )?.viewport;

    if (!viewport) return;

    if (refresh) {
      viewport.render();
      return;
    }

    const orientation = getDefaultViewportOrientation(
      id as PanelComponentKeyType,
    );

    viewport.setCamera({
      ...orientation,
      parallelProjection: true,
    });

    viewport.resetCamera();
    viewport.setProperties({ invert: false });
    viewport.setViewPresentation({ rotation: 0 });
    viewport.render();
  });

  if (!refresh) {
    fitDicomViewportsToScreen({
      syncToAxial: syncDicomZoom,
      repeat: true,
    });
  }
}

export function deleteActiveAnnotation() {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  if (!toolGroup) return;

  const cameras = toolGroup.getViewportIds().map((vpId) => {
    const enabledElement = getEnabledElementByViewportId(vpId);
    return { vpId, camera: enabledElement?.viewport?.getCamera() };
  });

  const activeAnnotationsUID =
    cornerstoneTools.annotation.selection.getAnnotationsSelected();
  activeAnnotationsUID.forEach((uid) => {
    const ann = annotation.state.getAnnotation(uid);
    if (ann.metadata.toolName === CustomCrosshairsTool.toolName) return;
    annotation.state.removeAnnotation(uid);
  });

  cameras.forEach(({ vpId, camera }) => {
    if (!camera) return;
    const enabledElement = getEnabledElementByViewportId(vpId);
    enabledElement?.viewport?.setCamera(camera);
    enabledElement?.viewport?.render();
  });
}

export function deleteAllAnnotations() {
  const allAnnotations = annotation.state.getAllAnnotations();
  deactivateExclusiveTools({});
  const annotationUIDsToRemove = allAnnotations
    .filter((a) => a.metadata?.toolName !== CustomCrosshairsTool.toolName)
    .map((a) => a.annotationUID)
    .filter((uid): uid is string => Boolean(uid));
  for (const toolName of annotationUIDsToRemove) {
    annotation.state.removeAnnotation(toolName);
  }
}

// export function hideAllAnnotations() {
//   const allAnnotations = annotation.state.getAllAnnotations();
//   if (allAnnotations.length === 0) return;
//   const shouldHide = !allAnnotations[0].isVisible;

//   console.log('너 참이냐 거짓이냐 ', shouldHide);
//   for (const ann of allAnnotations) {
//     if(!ann.annotationUID) return;
//     annotation.visibility.setAnnotationVisibility(ann.annotationUID, shouldHide);
//   }
// }

export function hideAllAnnotations(visible: boolean) {
  const allAnnotations = annotation.state.getAllAnnotations();
  if (allAnnotations.length === 0) return;

  for (const ann of allAnnotations) {
    if (!ann.annotationUID) continue;
    annotation.visibility.setAnnotationVisibility(ann.annotationUID, !visible);
  }

  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  toolGroup?.getViewportIds().map((vpId) => {
    const enabledElement = getEnabledElementByViewportId(vpId);
    enabledElement.viewport.render();
  });
}

export function HistoryUndo() {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  if (!toolGroup) return;

  const cameras = toolGroup.getViewportIds().map((vpId) => {
    const enabledElement = getEnabledElementByViewportId(vpId);
    return { vpId, camera: enabledElement?.viewport?.getCamera() };
  });

  utilities.HistoryMemo.DefaultHistoryMemo.undo(1);

  cameras.forEach(({ vpId, camera }) => {
    if (!camera) return;
    const enabledElement = getEnabledElementByViewportId(vpId);
    enabledElement?.viewport?.setCamera(camera);
    enabledElement?.viewport?.render();
  });
}

export function HistoryRedo() {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  if (!toolGroup) return;

  const cameras = toolGroup.getViewportIds().map((vpId) => {
    const enabledElement = getEnabledElementByViewportId(vpId);
    return { vpId, camera: enabledElement?.viewport?.getCamera() };
  });

  utilities.HistoryMemo.DefaultHistoryMemo.redo(1);

  cameras.forEach(({ vpId, camera }) => {
    if (!camera) return;
    const enabledElement = getEnabledElementByViewportId(vpId);
    enabledElement?.viewport?.setCamera(camera);
    enabledElement?.viewport?.render();
  });
}

export function updateViewportZoom(
  viewportId: PanelComponentKeyType,
  zoom: number,
  zoomScale?: number,
) {
  // console.time('updateViewportZoom')
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport3D;
  if (!vp) return 1;
  const currentZoom = vp.getZoom();
  if (currentZoom === zoom) return zoom;

  const cam = vp.getCamera();
  // const volumeId = vp.getVolumeId()
  const image = vp.getImageData();
  const element = vp.element;
  if (image && cam.parallelScale) {
    // const keepCamera = zoom===0;
    if (zoom == 0) {
      const scale = zoomScale?? 1;
      const oneToOneScale = vp.getZoom() * cam.parallelScale * scale;

      //console.log(oneToOneScale, vp.getZoom() * cam.parallelScale)

      const [iSpacing, jSpacing, kSpacing] = image.spacing;
      const [cols, rows, deps] = image.dimensions;

      let worldWidth;
      let worldHeight;
      switch (viewportId) {
        case 'Coronal':
          worldWidth = cols * iSpacing;
          worldHeight = deps * kSpacing;
          break;
        case 'Sagittal':
          worldWidth = rows * jSpacing;
          worldHeight = deps * kSpacing;
          break;
        default:
          worldWidth = cols * iSpacing;
          worldHeight = rows * jSpacing;
          break;
      }
      const { width: elW, height: elH } = element.getBoundingClientRect();
      if (
        !Number.isFinite(elW) ||
        !Number.isFinite(elH) ||
        elW <= 0 ||
        elH <= 0 ||
        !Number.isFinite(worldWidth) ||
        !Number.isFinite(worldHeight) ||
        worldWidth <= 0 ||
        worldHeight <= 0
      ) {
        return vp.getZoom();
      }
      // 2) 이미지 월드 크기 (mm)
      const imgAspect = worldWidth / worldHeight;
      const elementAspect = elW / elH;

      let fitWorldHeight: number;
      if (elementAspect > imgAspect) {
        // 요소가 더 가로로 넓다면 높이 기준으로 맞춤
        fitWorldHeight = worldHeight;
      } else {
        // 요소가 더 세로로 좁다면 너비 기준으로 맞춤
        // worldWidth → worldHeight 비율 반영
        fitWorldHeight = worldWidth * (elH / elW);
      }

      // 3) parallelScale 로 변환
      const fitScaleManual = fitWorldHeight / 2;
      zoom = oneToOneScale / fitScaleManual;
    }
    if (Math.abs(currentZoom - zoom) <= 1e-3) return currentZoom;
    vp.setZoom(zoom);
    vp.render();
  }
  return zoom;
  //  console.timeEnd('updateViewportZoom')
}


export function fitChamberViewportToImageSize({
  viewportId,
  imageSize,
  zoomScale = 0.8,
}: {
  viewportId: PanelComponentKeyType;
  imageSize?: number;
  zoomScale?: number;
}) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport | undefined;
  if (!viewport) return;

  const element = viewport.element;
  const { width: elW, height: elH } = element.getBoundingClientRect();
  const nextParallelScale = getChamberFitParallelScale({
    imageSize,
    elementWidth: elW,
    elementHeight: elH,
    zoomScale,
  });
  if (!nextParallelScale) return;
  const camera = viewport.getCamera();

  if (
    typeof camera.parallelScale === 'number' &&
    Number.isFinite(camera.parallelScale) &&
    Math.abs(camera.parallelScale - nextParallelScale) <= 1e-3
  ) {
    return;
  }

  viewport.setCamera({
    ...camera,
    parallelScale: nextParallelScale,
  });
  viewport.render();
}

export function getPixelSpacing(viewportId: PanelComponentKeyType) {
  const enabledElement = getEnabledElementByViewportId(viewportId);
  if (!enabledElement || !enabledElement.viewport) return;

  const imageData = enabledElement.viewport.getImageData();
  if (!imageData) return;

  return {
    dimensions: imageData.dimensions,
    spacing: imageData.spacing,
    origin: imageData.origin,
  };
}

// export async function updateViewportWindowLevel(
//   viewportId: PanelComponentKeyType,
//   windowLevel: number,
// ) {
//   const engine = getEngine(mainEngineId);
//   const vp = engine.getViewport(viewportId) as VolumeViewport3D;
//   const vpProps = vp.getProperties();
//   if (vpProps && vpProps.voiRange) {
//     const { upper, lower } = vpProps.voiRange;
//     const oldWL = (upper + lower) * 0.5;
//     if (oldWL === windowLevel) return;

//     const oldWWhalf = (upper - lower) * 0.5;
//     vp.setProperties({
//       voiRange: {
//         lower: windowLevel - oldWWhalf,
//         upper: windowLevel + oldWWhalf,
//       },
//     });
//     vp.render();
//   }
// }
// export async function updateViewportWindowWidth(
//   viewportId: PanelComponentKeyType,
//   windowWidth: number,
// ) {
//   const engine = getEngine(mainEngineId);
//   const vp = engine.getViewport(viewportId) as VolumeViewport3D;
//   const vpProps = vp.getProperties();
//   if (vpProps && vpProps.voiRange) {
//     const { upper, lower } = vpProps.voiRange;
//     const oldWW = upper - lower;
//     // console.log(oldWW,windowWidth)
//     if (oldWW === windowWidth) return;
//     const oldWL = (upper + lower) * 0.5;
//     const newWWhalf = windowWidth * 0.5;
//     vp.setProperties({
//       voiRange: { lower: oldWL - newWWhalf, upper: oldWL + newWWhalf },
//     });
//     vp.render();
//   }
// }
// export async function updateViewportWindowLevelWidth(
//   viewportId: PanelComponentKeyType,
//   windowLevel: number,
//   windowWidth: number,
// ) {
//   const engine = getEngine(mainEngineId);
//   const vp = engine.getViewport(viewportId) as VolumeViewport3D;
//   if (!vp) return;
//   const vpProps = vp.getProperties();
//   if (vpProps && vpProps.voiRange) {
//     const { upper, lower } = vpProps.voiRange;

//     const halfWindowWidth = windowWidth * 0.5;
//     const newVOIRange = {
//       lower: windowLevel - halfWindowWidth,
//       upper: windowLevel + halfWindowWidth,
//     };
//     if (upper === newVOIRange.upper && lower === newVOIRange.lower) return;
//     vp.setProperties({ voiRange: newVOIRange });
//     vp.render();
//   }
// }

export async function updateViewportsWindowLevel(
  windowLevel: number,
) {
  getEnabledElements().forEach(({viewport:vp})=>{
    const vpProps = vp.getProperties();
    if (vpProps && vpProps.voiRange) {
      const { upper, lower } = vpProps.voiRange;
      const oldWL = (upper + lower) * 0.5;
      if (oldWL === windowLevel) return;

      const oldWWhalf = (upper - lower) * 0.5;
      vp.setProperties({
        voiRange: {
          lower: windowLevel - oldWWhalf,
          upper: windowLevel + oldWWhalf,
        },
      });
      vp.render();
    }
  });
}
export async function updateViewportsWindowWidth(
  windowWidth: number,
) {
  getEnabledElements().forEach(({viewport:vp})=>{
    const vpProps = vp.getProperties();
    if (vpProps && vpProps.voiRange) {
      const { upper, lower } = vpProps.voiRange;
      const oldWW = upper - lower;
      if (oldWW === windowWidth) return;
      const oldWL = (upper + lower) * 0.5;
      const newWWhalf = windowWidth * 0.5;
      vp.setProperties({
        voiRange: { lower: oldWL - newWWhalf, upper: oldWL + newWWhalf },
      });
      vp.render();
    }
  });
}
export async function updateViewportsWindowLevelWidth(
  windowLevel: number,
  windowWidth: number,
) {
  getEnabledElements().forEach(({viewport:vp})=>{
    const vpProps = vp.getProperties();
    if (vpProps && vpProps.voiRange) {
      const { upper, lower } = vpProps.voiRange;

      const halfWindowWidth = windowWidth * 0.5;
      const newVOIRange = {
        lower: windowLevel - halfWindowWidth,
        upper: windowLevel + halfWindowWidth,
      };
      if (upper === newVOIRange.upper && lower === newVOIRange.lower) return;
      vp.setProperties({ voiRange: newVOIRange });
      vp.render();
    }
  });
}

export function updateViewportCameraDeltaMove(param: {
  viewportId: PanelComponentKeyType;
  delta: Types.Point3;
}) {
  const { delta, viewportId } = param;
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) throw '뷰포 왜없져?';
  const camera = viewport.getCamera();
  // const eyeFocalDist = vec3.distance(camera.focalPoint!, camera.position!);

  viewport.setCamera({
    ...camera,
    focalPoint: camera.focalPoint!.map((v, i) => v + delta[i]) as Types.Point3,
    position: camera.position!.map((v, i) => v + delta[i]) as Types.Point3,
  });
  viewport.render();
}

export function updateViewportRotation(param: {
  viewportId: PanelComponentKeyType;
  rollRad: number;
}) {
  const { viewportId, rollRad } = param;
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) throw '뷰포 왜없져?';

  let rotation = (rollRad * 180.0) / Math.PI;
  while (rotation < 0) rotation += 360;
  while (rotation > 360) rotation -= 360;
  console.log(viewport.getRotation(), rotation);
  viewport.setViewPresentation({ rotation });
  viewport.render();
}

export function updateViewportCameraNormalOrUp(param: {
  viewportId: PanelComponentKeyType;
  up?: Types.Point3;
  normal?: Types.Point3;
}) {
  const { up, normal, viewportId } = param;
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) throw '뷰포 왜없져?';
  const camera = viewport.getCamera();
  // console.log(camera)
  viewport.setCamera({
    ...camera,
    viewUp: up ?? camera.viewUp,
    viewPlaneNormal: normal ?? camera.viewPlaneNormal,
    // focalPoint: camera.focalPoint!.map((v,i) => v + delta[i])  as Types.Point3,
    // position: camera.position!.map((v,i) => v + delta[i])  as Types.Point3,
  });
  viewport.render();
}

export function updateViewportCamera({
  viewportId,
  focalPoint,
  viewUp,
  viewPlaneNormal,
  thickness,
  thicknessMethod,
  handleUpdatePanelState,
  parallelScale,
}: {
  viewportId: PanelComponentKeyType;
  focalPoint: Types.Point3;
  viewUp: Types.Point3;
  viewPlaneNormal: Types.Point3;
  thickness?: number;
  thicknessMethod?: ThicknessMethodType;
  handleUpdatePanelState?: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void;
  parallelScale?: number;
}) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) throw '뷰포 왜없져?';
  const EPS = 1e-6;
  const camera = viewport.getCamera();
  if (
    !isFinitePoint3(focalPoint) ||
    !isFinitePoint3(viewUp) ||
    !isFinitePoint3(viewPlaneNormal) ||
    !isFinitePoint3(camera.focalPoint) ||
    !isFinitePoint3(camera.position)
  ) {
    return;
  }
  const eyeFocalDist = vec3.distance(camera.focalPoint, camera.position);

  // 개같은 버그
  const newFocalPoint =
    focalPoint[0] === camera.focalPoint[0] &&
    focalPoint[1] === camera.focalPoint[1] &&
    focalPoint[2] === camera.focalPoint[2]
      ? (focalPoint.map((v) => v + EPS) as Types.Point3)
      : focalPoint;

  const nextCamera = {
    ...camera,
    viewUp,
    focalPoint: newFocalPoint,
    viewPlaneNormal,
    position: viewPlaneNormal.map(
      (v, i) => newFocalPoint[i] + v * eyeFocalDist,
    ) as Types.Point3,
    parallelScale: isFinitePositiveNumber(parallelScale)
      ? parallelScale
      : camera.parallelScale,
  };

  viewport.setCamera(nextCamera);
  if (handleUpdatePanelState) {
    const direction = getViewportCameraDirection(nextCamera);
    if (direction) handleUpdatePanelState('direction', direction);
  }
  // console.log(viewport.getViewReference(),viewport,viewport.getCamera())
  // console.log(viewport.getCamera())
  // 들어온값에 반만 적용해야함
  if (thickness) viewport.setSlabThickness(thickness * 0.5);
  if (thicknessMethod) {
    switch (thicknessMethod) {
      case 'Average':
        viewport.setBlendMode(Enums.BlendModes.AVERAGE_INTENSITY_BLEND);
        break;
      case 'MinIP':
        viewport.setBlendMode(Enums.BlendModes.MINIMUM_INTENSITY_BLEND);
        break;
      case 'MIP':
      default:
        viewport.setBlendMode(Enums.BlendModes.MAXIMUM_INTENSITY_BLEND);
        break;
    }
  }
  viewport.render();
  return newFocalPoint;
}
export function updateViewportOrientation({
  viewportId,
  viewPlaneNormal,
  viewUp,
}: {
  viewportId: PanelComponentKeyType;
  viewPlaneNormal?: Types.Point3;
  viewUp?: Types.Point3;
}) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) return;

  if (!viewPlaneNormal || !viewUp) {
    const orientation = getDefaultViewportOrientation(viewportId);
    viewPlaneNormal = orientation.viewPlaneNormal;
    viewUp = orientation.viewUp;
  }

  viewport.setOrientation({ viewPlaneNormal, viewUp });
  // viewport.render();
}
export function getViewportCamera(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId);
  if (!viewport) throw '뷰포 왜없져?';
  return viewport.getCamera();
}
export function getCanvasCoordinatesFromWorld(
  viewportId: PanelComponentKeyType,
  world: Types.Point3,
): { x: number; y: number } | undefined {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId);

  if (!vp || typeof vp.worldToCanvas !== 'function') return;

  const canvasCoords = vp.worldToCanvas(world);
  return { x: canvasCoords[0], y: canvasCoords[1] };
}

export async function updateViewportToWorld(
  viewportId: PanelComponentKeyType,
  world: Types.Point3,
  options: { centerView?: boolean } = {},
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport;
  if (!vp) return;

  const shouldCenterView = options.centerView === true;
  if (shouldCenterView) {
    cancelPendingDicomFitToScreen();
  }

  runWithoutDicomPanZoomSync(() => {
    // selection/slice 이동은 jumpToWorld가 담당한다.
    vp.jumpToWorld(world);

    // Axial 기준으로 CrosshairsTool의 selection center를 동기화한다.
    // Coronal/Sagittal에서 들어온 focus 요청은 CrosshairsTool 내부 selection이 이미 처리하거나,
    // 이후 Axial focus render를 통해 들어오는 경로에서 처리된다.
    if (viewportId === 'Axial') {
      updateCrosshairCenter(world);
    }

    // 중앙 정렬은 in-plane pan만 적용한다. slice/axis normal 성분은 건드리지 않는다.
    if (shouldCenterView) {
      centerDicomViewportOnWorld(viewportId, world);
    }
  });
  // console.log(vp.getViewReference(),vp,vp.getCamera())
}

function isInBounds(world: [number, number, number], bounds: number[]) {
  return (
    world[0] >= bounds[0] &&
    world[0] <= bounds[1] &&
    world[1] >= bounds[2] &&
    world[1] <= bounds[3] &&
    world[2] >= bounds[4] &&
    world[2] <= bounds[5]
  );
}
function getNextPosition({
  center,
  normal,
  step,
}: {
  center: Types.Point3;
  normal: Types.Point3;
  step: number;
}) {
  return center.map((v, i) => v + normal[i] * step) as Types.Point3;
}

export function getWorldCoordinate(
  viewportId: PanelComponentKeyType,
  coord: Types.Point3,
  renderingQuality: RenderingQualityKeyType,
) {
  const vp = getViewport(viewportId);
  const imageData = vp.getImageData();
  if (!imageData) return coord;
  const { origin, spacing } = imageData;
  const scale = renderingQualityMapping[renderingQuality] < 3 ? 0.5 : 1;
  return coord.map(
    (v: number, i: number) => v * spacing[i] * scale + origin[i],
  ) as typeof coord;
}

export function getVolumeIndex(
  viewportId: PanelComponentKeyType,
  world: Types.Point3,
  renderingQuality: RenderingQualityKeyType,
) {
  const vp = getViewport(viewportId);
  const imageData = vp.getImageData();
  if (!imageData) return world;
  const { origin, spacing } = imageData;
  const scale = renderingQualityMapping[renderingQuality] < 3 ? 0.5 : 1;
  return world.map(
    (v: number, i: number) =>
      Math.abs(Math.round((v - origin[i]) / (spacing[i] * scale))) + 1,
  ) as Types.Point3;
}

export function getOrthoNormal(v1: Types.Point3, v2: Types.Point3) {
  return normalize(cross(v1, v2));
}

export function getCenters({
  center,
  spacing,
  viewPlaneNormal,
  pixelSpacing,
  imageSize,
  additionalSlice,
  flip,
}: {
  center: Types.Point3;
  spacing: number;
  viewPlaneNormal: Types.Point3;
  pixelSpacing: Types.Point3;
  imageSize: number;
  additionalSlice: number;
  flip?: boolean;
}) {
  // 뻗는 방향 동일하게
  const len = length(pixelSpacing);
  const planeNormal = scalePoint3(viewPlaneNormal, [len, len, len]);
  const planeNormalLength = length(planeNormal);
  const gridStep = (flip ? -1 : 1) * (spacing / planeNormalLength);
  const halfImageSize = imageSize * 0.5 + additionalSlice * spacing;

  let index = 0;
  let centers = [center];
  let nextCenter = getNextPosition({
    center,
    normal: planeNormal,
    step: -gridStep,
  });
  let distance = spacing;
  // console.log(planeNormal,gridStep)
  while (distance <= halfImageSize) {
    index++;
    distance += spacing;
    centers.push(nextCenter);
    nextCenter = getNextPosition({
      center: nextCenter,
      normal: planeNormal,
      step: -gridStep,
    });
  }

  centers = centers.reverse();
  nextCenter = getNextPosition({ center, normal: planeNormal, step: gridStep });
  distance = spacing;
  while (distance <= halfImageSize) {
    distance += spacing;
    centers.push(nextCenter);
    nextCenter = getNextPosition({
      center: nextCenter,
      normal: planeNormal,
      step: gridStep,
    });
  }

  return { centers, index };
}

const cosLUT = Array.from({ length: 360 }, (_, i) =>
  Math.cos((i * Math.PI) / 180),
);
const sinLUT = Array.from({ length: 360 }, (_, i) =>
  Math.sin((i * Math.PI) / 180),
);
export function alignAxis(
  longAxis: Types.Point3,
  upAxis: Types.Point3,
  // rightAxis: Types.Point3,
  targetDir: Types.Point3,
): {
  up: Types.Point3;
  theta: number;
  // right: Types.Point3
} {
  const rotateVecAroundAxis = (
    v: Types.Point3,
    axis: Types.Point3,
    // theta: number,
    degree: number,
  ): Types.Point3 => {
    // const cosTheta = Math.cos(theta);
    // const sinTheta = Math.sin(theta);
    const cosTheta = cosLUT[degree];
    const sinTheta = sinLUT[degree];

    const dotVA = dot(v, axis);
    const crossVA = cross(axis, v);

    return [
      v[0] * cosTheta +
        crossVA[0] * sinTheta +
        axis[0] * dotVA * (1 - cosTheta),
      v[1] * cosTheta +
        crossVA[1] * sinTheta +
        axis[1] * dotVA * (1 - cosTheta),
      v[2] * cosTheta +
        crossVA[2] * sinTheta +
        axis[2] * dotVA * (1 - cosTheta),
    ];
  };

  const axis = normalize(longAxis);
  const up = normalize(upAxis);
  // const right = normalize(rightAxis);
  const target = normalize(targetDir);

  let maxDot = -1;
  let bestTheta = 0;

  // const rad0 = Math.PI / 180;
  for (let i = 0; i < 360; ++i) {
    // const theta = i * rad0;
    // const upRotated = normalize(rotateVecAroundAxis(up, axis, theta));
    const upRotated = normalize(rotateVecAroundAxis(up, axis, i));
    const curDot = dot(upRotated, target);
    if (curDot > maxDot) {
      maxDot = curDot;
      // bestTheta = theta;
      bestTheta = i;
    }
  }

  const newUp = normalize(rotateVecAroundAxis(up, axis, bestTheta));
  // const newRight = normalize(rotateVecAroundAxis(right, axis, bestTheta));

  return {
    up: newUp,
    theta: bestTheta,
    // right: newRight,
  };
}

export function rotateAroundAxis(
  v: [number, number, number],
  axis: [number, number, number],
  angleRad: number,
) {
  const m = mat4.create();
  mat4.fromRotation(m, angleRad, axis);
  const vec3v = vec4.fromValues(v[0], v[1], v[2], 0);
  const out = vec4.create();
  vec4.transformMat4(out, vec3v, m);
  return normalize([out[0], out[1], out[2]] as Types.Point3);
}

function handleZoom(
  targetViewport: VolumeViewport,
  refViewport: VolumeViewport,
  delta: number,
  zoomFactor: number,
) {
  const camera = targetViewport.getCamera();
  if (!camera.parallelProjection) return;

  const currentScale = camera.parallelScale ?? 1.0;
  const zoomF = zoomFactor;
  const newScale = currentScale * (1 + zoomF * delta);
  const clampedScale = Math.min(
    Math.max(newScale, MIN_PARALLEL_SCALE),
    MAX_PARALLEL_SCALE,
  );

  camera.parallelScale = clampedScale;
  targetViewport.setCamera(camera);
  refViewport.setCamera({
    ...refViewport.getCamera(),
    parallelScale: clampedScale,
  });

  refViewport.render();
  targetViewport.render();
  requestAnimationFrame(() =>
    scheduleRender('Direction', {
      type: 'guideZoom',
      target: targetViewport.id,
    }),
  );
}
function handleScroll(
  targetViewportId: PanelComponentKeyType,
  delta: number,
  isRef: boolean,
) {
  // const direction = Math.sign(delta);
  requestAnimationFrame(() =>
    scheduleRender('Direction', {
      type: 'guideSliceMove',
      delta,
      viewportId: targetViewportId,
      isRef,
    }),
  );
}
function handleRotation(e: PointerEvent, isRef: boolean) {
  if (e.movementX !== 0 || e.movementY !== 0) {
    requestAnimationFrame(() =>
      scheduleRender('Direction', {
        type: 'guideRoll',
        movement: [e.movementX, e.movementY],
        client: isRef ? [e.clientX, e.clientY] : [e.layerX, e.layerY],
        isRef,
      }),
    );
  }
}
function handleScoutDisplayRotation(e: PointerEvent) {
  if (e.movementX !== 0 || e.movementY !== 0) {
    requestAnimationFrame(() =>
      scheduleRender('Direction', {
        type: 'scoutDisplayRoll',
        movement: [-e.movementX, -e.movementY],
        client: [e.clientX, e.clientY],
      }),
    );
  }
}
function handlePan(viewport: VolumeViewport, e: PointerEvent, isRef: boolean) {
  const ref = viewport.canvasToWorld([0, 0]);
  const movement = viewport.canvasToWorld([e.movementX, e.movementY]);
  requestAnimationFrame(() =>
    scheduleRender('Direction', {
      type: 'guidePan',
      translation: [
        Number((movement[0] - ref[0]).toFixed(5)),
        Number((movement[1] - ref[1]).toFixed(5)),
        Number((movement[2] - ref[2]).toFixed(5)),
      ] as Types.Point3,
      isRef,
    }),
  );
}
export function setVOIAll(
  voi: { lower: number; upper: number },
  isNegative?: boolean,
) {
  const engine = getEngine(mainEngineId);
  const viewports = engine.getViewports?.() ?? [];

  if (isNegative) {
    viewports.forEach((vp) => {
      setViewportNegative(vp.id as PanelComponentKeyType, true);
    });
  }

  for (const vp of viewports) {
    if (typeof (vp as any).setProperties === 'function') {
      (vp as any).setProperties({ voiRange: voi });
    }
  }

  if (typeof (engine as any).renderAll === 'function') {
    (engine as any).renderAll();
  } else {
    for (const vp of viewports) (vp as any).render?.();
  }
}
function handleVOI(targetViewport: VolumeViewport, e: PointerEvent) {
  //똑같이이슈
  let deltaY = -e.movementY;
  let deltaX = e.movementX;

  const voi = targetViewport.getProperties?.().voiRange;
  if (!voi) return;
  let { upper, lower } = voi;

  let ww = upper - lower;
  let wl = (upper + lower) / 2;

  const wwFactor = 2.0;
  const wlFactor = 2.0;

  ww += deltaX * wwFactor;
  wl -= deltaY * wlFactor;

  ww = Math.max(ww, 1);

  scheduleRender('Direction', {
    type: 'afterVOIModified',
    windowWidth:ww,
    windowLevel:wl,
  });
  // upper = wl + ww / 2;
  // lower = wl - ww / 2;

  // setVOIAll({ lower, upper });
}

function isInside(xy: Types.Point2, polygon: Types.Point2[]) {
  const [x, y] = xy;
  let intersections = 0;
  for (let i = 0; i < 4; i++) {
    const [sx, sy] = polygon[i];
    const [ex, ey] = polygon[(i + 1) % 4];
    if (
      ((sy <= y && y < ey) || (ey <= y && y < sy)) &&
      x < ((ex - sx) * (y - sy)) / (ey - sy) + sx
    ) {
      intersections++;
    }
  }
  return intersections % 2 == 1;
}

export function lastChamberTransform(
  viewportId: PanelComponentKeyType,
  rollRad: number,
  flipVertical: boolean,
  flipHorizontal: boolean,
  handleUpdatePanelState?: (
    key: keyof PanelState | string,
    value: PanelState[keyof PanelState],
  ) => void,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as VolumeViewport;

  const camera = vp.getCamera();
  const { viewUp, viewPlaneNormal, focalPoint, position } = camera;
  if (!viewUp || !viewPlaneNormal || !focalPoint || !position) return;

  let newViewUp = viewUp;
  let newPlaneNormal = viewPlaneNormal;
  //회전먼저
  newViewUp = rotateAroundAxis(newViewUp, newPlaneNormal, rollRad);

  newViewUp = flipVertical
    ? (vec3.negate([0, 0, 0], newViewUp) as Types.Point3)
    : newViewUp;
  newPlaneNormal = flipVertical
    ? (vec3.negate([0, 0, 0], newPlaneNormal) as Types.Point3)
    : newPlaneNormal;
  newPlaneNormal = flipHorizontal
    ? (vec3.negate([0, 0, 0], newPlaneNormal) as Types.Point3)
    : newPlaneNormal;
  // newViewUp = rotateAroundAxis(newViewUp, newPlaneNormal, rollRad);

  // let newViewUp = rotateAroundAxis(viewUp,viewPlaneNormal,rollRad);

  const neg = (Number(flipVertical) + Number(flipHorizontal)) % 2 === 1;
  const offset = [
    position[0] - focalPoint[0],
    position[1] - focalPoint[1],
    position[2] - focalPoint[2],
  ].map((v) => (neg ? -v : v)) as Types.Point3;

  const newOffset = rotateAroundAxis(offset, newPlaneNormal, rollRad);
  const newPosition = [
    focalPoint[0] + newOffset[0],
    focalPoint[1] + newOffset[1],
    focalPoint[2] + newOffset[2],
  ] as Types.Point3;

  // newViewUp = flipVertical?vec3.negate([0,0,0],viewUp) as Types.Point3:viewUp;
  // let newPlaneNormal = flipVertical?vec3.negate([0,0,0],viewPlaneNormal) as Types.Point3:viewPlaneNormal;
  // newPlaneNormal = flipHorizontal?vec3.negate([0,0,0],newPlaneNormal) as Types.Point3:newPlaneNormal;

  const nextCamera = {
    ...camera,
    focalPoint,
    position: newPosition,
    viewUp: newViewUp,
    viewPlaneNormal: newPlaneNormal,
  };

  vp.setCamera(nextCamera);
  if (handleUpdatePanelState) {
    const direction = getViewportCameraDirection(nextCamera);
    if (direction) handleUpdatePanelState('direction', direction);
  }
  vp.render();
  return { viewPlaneNormal: newPlaneNormal, viewUp: newViewUp };
}

let chamberFlag = false;
export function setChamberViewEvents({
  targetViewportId,
  toolName,
}: {
  targetViewportId: PanelComponentKeyType;
  toolName: string;
}) {
  const refViewportId = 'Direction';
  if (refViewportId === targetViewportId) throw 'ㅁㅊ';
  const engine = getEngine(mainEngineId);
  const refViewport = engine.getViewport(refViewportId) as VolumeViewport;
  const targetViewport = engine.getViewport(targetViewportId) as VolumeViewport;
  if (!refViewport || !targetViewport) throw `ㅁㅊㅁㅊ;;${targetViewportId}`;
  const wheel = (e: WheelEvent) => {
    const direction = Math.sign(e.deltaY);
    if (e.ctrlKey) {
      handleZoom(targetViewport, refViewport, direction, 0.1);
      return;
    }
    if (e.deltaY === 0) return;
    let delta = e.deltaY;
    if (e.deltaMode === 1) delta *= 16;
    else if (e.deltaMode === 2) delta *= window.innerHeight;
    const zoomFactor = Math.min(Math.abs(e.deltaY) / 100, 10);

    // 요것은 항시 guide가 움지는거로.
    requestAnimationFrame(() =>
      scheduleRender('Direction', {
        type: 'guideSliceMove',
        delta: direction * zoomFactor,
        viewportId: targetViewportId,
      }),
    );
  };

  const move = (e: PointerEvent, viewportId: PanelComponentKeyType) => {
    const isRef = viewportId === refViewportId;
    const noKeys = !e.ctrlKey && !e.altKey && !e.shiftKey;
    // console.log(noKeys)
    if (e.buttons === 1 && !chamberFlag) {
      if (!toolName) return;
      switch (toolName) {
        case 'Scroll':
          handleScroll(targetViewportId, e.movementY, isRef);
          break;
        case 'Rotation':
          if (isRef) handleScoutDisplayRotation(e);
          else handleRotation(e, isRef);
          break;
        case 'Zoom':
          handleZoom(targetViewport, refViewport, e.movementY, 0.003);
          break;
        case 'Pan':
          handlePan(isRef ? refViewport : targetViewport, e, isRef);
          break;
        case 'WindowLevel':
          handleVOI(targetViewport, e);
        default:
          break;
      }
    }

    if (e.buttons === 1 && e.ctrlKey) {
      handleRotation(e, isRef);
    }

    if (e.buttons === 2) {
      handlePan(isRef ? refViewport : targetViewport, e, isRef);
    }
    if (e.buttons === 3) {
      handleZoom(targetViewport, refViewport, e.movementY, 0.003);
    }
  };
  const onMouseDownTarget = () => {
    chamberFlag = false;
  };
  // const test : PanelComponentKeyType[]=['Axial','FourChamber','ShortAxis','TwoChamber'];
  const onMouseDown = (e: PointerEvent) => {
    const svg =
      refViewport.element.parentElement?.querySelector('svg#custom-overlay');
    const g = svg?.querySelector('g')!;
    // test.forEach(vi=>{
    //   const vp = engine.getViewport(vi) as VolumeViewport;
    //   console.log(vi,vp.getRotation(),vp.getViewPresentation(),vp.getCamera().rotation,vp.options)
    // })
    const len = g.children.length;
    const isIn = isInside(
      [e.offsetX, e.offsetY],
      [
        [
          Number(g.children[0].getAttribute('x1')),
          Number(g.children[0].getAttribute('y1')),
        ],
        [
          Number(g.children[0].getAttribute('x2')),
          Number(g.children[0].getAttribute('y2')),
        ],
        [
          Number(g.children[len - 1].getAttribute('x2')),
          Number(g.children[len - 1].getAttribute('y2')),
        ],
        [
          Number(g.children[len - 1].getAttribute('x1')),
          Number(g.children[len - 1].getAttribute('y1')),
        ],
      ],
    );
    chamberFlag = isIn;
  };
  const onMouseUp = (e: PointerEvent) => {
    chamberFlag = false;
  };
  const moveTarget = (e: PointerEvent) => move(e, targetViewportId);
  const moveRef = (e: PointerEvent) => move(e, refViewportId);
  // const downTarget = (e:PointerEvent)=>onMouseDown(e,targetViewport.element)

  targetViewport.element.addEventListener('wheel', wheel);
  refViewport.element.addEventListener('wheel', wheel);
  targetViewport.element.addEventListener('pointermove', moveTarget);

  targetViewport.element.addEventListener('pointerdown', onMouseDownTarget);
  targetViewport.element.addEventListener('pointerup', onMouseUp);

  refViewport.element.addEventListener('pointermove', moveRef);
  refViewport.element.addEventListener('pointerdown', onMouseDown);
  refViewport.element.addEventListener('pointerup', onMouseUp);

  return () => {
    targetViewport.element.removeEventListener('wheel', wheel);
    refViewport.element.removeEventListener('wheel', wheel);
    targetViewport.element.removeEventListener('pointermove', moveTarget);
    targetViewport.element.removeEventListener(
      'pointerdown',
      onMouseDownTarget,
    );
    targetViewport.element.removeEventListener('pointerup', onMouseUp);
    refViewport.element.removeEventListener('pointermove', moveRef);
    refViewport.element.removeEventListener('pointerdown', onMouseDown);
    refViewport.element.removeEventListener('pointerup', onMouseUp);
  };
}
export function setupGuideViewport({
  viewportId,
  planeNormal,
  up,
  focalPoint,
}: {
  viewportId: PanelComponentKeyType;
  planeNormal: Types.Point3;
  up: Types.Point3;
  focalPoint: Types.Point3;
}) {
  const refViewportId = 'Direction';
  const engine = getEngine(mainEngineId);
  const refViewport = engine.getViewport(refViewportId) as VolumeViewport;
  const targetViewport = engine.getViewport(viewportId) as VolumeViewport;

  const targetCamera = targetViewport.getCamera();

  const refCamera = refViewport.getCamera();
  const refDist = vec3.distance(refCamera.focalPoint!, refCamera.position!);

  // console.log(targetCamera,
  //     planeNormal,
  //     up,
  //     focalPoint,
  // );
  refViewport.setCamera({
    ...refCamera,
    viewPlaneNormal: planeNormal,
    viewUp: up,
    focalPoint,
    position: planeNormal.map(
      (v, i) => refCamera.focalPoint![i] + v * refDist,
    ) as Types.Point3,
    parallelScale: targetCamera.parallelScale,
  });
  refViewport.render();
}

export function unsetInvisibleSection({
  viewportId,
}: {
  viewportId: PanelComponentKeyType;
}) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  if (!viewport) return;
  const { element } = viewport;
  if (!element) return;

  const overlay = element.querySelector(`svg.overlay${viewportId}`);
  if (overlay) element.removeChild(overlay);

  viewport.render();
}

export function getInvisibleSection({
  viewportId,
  imageSize,
  pixelSpacing,
}: {
  viewportId: PanelComponentKeyType;
  imageSize: number;
  pixelSpacing: Types.Point3;
}) {
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(viewportId) as VolumeViewport;
  // const pixelSpacing = viewport.getImageData()?.spacing!

  const centerX = viewport.canvas.width / 2 / window.devicePixelRatio;
  const centerY = viewport.canvas.height / 2 / window.devicePixelRatio;

  const camera = viewport.getCamera();

  const up = scalePoint3(camera.viewUp!, pixelSpacing);
  const upLength = length(up);
  const right = scalePoint3(
    getOrthoNormal(camera.viewUp!, camera.viewPlaneNormal!),
    pixelSpacing,
  );
  const rightLength = length(right);

  const upStep = (imageSize * 0.5) / upLength;
  const rightStep = (imageSize * 0.5) / rightLength;

  const center = viewport.canvasToWorld([centerX, centerY]);

  const dnl = getNextPosition({ center, normal: up, step: upStep });
  const dn = getNextPosition({ center, normal: right, step: rightStep });

  const dY = Math.abs(centerY - viewport.worldToCanvas(dnl)[1]);
  const dX = Math.abs(viewport.worldToCanvas(dn)[0] - centerX);

  const corners: [number, number][] = [
    [centerX - dX, centerY + dY],
    [centerX + dX, centerY + dY],
    [centerX + dX, centerY - dY],
    [centerX - dX, centerY - dY],
  ];

  // console.log(viewport.canvasToWorld(corners[3]))
  // console.log(corners)
  let svg = viewport.element.querySelector(
    `svg.overlay${viewportId}`,
  ) as SVGSVGElement | null;
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add(`overlay${viewportId}`);
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none'; // 마우스 이벤트 차단 (뷰포트 인터랙션 방해 X)
    viewport.element.appendChild(svg);
  }

  const maskId = `shade-mask-${viewportId}`; // 뷰포트별 고유 mask id

  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.appendChild(defs);
  }

  let mask = defs.querySelector(`mask#${maskId}`) as SVGMaskElement | null;
  if (!mask) {
    mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
    mask.setAttribute('id', maskId);
    defs.appendChild(mask);

    const full = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    full.setAttribute('x', '0');
    full.setAttribute('y', '0');
    full.setAttribute('width', '100%');
    full.setAttribute('height', '100%');
    full.setAttribute('fill', 'white');
    mask.appendChild(full);
  }

  let polygon = mask.querySelector('polygon') as SVGPolygonElement | null;
  if (!polygon) {
    polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    mask.appendChild(polygon);
  }
  polygon.setAttribute(
    'points',
    corners.map(([x, y]) => `${x},${y}`).join(' '),
  );
  polygon.setAttribute('fill', 'black');

  let shade = svg.querySelector('rect.shade') as SVGRectElement | null;
  if (!shade) {
    shade = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    shade.classList.add('shade');
    shade.setAttribute('x', '0');
    shade.setAttribute('y', '0');
    shade.setAttribute('width', '100%');
    shade.setAttribute('height', '100%');
    shade.setAttribute('fill', 'rgba(0,0,0,0.5)');
    shade.setAttribute('mask', `url(#${maskId})`);
    svg.appendChild(shade);
  }
  let edge = svg.querySelector('polygon.edge') as SVGPolygonElement | null;
  if (!edge) {
    edge = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    edge.classList.add('edge');
    edge.setAttribute('fill', 'none');
    edge.setAttribute('stroke', 'red');
    edge.setAttribute('stroke-width', '0.5');
    edge.setAttribute('pointer-events', 'none');
    svg.appendChild(edge);
  }

  edge.setAttribute('points', corners.map(([x, y]) => `${x},${y}`).join(' '));
  viewport.render();
  return { dX, dY };
}
export function getGuideLines({
  viewportId,
  axis,
  centers,
  pixelSpacing,
  imageSize,
}: {
  viewportId: PanelComponentKeyType;
  axis: Types.Point3;
  centers: Types.Point3[];
  pixelSpacing: Types.Point3;
  imageSize: number;
}) {
  const refViewportId = 'Direction';
  const engine = getEngine(mainEngineId);
  const viewport = engine.getViewport(refViewportId) as VolumeViewport;
  const halfImageSize = imageSize * 0.5;

  const normal = scalePoint3(axis, pixelSpacing).map((v) => -v) as Types.Point3;
  const normalLength = length(normal);
  const step = halfImageSize / normalLength;

  const ubs = centers.map((center) => {
    return {
      u: getNextPosition({ center, normal, step }),
      b: getNextPosition({ center, normal, step: -step }),
    };
  });
  if (ubs.length < 2) { return undefined; }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('pointer-events', 'none');
  svg.appendChild(g);

  // worldToCanvas 성능 구려요,최소화
  const initU = viewport.worldToCanvas(ubs[0].u);
  const initB = viewport.worldToCanvas(ubs[0].b);

  const nextU = viewport.worldToCanvas(ubs[1].u);
  const nextB = viewport.worldToCanvas(ubs[1].b);

  const deltaU = [nextU[0] - initU[0], nextU[1] - initU[1]] as Types.Point2;
  const deltaB = [nextB[0] - initB[0], nextB[1] - initB[1]] as Types.Point2;

  let U = initU;
  let B = initB;

  // const lineLength = U[0]-B[0]!==0 || U[1]-B[1]!==0 ? Math.sqrt((U[0]-B[0])**2+(U[1]-B[1])**2):1;

  const dash = 1;
  const gap = 10;
  const borderCount = Math.round(20 / distance(ubs[0].u, ubs[1].u));

  ubs.forEach((_, index) => {
    const xy1 = U;
    const xy2 = B;
    U = [U[0] + deltaU[0], U[1] + deltaU[1]];
    B = [B[0] + deltaB[0], B[1] + deltaB[1]];
    const fakeLine = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'line',
    );
    fakeLine.setAttribute('class', `faker`);
    fakeLine.setAttribute('pointer-events', 'stroke');
    fakeLine.setAttribute('x1', xy1[0].toString());
    fakeLine.setAttribute('y1', xy1[1].toString());
    fakeLine.setAttribute('x2', xy2[0].toString());
    fakeLine.setAttribute('y2', xy2[1].toString());
    fakeLine.setAttribute('stroke', 'transparent');
    fakeLine.setAttribute('stroke-width', '4');

    const isBorderLine = index % borderCount == 0 || index == ubs.length - 1;

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('pointer-events', 'none');
    line.setAttribute('x1', xy1[0].toString());
    line.setAttribute('y1', xy1[1].toString());
    line.setAttribute('x2', xy2[0].toString());
    line.setAttribute('y2', xy2[1].toString());
    line.setAttribute('shape-rendering', 'geometricPrecision');
    line.setAttribute('stroke', 'white');
    line.setAttribute('stroke-width', '0.6');
    line.setAttribute('vector-effect', 'non-scaling-size');
    if (!isBorderLine) {
      line.setAttribute('stroke-dasharray', `${dash} ${gap} ${dash} ${gap}`);
      // line.setAttribute('stroke-dashoffset', `${dashOffset}`);
    }

    fakeLine.addEventListener(
      'wheel',
      (e: WheelEvent) =>
        viewport.element.dispatchEvent(new WheelEvent('wheel', e)),
      { passive: true },
    );
    fakeLine.addEventListener('pointerdown', (e: PointerEvent) => {
      chamberFlag = true;
      if (e.ctrlKey) {
        viewport.element.dispatchEvent(new PointerEvent('pointerdown', e));
        return;
      }
      if (e.buttons === 1)
        scheduleRender('Direction', {
          type: 'guideSliceMove',
          nextIndex: index,
          viewportId,
        });
    });
    fakeLine.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.ctrlKey || e.buttons === 2) {
        viewport.element.dispatchEvent(new PointerEvent('pointermove', e));
        return;
      }
      if (e.buttons === 1) {
        if (chamberFlag)
          scheduleRender('Direction', {
            type: 'guideSliceMove',
            nextIndex: index,
            viewportId,
          });
        else viewport.element.dispatchEvent(new PointerEvent('pointermove', e));
      }
      if (e.buttons === 0) line.setAttribute('stroke', 'yellow');
    });
    fakeLine.addEventListener('pointerup', (e: PointerEvent) => {
      chamberFlag = false;
    });
    fakeLine.addEventListener('pointerout', () => {
      line.setAttribute('stroke', 'white');
    });

    g.appendChild(fakeLine);
    g.appendChild(line);
  });

  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line.setAttribute('id', 'highlight');
  line.setAttribute('stroke', 'green');
  line.setAttribute('stroke-width', '2');
  svg.appendChild(line);

  const text0 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text0.setAttribute('id', 'start');
  text0.setAttribute('shape-rendering', 'geometricPrecision');
  text0.setAttribute('stroke', 'white');
  text0.setAttribute('stroke-width', '0.6');
  text0.setAttribute(
    'x',
    ((initU[0] - deltaU[0] + initB[0] - deltaB[0]) * 0.5).toString(),
  );
  text0.setAttribute(
    'y',
    ((initU[1] - deltaU[1] + initB[1] - deltaB[1]) * 0.5).toString(),
  );
  text0.textContent = '1';
  svg.appendChild(text0);

  const text1 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text1.setAttribute('id', 'end');
  text1.setAttribute('shape-rendering', 'geometricPrecision');
  text1.setAttribute('stroke', 'white');
  text1.setAttribute('stroke', 'white');
  text1.setAttribute('stroke-width', '0.6');
  text1.setAttribute(
    'x',
    ((U[0] + deltaU[0] + B[0] + deltaB[0]) * 0.5).toString(),
  );
  text1.setAttribute(
    'y',
    ((U[1] + deltaU[1] + B[1] + deltaB[1]) * 0.5).toString(),
  );
  text1.textContent = `${ubs.length}`;
  svg.appendChild(text1);

  return svg;
}


export function updateCrosshairCenter(position: Types.Point3) {
  const toolGroup = ToolGroupManager.getToolGroup('sharedToolGroup');
  if (!toolGroup) return;
  // console.log('콜록콜록2')

  let viewportIds = toolGroup.getViewportIds();
  if (viewportIds && viewportIds.length === 1) {
    return; //viewportIds 3개일때만 움직여야지. 한개는 뭐하러 옮겨
  }
  const crosshairTool = toolGroup.getToolInstance(
    CustomCrosshairsTool.toolName,
  ) as CustomCrosshairsTool;
  const viewportsInfo = crosshairTool._getViewportsInfo();
  viewportsInfo.map(({ viewportId }) => {
    const el = getEnabledElementByViewportId(viewportId);
    crosshairTool._jump(el, position);
  });
}


// sync manager
export function registerSynchronizeViewport(viewportId: PanelComponentKeyType) {
  const renderingEngine = getRenderingEngine(mainEngineId);
  if (!renderingEngine?.getViewport?.(viewportId)) return;

  if (SynchronizerManager.getAllSynchronizers().length === 0) {
    // synchronizers.createZoomPanSynchronizer('zoomTest');
    // synchronizers.createImageSliceSynchronizer;
    // synchronizers.createSlabThicknessSynchronizer('thicknessTest');
    synchronizers.createVOISynchronizer('VOITest', {
      syncInvertState: true,
      syncColormap: true,
    });
  }

  // synchronizers.
  // const zoomSync = SynchronizerManager.getSynchronizer(
  //   'zoomTest',
  // ) as Synchronizer;
  // const slabSync = SynchronizerManager.getSynchronizer(
  //   'thicknessTest',
  // ) as Synchronizer;
  const VOISync = SynchronizerManager.getSynchronizer(
    'VOITest',
  ) as Synchronizer;
  // const presentSync = SynchronizerManager.getSynchronizer('presentTest') as Synchronizer;

  const sourceViewport: Types.IViewportId = {
    renderingEngineId: mainEngineId,
    viewportId: viewportId,
  };
  // const targetViewport : Types.IViewportId[] = targetViewportIds.map(id=>({renderingEngineId:mainEngineId,viewportId:id}));

  // zoomSync.add(sourceViewport);
  // slabSync.add(sourceViewport);
  VOISync.add(sourceViewport);

  // console.log(zoomSync.getSourceViewports(), zoomSync.getTargetViewports());
  // presentSync.add(sourceViewport)
}

export function unregisterSynchronizeViewport() {
  // SynchronizerManager.destroySynchronizer('synchronizer');
  SynchronizerManager.destroySynchronizer('VOITest');
  // SynchronizerManager.destroySynchronizer('thicknessTest');
  // SynchronizerManager.destroySynchronizer('zoomTest');
  // SynchronizerManager.destroySynchronizer('presentTest');
}

export function getManualVolumeFromSeriesId(seriesId: string) {
  const manualVolumeId = `${seriesId}_manual`;

  return cache.getVolume(manualVolumeId);
}

export function getVolumeFromSeriesId(seriesId: string, half: boolean = false) {
  return half
    ? getPreViewVolumeFromSeriesId(seriesId)
    : getOriginalVolumeFromSeriesId(seriesId);
}

export function getOriginalVolumeFromSeriesId(seriesId: string) {
  return cache.getVolume(`ORIGINAL_${seriesId}`);
}

export function getLabelVolumeFromSeriesId(
  seriesId: string,
  half: boolean = false,
) {
  return cache.getVolume(
    half ? `${seriesId}_labelmapHalf` : `${seriesId}_labelmap`,
  );
}

export function getBranchesVolumeFromSeriesId(
  seriesId: string,
  half: boolean = false,
) {
  return cache.getVolume(
    half ? `${seriesId}_branchesHalf` : `${seriesId}.branches`,
  );
}

export function getLawtVolumeFromSeriesId(
  seriesId: string,
  half: boolean = false,
) {
  return cache.getVolume(half ? `${seriesId}.lawtHalf` : `${seriesId}.lawt`);
}

export function checkOriginalVolumeFromSeriesId(seriesId: string) {
  return !getOriginalVolumeFromSeriesId(seriesId);
}

export function getPreViewVolumeFromSeriesId(seriesId: string) {
  return cache.getVolume(`PREVIEW_${seriesId}`);
}

export function gecalarDataFromVolume(volume: ImageVolume) {
  // const volume = getOriginalVolumeFromSeriesId(seriesId);
  // if(!volume) return;
  const { voxelManager } = volume;
  if (!voxelManager) return;
  if (!voxelManager.getCompleteScalarDataArray) return;
  return voxelManager.getCompleteScalarDataArray();
}

export function disableElement(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  engine.disableElement(viewportId);
}
export async function setDICOMViewportVolume({
  viewportId,
  element,
  // element :HTMLDivElement,
  afterInitialize,
  seriesId,
  slabThickness,
  thicknessMethod,
  lowQuality,
}: {
  viewportId: PanelComponentKeyType;
  element: HTMLDivElement;
  // element :HTMLDivElement,
  afterInitialize: () => void;
  seriesId: string;
  slabThickness?: number;
  thicknessMethod?: ThicknessMethodType;
  lowQuality?: boolean;
}) {
  const engine = getEngine(mainEngineId);

  engine.enableElement(getPublicViewportInput(viewportId, element));
  const cachedVolume = getVolumeFromSeriesId(seriesId, lowQuality); //cache.getVolume(`ORIGINAL_${seriesId}`);
  if (!cachedVolume) {
    let blendMode;
    switch (thicknessMethod) {
      case 'Average':
        blendMode = Enums.BlendModes.AVERAGE_INTENSITY_BLEND;
        break;
      case 'MinIP':
        blendMode = Enums.BlendModes.MINIMUM_INTENSITY_BLEND;
        break;
      case 'MIP':
      default:
        blendMode = Enums.BlendModes.MAXIMUM_INTENSITY_BLEND;
        break;
    }
    const volumeId = `PREVIEW_${seriesId}`;

    await setVolumesForViewports(
      engine,
      [
        {
          volumeId,
          slabThickness: slabThickness ? slabThickness * 0.5 : slabThickness, //반만적용해야해
          blendMode,
        } as Types.IVolumeInput,
      ],
      [viewportId],
      true,
      false,
    );
  }
  // if (viewportId === 'Axial' ||
  //   viewportId === 'Coronal' ||
  //   viewportId === 'Sagittal'
  // ) {
  //   if (stop2DConnectedConsoleRef[viewportId]) {
  //     stop2DConnectedConsoleRef[viewportId]!();
  //   }
  //   stop2DConnectedConsoleRef[viewportId] = enableShiftLiveFlood(viewportId);
  // }

  afterInitialize();
}

export function detachVolume(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  // const vp = engine.getViewport(viewportId) as Types.IVolumeViewport;
  engine.disableElement(viewportId);
  console.log('볼륨 뗌', viewportId);
}

export function getViewportIds() {
  const engine = getEngine(mainEngineId);

  return engine.getViewports().map((viewport) => viewport.id);
}

export function getViewportIdsEnabled(viewportIds: PanelComponentKeyType[]) {
  const engine = getEngine(mainEngineId);
  // 몹시 단순하게. 솔까 정확한 id도 필요없음. call한녀석이 해야해
  return engine.getViewports().length === viewportIds.length;
}

export function addRequestPool({
  accessToken,
  series,
  volumeId,
  filepath,
  element,
  priority = 10,
  postFunction,
  viewportId,
  slabThickness,
  thicknessMethod,
  lowQuality,
}: {
  accessToken: string;
  series: Series;
  element?: HTMLDivElement;
  volumeId: string;
  filepath: string;
  priority: number;
  postFunction?: (_fail?: boolean) => void;
  viewportId?: PanelComponentKeyType;
  slabThickness?: number;
  thicknessMethod?: ThicknessMethodType;
  lowQuality?: boolean | undefined;
}) {
  const cachedVolume = cache.getVolume(volumeId);
  const setVolume = async () => {
    if (viewportId) {
      const engine = getEngine(mainEngineId);
      const vp = engine.getViewport(viewportId) as Types.IVolumeViewport;
      // if(!vp && element){
      //   engine.enableElement(getPublicViewportInput(viewportId,element));
      // }
      let blendMode;
      switch (thicknessMethod) {
        case 'Average':
          blendMode = Enums.BlendModes.AVERAGE_INTENSITY_BLEND;
          break;
        case 'MinIP':
          blendMode = Enums.BlendModes.MINIMUM_INTENSITY_BLEND;
          break;
        case 'MIP':
        default:
          blendMode = Enums.BlendModes.MAXIMUM_INTENSITY_BLEND;
          break;
      }

      // 대부분 디버깅시점에
      if (!vp) {
        engine.enableElement(getPublicViewportInput(viewportId, element!));
      }
      await setVolumesForViewports(
        engine,
        [
          {
            volumeId,
            slabThickness: slabThickness ? slabThickness * 0.5 : slabThickness, //반만적용
            blendMode,
          } as Types.IVolumeInput,
        ],
        [viewportId],
        true,
        true,
      );
    }
    if (postFunction) postFunction();
  };

  if (cachedVolume) {
    setVolume();
  } else {
    const sendRequest = async () => {
      console.log('따운????????', volumeId, viewportId);
      const { data, status } = await fetchSeriesArrayBuffer({
        accessToken,
        seriesId: series.id,
        target: `${filepath}.nii.gz`,
      });
      // console.log(filepath, data);
      if (status !== 200) {
        if (postFunction) {
          console.log('혹시 timeOut?')
          postFunction(true)
        };
        return;
      }

      const { imageData, headers } = await runWorkerTask({
        type: 'decompress',
        data,
        genHeader: true,
      });

      headers.metadata.Modality = series.modality;
      headers.metadata.voiLut = [
        {
          windowWidth: series.imageTag.windowWidth ?? defaultWindowWidth,
          windowCenter: series.imageTag.windowCenter ?? defaultWindowCenter,
        },
      ];

      headers.scalarData = new Int16Array(imageData);
      const volume = volumeLoader.createLocalVolume(volumeId, headers);
      await volume.load();
      setVolume();
    };
    downloadQueue.enqueue(sendRequest, priority);
  }
}
type DownloadAndDecompressType = {
  imageData: ArrayBuffer;
  headers: Types.LocalVolumeOptions;
  out?: Float32Array;
};
const immediateRawBufferPool: Record<
  string,
  Promise<DownloadAndDecompressType> | undefined
> = {};

export const BitMaskMap: Record<string, number> = {
  coronaryWall: 1 << 0,
  calcium: 1 << 1,
  la: 1 << 2,
  lv: 1 << 3,
  ra: 1 << 4,
  rv: 1 << 5,
  aorta: 1 << 6,
  coronary: 1 << 7,
  laWall: 1 << 8,
  lvWall: 1 << 9,
  raWall: 1 << 10,
  rvWall: 1 << 11,
  epicardial: 1 << 12,
  avc: 1 << 13,
} as const;
const InvertBitMaskMap = Object.fromEntries(
  Object.entries(BitMaskMap).map(([key, value]) => [value, key]),
) as { [key: number]: string };

// let bitMaskSet : Record<string,Set<number>> = {};
let bitMaskSetMap: Record<string, Record<string, Set<number>>> = {};
// let arrangeBitMask : Record<number,number> = {};
let arrangeBitMaskMap: Record<string, Record<number, number>> = {};
// let uniqueIndices : Set<number> = new Set();
const bufferQueuePriority = 2;
export function clearDownloadBufferQueue() {
  console.log({bitMaskSetMap, arrangeBitMaskMap});
  // bitMaskSet={};
  bitMaskSetMap = {};
  // arrangeBitMask={};
  arrangeBitMaskMap = {};
  if (!immediateRawBufferPool) return;
  Object.entries(immediateRawBufferPool).forEach(([key, value]) => {
    // if(value) {
    //   // immediateRawBufferPool[key]=undefined;
    // }
    console.log('꺄하하하학 지워버러야지', key);
    delete immediateRawBufferPool[key];
  });
  downloadQueue.clearSpecificPriority(bufferQueuePriority);
}

const initialDownloadQueueTargets = {
  merge_mapping: (p?: any) =>{
    const path =
      p?.SavedInfo?.Modification?.merge_mapping??
      p?.SavedInfo?.merge_mapping;
    return path
      ? path.replace(/^\.\//, '').replaceAll('/', '%2F')
      : 'compressed_merge_mapping.nii.gz';
  },


  merge_components: (p?: any) =>{
    const path =
      p?.SavedInfo?.Modification?.compressed_merge??
      p?.SavedInfo?.compressed_merge;
    return path
      ? path.replace(/^\.\//, '').replaceAll('/', '%2F')
      : 'compressed_merge_components.nii.gz';
  },


  wallThickness: (p?: any) =>
    (p?.SavedInfo?.LAWT_path
      ? p.SavedInfo.LAWT_path.replace(/^\.\//, '').replaceAll('/', '%2F')
      : 'la_wall_thickness.nii.gz'),

  // branches: (p?: any) => {
  //   const path =
  //     p?.SavedInfo?.Interpretation?.coronary_merge_path??
  //     p?.SavedInfo?.coronary_merge_path;

  //   return path
  //     ? path.replace(/^\.\//, '').replaceAll('/', '%2F')
  //     : 'compressed_merge_branches.nii.gz';
  // },
} as const;

export function downloadQueueByType({
  seriesId,
  accessToken,
  type,
  desc,
  tags,
  parameter,
}: {
  seriesId: string;
  accessToken: string;
  type:
    // | 'original'
    | 'segmentation'
    | 'branches'
    | 'wallthickness'
    | 'segmentationHalf'
    | 'wallthicknessHalf';
  desc: () => void;
  tags?: ImageTag;
  parameter?: any;
}) {
  function queuingBitmask() {
    //진입 방지
    const immKey = `${seriesId}.bitmask`;
    // immediateRawBufferPool[`bitmask`] = undefined;
    if (Object.keys(immediateRawBufferPool).some((key) => key === immKey))
      return 0;

    downloadQueue.enqueue(async () => {
      const { data, status } = await fetchSeriesArrayBuffer({
        accessToken,
        seriesId,
        target: initialDownloadQueueTargets.merge_mapping(parameter), //'compressed_merge_mapping.nii.gz',
      });
      if (status !== 200) return;
      immediateRawBufferPool[immKey] = undefined;
      runWorkerTask({ type: 'decompress', data }).then(
        ({ imageData }: { imageData: ArrayBuffer }) => {
          const bitMaskLookUp = new Uint16Array(imageData);
          // bitMaskSetMap[seriesId] = {};
          // arrangeBitMaskMap[seriesId] = {};
          const arrangeBitMask: Record<number, number> = {};
          const bitMaskSet: Record<string, Set<number>> = {};
          Object.keys(BitMaskMap).forEach(
            (key) => (bitMaskSet[key] = new Set()),
          );
          // Object.keys(BitMaskMap).forEach(key=>bitMaskSetMap[seriesId][key] = new Set())
          bitMaskLookUp.forEach((mask, index) => {
            if (mask === 0) return;
            const bitMasks = decomposeMask(mask);
            bitMasks.forEach((bitMask) => {
              const bitMaskKey = InvertBitMaskMap[bitMask];
              bitMaskSet[bitMaskKey].add(mask);
              // bitMaskSetMap[seriesId][bitMaskKey].add(mask);
            });
            arrangeBitMask[mask] = index;
          });
          arrangeBitMaskMap[seriesId] = arrangeBitMask;
          bitMaskSetMap[seriesId] = bitMaskSet;
          console.log('내가 젤 오래걸려용 아닝');
          desc();
        },
      );
    }, bufferQueuePriority);
    return 1;
  }

  switch (type) {
    // case 'original':
    //   //계속 때려도 돼.
    //   return queuingOriginal();
    case 'segmentation':
    case 'segmentationHalf':
      const segmentationId = `segmentation_${seriesId}_labelmap${type === 'segmentationHalf' ? 'Half' : ''}`;
      const poolKey = `${seriesId}.labelmap${type === 'segmentationHalf' ? 'Half' : ''}`;
      if (
        !segmentation.state.getSegmentation(segmentationId) &&
        !immediateRawBufferPool[poolKey]
      ) {
        downloadQueue.enqueue(async () => {
          console.log('꺄하하하학 받아놔야징', segmentationId);
          const { data, status } = await fetchSeriesArrayBuffer({
            accessToken,
            seriesId,
            target: initialDownloadQueueTargets.merge_components(parameter), //'compressed_merge_components.nii.gz',
          });
          if (status === 200) {
            immediateRawBufferPool[poolKey] = runWorkerTask({
              type: 'decompress',
              data,
            });
            if (type === 'segmentationHalf') {
              if (!tags) throw '??';
              const { imageData } = await immediateRawBufferPool[poolKey];
              const dim = [tags.width, tags.height, tags.depthInterpolated];
              immediateRawBufferPool[poolKey] = runWorkerTask({
                type: 'halfResolution',
                data: imageData,
                dim,
              });
            }
            console.log('내가 젤 오래걸려용');
          }
          desc();
          // scheduleRender('Capture',{type:'okok'})
        }, bufferQueuePriority);
        return 1 + queuingBitmask();
      } else {
        return 0;
      }
    // case 'branches':
    //   const branchesId = `${seriesId}.branches`;
    //   if (
    //     !cache.getVolume(branchesId) &&
    //     !immediateRawBufferPool[`${seriesId}.branches`]
    //   ) {
    //     downloadQueue.enqueue(async () => {
    //       console.log(
    //         '꺄하하하학 나도 따라 받아놔야징',
    //         'compressed_merge_branches',
    //       );

    //       // const path =
    //       //   parameter?.SavedInfo?.Interpretation?.coronary_merge_path
    //       //     ?.replace(/^\.\//, '') // 앞의 ./ 제거
    //       //     ?.replaceAll('/', '%2F') ?? 'compressed_merge_branches.nii.gz'; // / → %2F 변환

    //       const { data, status } = await fetchSeriesArrayBuffer({
    //         accessToken,
    //         seriesId,
    //         target: initialDownloadQueueTargets.branches(parameter),
    //       });

    //       if (status === 200) {
    //         immediateRawBufferPool[`${seriesId}.branches`] = runWorkerTask({
    //           type: 'decompress',
    //           data,
    //         });
    //       }
    //       desc();
    //     }, bufferQueuePriority);
    //     return 1;
    //   } else {
    //     return 0;
    //   }
    case 'wallthickness':
    case 'wallthicknessHalf':
      const lawtId = `${seriesId}.lawt`;
      if (!cache.getVolume(lawtId) && !immediateRawBufferPool[lawtId]) {
        downloadQueue.enqueue(async () => {
          const { data, status } = await fetchSeriesArrayBuffer({
            accessToken,
            seriesId,
            target: initialDownloadQueueTargets.wallThickness(parameter),//'la_wall_thickness.nii.gz',
          });
          if (status === 200) {
            immediateRawBufferPool[lawtId] = runWorkerTask({
              type: 'decompress',
              data,
            });
            // 이녀석은 half여도 원래 id에 꽂아보자
            // 위에 seg Half랑 거의 흡사
            if (type === 'wallthicknessHalf') {
              if (!tags) throw '??';
              const { imageData } = await immediateRawBufferPool[lawtId];
              const dim = [tags.width, tags.height, tags.depthInterpolated];
              immediateRawBufferPool[lawtId] = runWorkerTask({
                type: 'halfResolution',
                data: imageData,
                dim,
              });
            }
          }
          desc();
        }, bufferQueuePriority);
        return 1;
      } else {
        return 0;
      }
    default:
      throw '너 뭐하니?';
  }
  return 0;
}

const defaultCreateLocalLabelmapVolumeOptions = (
  bits: number,
  referencedVolume: ImageVolume,
) =>
  ({
    metadata: {
      ...referencedVolume.metadata,
      BitsAllocated: bits,
      BitsStored: bits,
      HighBit: bits - 1,
      PixelRepresentation: 0,
    },
    dimensions: referencedVolume.dimensions,
    spacing: referencedVolume.spacing,
    origin: referencedVolume.origin,
    direction: referencedVolume.direction,

    referencedImageIds: referencedVolume.imageIds,
    referencedVolumeId: referencedVolume.volumeId,
    targetBuffer: { type: `Uint${bits}Array` },
  }) as Types.LocalVolumeOptions;

export async function loadLAWT(
  seriesId: string,
  setter: () => void,
  half: boolean,
) {
  downloadQueue.enqueue(async () => {
    const poolKey = `${seriesId}.lawt`;
    const testLAWTVolume = cache.getVolume(poolKey);
    console.log(testLAWTVolume);
    if (!testLAWTVolume) {
      const originalVolume = getVolumeFromSeriesId(seriesId, half); //cache.getVolume(`ORIGINAL_${seriesId}`);
      if (!originalVolume) {
        throw '오리 없는데 시크니스 시도';
      }
      const result = await immediateRawBufferPool[poolKey];
      if (!result) {
        throw '시크니스 없는데 시도하는 상황';
      }
      const { imageData } = result as DownloadAndDecompressType;
      const scalarData = new Uint8Array(imageData);
      const volumeOptions = defaultCreateLocalLabelmapVolumeOptions(
        8,
        originalVolume,
      );
      const newMeta = {
        ...volumeOptions,
        metadata: {
          ...volumeOptions.metadata,
          BitsAllocated: 8,
          BitsStored: 8,
          HighBit: 7,
          PixelRepresentation: 1,
          SamplesPerPixel: 1,
        },
        scalarData,
        targetBuffer: { type: 'Uint8Array' },
      } as Types.LocalVolumeOptions;

      const volume = volumeLoader.createLocalVolume(poolKey, newMeta);
      volume.load();
      immediateRawBufferPool[poolKey] = undefined;
      console.log('완');
      setter();
    } else {
      console.log('완2');
      setter();
    }
  });
}

export function decomposeMask(labelValue: number): Uint32Array {
  const parts = new Uint32Array(32);
  let v = labelValue >>> 0;
  let count = 0;
  while (v !== 0) {
    const lowBit = v & -v;
    parts[count++] = lowBit;
    v ^= lowBit;
  }
  return parts.slice(0, count);
}
function blendColors({
  parts,
  segmentations,
}: {
  parts: Uint32Array;
  segmentations: Partial<Segmentation>;
}): [number, number, number, number] {
  const sum: [number, number, number, number] = [0, 0, 0, 0];
  let cnt = 0;
  parts.forEach((bit) => {
    const props =
      segmentations[InvertBitMaskMap[bit] as SegmentationControllerType];
    if (props && props.opacity != 0 && props.visibility) {
      sum[0] += props.color[0];
      sum[1] += props.color[1];
      sum[2] += props.color[2];
      sum[3] += Number(props.visibility) * props.opacity;
      cnt++;
    }
  });
  if (cnt === 0) return sum;
  return [sum[0] / cnt, sum[1] / cnt, sum[2] / cnt, (sum[3] * 255) / cnt];
}

function createColorLUT({
  arrangeBitMask,
  segmentations,
  representIndex = 0,
}: {
  arrangeBitMask: Record<number, number>;
  segmentations: Partial<Segmentation>;
  representIndex: number;
}) {
  let res: Types.ColorLUT = Array.from(
    { length: 255 },
    () => [0, 0, 0, 0] as Types.Color,
  );
  Object.entries(arrangeBitMask).forEach(
    ([originBit, arrangeBit]) =>
      (res[arrangeBit] = blendColors({
        parts: decomposeMask(Number(originBit) & representIndex),
        segmentations,
      }) as Types.Color),
  );
  return res;
}
export function getLabelmapSegmentationCachedStats(
  seriesId: string,
  half: boolean = false,
) {
  const segmentationId = half
    ? `segmentation_${seriesId}_labelmapHalf`
    : `segmentation_${seriesId}_labelmap`;
  const seg = segmentation.state.getSegmentation(segmentationId);
  if (!seg) throw '경우없어요';

  return seg.cachedStats;
  // const arrangeBitMask = seg.cachedStats['arrangeBitMask'] as Record<number,number>;
  // const prevRepresentIndex = seg.cachedStats['representIndex'] as number;
  // return {
  //   bitMaskSetMap,
  //   arrangeBitMaskMap
  // }
}

export function updateLabelmapSegment({
  // seriesId,
  segmentationId,
  segmentTarget,
  segmentations,
  isModification = false,
}: {
  // seriesId: string;
  segmentationId?: string;
  segmentTarget: string;
  segmentations: Partial<Segmentation>;
  isModification?: boolean;
}) {
  let seg: ToolTypes.Segmentation | undefined = undefined;
  // segmentation.state.getSegmentations().forEach(seg=>{
  //   if(segmentationIds.includes(seg.segmentationId)) return;
  //   segmentation.removeSegmentation(seg.segmentationId);
  // })
  if(getPlaqueDisplayType(segmentTarget)) return;
  const viewportIds = getEnabledElements().map((v) => v.viewportId);
  if (viewportIds.length === 0) return;

  //한놈만 있어도 됨
  const viewportId = viewportIds[0];

  // EF같은 새로운 관리방식으로 인해 seriesId로 처리가 불가능해짐.
  // 매번 representation을 탐지해야함.
  // viewportIds.forEach(viewportId=>{
  seg = segmentationId
    ? segmentation.state.getSegmentation(segmentationId)
    : segmentation.activeSegmentation.getActiveSegmentation(viewportId);
  // segmentation.state.getSegmentationRepresentations(viewportId)
  // .forEach(segRep=>{
  //   segmentation.state.removeLabelmapRepresentation(viewportId,segRep.segmentationId,true);
  // })
  // })
  // let segmentationId = `segmentation_${seriesId}_labelmap`;
  // let seg = segmentation.state.getSegmentation(segmentationId);

  if (!seg) {
    console.log('세그멘테이션이 존재하지 않습니다.');
    return;
  }

  const { segmentationId: targetSegmentationId } = seg;
  const arrangeBitMask = seg.cachedStats['arrangeBitMask'] as Record<
    number,
    number
  >;
  const colorLUTIndex = seg.cachedStats['colorLUTIndex'] as number;
  const representIndex = seg.cachedStats['representIndex'] as number;

  const segmentIndex = BitMaskMap[segmentTarget];
  if (segmentIndex === undefined) {
    throw new Error(`Unknown segmentTarget: ${segmentTarget}`);
  }
  const colorLUT = segmentation.state.getColorLUT(colorLUTIndex)!;
  const vpIds =
    segmentation.state.getViewportIdsWithSegmentation(targetSegmentationId);
  let needsVisibilityUpdate = false;
  const updatedSegmentVisibility = new Map<number, boolean>();

  for (const [originBitStr, arrangeBit] of Object.entries(arrangeBitMask)) {
    const originBit = Number(originBitStr);
    if ((originBit & segmentIndex) === 0) continue;

    const colorLUTelement = blendColors({
      parts: decomposeMask(originBit & representIndex),
      segmentations,
    }) as Types.Color;
    needsVisibilityUpdate ||=
      (colorLUTelement[3] & colorLUT[arrangeBit][3]) === 0;
    colorLUT[arrangeBit] = colorLUTelement;
    updatedSegmentVisibility.set(arrangeBit, colorLUTelement[3] !== 0);
    vpIds.forEach((viewportId) => {
      segmentation.config.color.setSegmentIndexColor(
        viewportId,
        targetSegmentationId,
        arrangeBit,
        colorLUTelement,
      );
    });
  }

  if (needsVisibilityUpdate) {
    const reps =
      segmentation.state.getSegmentationRepresentationsBySegmentationId(
        targetSegmentationId,
      );

    for (const { representations } of reps) {
      for (const rep of representations) {
        for (const [key, segInfo] of Object.entries(rep.segments)) {
          const idx = Number(key);
          const visible = updatedSegmentVisibility.get(idx);
          if (visible !== undefined) {
            segInfo.visible = visible;
          }
        }
      }
    }
  }
}

export function removeLabelmapViewport({
  viewportId,
  seriesId,
}: {
  viewportId: PanelComponentKeyType;
  seriesId?: string;
}) {
  const segmentationState = segmentation.state;
  const type = ToolEnums.SegmentationRepresentations.Labelmap;
  if (seriesId) {
    const segmentationId = `segmentation_${seriesId}_labelmap`;
    const segmentationIdHalf = `segmentation_${seriesId}_labelmapHalf`;
    if (
      segmentationState.getSegmentationRepresentation(viewportId, {
        segmentationId,
        type,
      })
    ) {
      segmentation.removeLabelmapRepresentation(
        viewportId,
        segmentationId,
        true,
      );
      console.log(viewportId, '지웠어요', segmentationId);
    }
    if (
      segmentationState.getSegmentationRepresentation(viewportId, {
        segmentationId: segmentationIdHalf,
        type,
      })
    ) {
      segmentation.removeLabelmapRepresentation(
        viewportId,
        segmentationIdHalf,
        true,
      );
      console.log(viewportId, '지웠어요', segmentationIdHalf);
    }
  } else {
    segmentationState
      .getSegmentationRepresentations(viewportId, { type })
      .forEach(({ segmentationId }) => {
        segmentation.removeLabelmapRepresentation(
          viewportId,
          segmentationId,
          true,
        );
        console.log(viewportId, '지웠어요', segmentationId);
      });
  }
}

const defaultLabelmapStyle: ToolTypes.LabelmapStyle = {
  renderFill: true,
  renderOutline: true,
  fillAlpha: 0.9,
  renderFillInactive: true,
  fillAlphaInactive: 0.8,
  outlineWidth: 0.5,
};

const defaultOutlineStyle: ToolTypes.LabelmapStyle = {
  renderFill: false,
  renderOutline: true,
  fillAlpha: 0,
  fillAlphaInactive: 0,
  renderFillInactive: false,
  outlineWidth: 1,
};

export function getOrCreateManualVolume(seriesId: string, lowQuality: boolean) {
  const labelmapVolumeId =lowQuality? `${seriesId}_labelmapHalf`: `${seriesId}_labelmap`;
  const manualVolumeId = `${seriesId}_manual`;
  let manualVolume = cache.getVolume(manualVolumeId);
  if (!manualVolume) {
    const labelmapVolume = cache.getVolume(labelmapVolumeId);
    if (!labelmapVolume) throw '라벨맵이 없는데 어떻게 오셨죠?';

    manualVolume = volumeLoader.createAndCacheDerivedLabelmapVolume(
      labelmapVolumeId,
      {
        volumeId: manualVolumeId,
        voxelRepresentation: Enums.VoxelManagerEnum.Volume,
      },
    );
  }
  return manualVolume;
}

export function hasManualSegmentationSourceVolume(
  seriesId: string | undefined,
  lowQuality: boolean,
) {
  if (!seriesId) return false;
  const editMode = configStore.get().EditMode;
  const baseSegmentationId = `segmentation_${seriesId}`;
  const sourceVolumeId =
    editMode === EditNames.regulator
      ? `regulator_${seriesId}`
      : lowQuality
        ? `${seriesId}_labelmapHalf`
        : `${seriesId}_labelmap`;
  const sourceSegmentationId =
    editMode === EditNames.regulator
      ? `${baseSegmentationId}_regulator`
      : lowQuality
        ? `${baseSegmentationId}_labelmapHalf`
        : `${baseSegmentationId}_labelmap`;

  if (cache.getVolume(`${seriesId}_manual`)) return true;
  if (!cache.getVolume(sourceVolumeId)) return false;

  return (
    segmentation.state.getSegmentationRepresentationsBySegmentationId(
      sourceSegmentationId,
    ).length > 0
  );
}

// export function getLabelmapVolume(seriesId: string) {
//   const labelmapVolumeId = `${seriesId}_labelmap`;
//   const labelmapVolume = cache.getVolume(labelmapVolumeId);
//   if (!labelmapVolume) throw '없다?';
//   return labelmapVolume;
// }

export function getViewportAxes(viewportId: PanelComponentKeyType) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId);
  if (!vp) return;

  const { viewPlaneNormal, viewUp } = vp?.getCamera();
  if (!viewPlaneNormal || !viewUp) return;
  const viewRight = getOrthoNormal(viewUp, viewPlaneNormal);

  return { viewPlaneNormal, viewUp, viewRight };
}

export function getCanvasToWorld(
  viewportId: PanelComponentKeyType,
  canvasPoint: [number, number],
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId);
  if (!vp) return;

  return vp.canvasToWorld(canvasPoint);
}

export function renderEnabledElements() {
  getEnabledElements().forEach(({ viewport }) => viewport.render());
}

function endStrokeAndCaptureDelta(): UndoDelta | null {
  let capturedDelta: UndoDelta | null = null;
  const unsubscribe = subscribeSegmentHistoryChange(({ delta, invert }) => {
    if (!invert && !capturedDelta && delta.flippedIndices.length > 0) {
      capturedDelta = delta;
    }
  });

  try {
    endStroke();
  } finally {
    unsubscribe();
  }

  return capturedDelta;
}

export function isHitCornerstoneAnnotation(
  element: HTMLDivElement,
  clientPoint: [number, number],
  worldPoint: [number, number, number],
) {
  const enableElement = getEnabledElement(element);
  if (!enableElement) return false;

  // line
  const anno = toolsUtils.getAnnotationNearPointOnEnabledElement(
    enableElement,
    clientPoint,
    6,
  );
  if (anno) return true;

  // check crosshairs annotation
  const toolGroupId = 'sharedToolGroup';
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  const crossHairsTool = toolGroup!.getToolInstance(
    CustomCrosshairsTool.toolName,
  ) as CustomCrosshairsTool;
  if (!crossHairsTool) return false;

  const viewport = enableElement.viewport;
  if (!viewport) return false;

  const imageData = viewport.getImageData();
  if (!imageData) return false;

  const spacing = imageData.spacing;

  // rotation / thickness point
  return annotation.state.getAllAnnotations().some((anno) => {
    const { handles } = anno.data;
    let res: boolean = false;
    if (handles) {
      for (const key in handles) {
        if (key === 'rotationPoints' || key === 'slabThicknessPoints') {
          res =
            (handles[key] as []).some((arr) => {
              return distance(arr[0], worldPoint) < spacing[0];
            }) || res;
        }
      }
    }
    return res;
  });
}

export function setMousePointer(pointerName: any = '') {
  const toolGroupId = 'sharedToolGroup';
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    console.warn('툴그룹 없음:');
    return;
  }
  toolGroup.setViewportsCursorByToolName(pointerName);
}

type ManualModifierInputSnapshot = {
  clientXY: [number, number] | null;
  shiftDown: boolean;
};

const manualModifierInputSnapshot: ManualModifierInputSnapshot = {
  clientXY: null,
  shiftDown: false,
};

let manualModifierInputSnapshotListenersInstalled = false;

function recordManualModifierPointerSnapshot(e: MouseEvent | WheelEvent) {
  manualModifierInputSnapshot.clientXY = [e.clientX, e.clientY];
}

function ensureManualModifierInputSnapshotListeners() {
  if (manualModifierInputSnapshotListenersInstalled) return;
  if (typeof window === 'undefined') return;

  manualModifierInputSnapshotListenersInstalled = true;
  window.addEventListener('mousemove', recordManualModifierPointerSnapshot, true);
  window.addEventListener('mousedown', recordManualModifierPointerSnapshot, true);
  window.addEventListener('wheel', recordManualModifierPointerSnapshot, true);
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Shift') {
        manualModifierInputSnapshot.shiftDown = true;
      }
    },
    true,
  );
  window.addEventListener(
    'keyup',
    (e) => {
      if (e.key === 'Shift') {
        manualModifierInputSnapshot.shiftDown = false;
      }
    },
    true,
  );
  window.addEventListener('blur', () => {
    manualModifierInputSnapshot.clientXY = null;
    manualModifierInputSnapshot.shiftDown = false;
  });
}

ensureManualModifierInputSnapshotListeners();

function updateBrushOverlayPosition(params: {
  element: HTMLDivElement;
  svg?: Element | null;
  clientX: number;
  clientY: number;
}) {
  const { element, svg, clientX, clientY } = params;
  const brushGroup = svg?.querySelector('g#brush');
  if (!brushGroup) return;

  const rect = element.getBoundingClientRect();
  brushGroup.setAttribute(
    'transform',
    `translate(${clientX - rect.left},${clientY - rect.top})`,
  );
}

export function updateModifierOverlay(params: {
  element: HTMLDivElement;
  pixelsPerMM?: number;
  radiusMM?: number;
  brushStep?: number;
  brushUnit?: number;
  brushStepDelta?: number;
  range?:[number,number];
  colorHex?: string;
  modifierType?: ModifierType;
}) {
  const {
    element,
    radiusMM,
    pixelsPerMM,
    brushStep,
    brushUnit,
    brushStepDelta,
    range,
    colorHex,
    modifierType,
  } = params;
  // getEnabledElements().forEach(({viewport})=>{
  const svg = element.parentElement?.querySelector('svg#custom-overlay');
  if (!svg) return;
  const brushCircle = svg.querySelector('circle#brush');
  if (!brushCircle) return;

  let currentPixelsPerMM = pixelsPerMM;
  let currentRadiusMM = radiusMM;
  let currentBrushUnit = brushUnit;
  let currentBrushStep = brushStep;
  let currentRange = range;
  if (!currentPixelsPerMM) {
    currentPixelsPerMM = Number(brushCircle.getAttribute('pixelsPerMM'));
  }
  if (!currentBrushUnit) {
    currentBrushUnit = Number(brushCircle.getAttribute('brushUnit'));
  }
  if (!currentBrushStep) {
    currentBrushStep = Number(brushCircle.getAttribute('brushStep'));
  }
  if (
    Number.isFinite(brushStepDelta) &&
    Number.isFinite(currentBrushUnit) &&
    Number.isFinite(currentBrushStep)
  ) {
    currentBrushStep = Math.max(
      1,
      Math.min(10, Math.round(currentBrushStep! + brushStepDelta!)),
    );
    currentRadiusMM = currentBrushStep * currentBrushUnit!;
  }
  if (!currentRadiusMM) {
    currentRadiusMM = Number(brushCircle.getAttribute('radiusMM'));
  }
  if(!currentRange){
    currentRange = brushCircle.getAttribute('range')?.split(',').map(Number) as [number,number];
  }
  brushCircle.setAttribute('pixelsPerMM', String(currentPixelsPerMM));
  brushCircle.setAttribute('radiusMM', String(currentRadiusMM));
  if (Number.isFinite(currentBrushStep)) {
    brushCircle.setAttribute('brushStep', String(currentBrushStep));
  }
  if (Number.isFinite(currentBrushUnit)) {
    brushCircle.setAttribute('brushUnit', String(currentBrushUnit));
  }
  brushCircle.setAttribute('range', String(currentRange));
  brushCircle.setAttribute(
    'r',
    (currentRadiusMM * currentPixelsPerMM).toFixed(2),
  );
  if (colorHex) brushCircle.setAttribute('fill', colorHex);

  // 이떄는 브러쉬가 안보여도 될듯?
  if (modifierType !== undefined) {
    const visibility =
      modifierType === ModifierType.PREVIEW ? 'collapse' : 'visible';
    brushCircle.setAttribute('visibility', visibility);

    if (modifierType === ModifierType.ERASER) {
      brushCircle.setAttribute('stroke-opacity', configStore.get().EditMode !== null ? '1' : '0.2');
      brushCircle.setAttribute('fill-opacity', '0.0');
    } else {
      brushCircle.setAttribute(
        'stroke-opacity',
        configStore.get().EditMode !== null ? '1' : '0.0',
      );
      brushCircle.setAttribute('fill-opacity', '0.2');
    }
  }
}

export function setModifierOverlay(params: {
  radiusMM: number;
  brushStep?: number;
  brushUnit?: number;
  range:[number,number];
  modifierType: ModifierType;
}) {
  const { radiusMM, brushStep, brushUnit, range, modifierType } = params;

  return getEnabledElements().map((prop) => {
    const { viewport } = prop;
    const viewportId = prop.viewportId as PanelComponentKeyType;

    const { element } = viewport;
    const overlayRoot = viewport.element.parentElement;
    const svg =
      overlayRoot?.querySelector('svg#custom-overlay');

    if (!svg) return;

    const pixelsPerMM = requestPixelsPerMM({ viewport, viewportId });

    let brushGroup = svg.querySelector('g#brush');
    if (!brushGroup) {
      brushGroup = makeBrushGroupSvg({
        radiusMM,
        pixelsPerMM,
        brushStep,
        brushUnit,
        range,
        modifierType,
        ...(configStore.get().EditMode !== null && {
          stroke: '#2f80ff',
          strokeOpacity: 1,
        }),
      });
      svg.innerHTML = '';
    }

     // append mouse move
    let pending = false;
    function onMouseMoveSVG(e: MouseEvent) {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        updateBrushOverlayPosition({
          element,
          svg,
          clientX: e.clientX,
          clientY: e.clientY,
        });
        pending = false;
      });
    }

    svg.appendChild(brushGroup);
    element.addEventListener('mousemove', onMouseMoveSVG);

    return () => {
      svg.removeChild(brushGroup);
      element.removeEventListener('mousemove', onMouseMoveSVG);
    };
  });
}

type FloodFillSeedCanvas = { x: number; y: number };

function getFloodFillSeedCanvas(
  clientXY: [number, number],
  viewportId: PanelComponentKeyType,
): FloodFillSeedCanvas | null {
  const vp = getViewport(viewportId) as Types.IVolumeViewport;
  const cs = (vp as any)?.canvas as HTMLCanvasElement | undefined;
  if (!cs) return null;

  const rect = cs.getBoundingClientRect();
  return {
    x: Math.round((clientXY[0] - rect.left) * (cs.width / rect.width)),
    y: Math.round((clientXY[1] - rect.top) * (cs.height / rect.height)),
  };
}

function getShiftFloodPreviewSeedKey(
  viewportId: PanelComponentKeyType,
  seedCanvas: FloodFillSeedCanvas,
  range: [number, number],
  sliceKey: string,
) {
  return `${viewportId}:${sliceKey}:${seedCanvas.x}:${seedCanvas.y}:${range[0]}:${range[1]}`;
}

function getShiftFloodPreviewSliceKey(
  viewportId: PanelComponentKeyType,
  manualVolume: any,
) {
  const vp = getViewport(viewportId) as Types.IVolumeViewport;
  const sliceIndex = (vp as any)?.getSliceIndex?.();
  if (Number.isFinite(sliceIndex)) return `slice:${Math.round(sliceIndex)}`;

  const sliceAxis = getConnect2DViewportSliceAxis(viewportId);
  const focalPoint = vp?.getCamera?.()?.focalPoint;
  if (!sliceAxis || !manualVolume?.imageData || !focalPoint) {
    return 'slice:unknown';
  }

  const focalIndex = worldToIndex(manualVolume.imageData, focalPoint);
  return `${sliceAxis.axis}:${Math.round(focalIndex[sliceAxis.index])}`;
}

// 하단 enableShiftLiveFlood 내부함수 내용
function computeAndDrawEx(
  clientXY: [number, number],
  viewportId: PanelComponentKeyType,
  range: [number, number],
  seedCanvasOverride?: FloodFillSeedCanvas,
) {
  // const info = getVolumeWorldPointInfo(viewportId, e.clientX, e.clientY);
  // if (!info?.position || typeof info.value !== 'number') return;

  const vp = getViewport(viewportId) as Types.IVolumeViewport;
  const imageData = vp.getImageData();
  if (!imageData) return;

  const scalar = getHuData(viewportId);
  if (!scalar) return;

  // const seedHU = info.value!;
  // const lo = seedHU - 200;
  // const hi = seedHU + 200;

  const lo = range[0];
  const hi = range[1];

  const seedCanvas =
    seedCanvasOverride ?? getFloodFillSeedCanvas(clientXY, viewportId);
  if (!seedCanvas) return;

  // console.time('floodfill2D');
  const mul = floodFill2D_Multi(vp, scalar, seedCanvas, lo, hi);
  // console.timeEnd('floodfill2D');
  // console.log('floodfill2D length', mul.length);
  return mul;
}

type Connect2DSliceAxis = 'x' | 'y' | 'z';

type Connect2DRegion = {
  seriesId: string;
  lowQuality: boolean;
  viewportId: PanelComponentKeyType;
  sliceAxis: Connect2DSliceAxis;
  sliceAxisIndex: 0 | 1 | 2;
  sliceIndex: number;
  indices: Uint32Array;
  canvas: [number, number];
};

export type Connect2DRegionSummary = {
  viewportId: PanelComponentKeyType;
  sliceAxis: Connect2DSliceAxis;
  sliceIndex: number;
  voxelCount: number;
};

export type Connect2DSelectionState = {
  start: Connect2DRegionSummary | null;
  end: Connect2DRegionSummary | null;
  ready: boolean;
  message?: string;
};

type Connect2DActiveStroke = {
  seriesId: string;
  lowQuality: boolean;
  viewportId: PanelComponentKeyType;
  sliceAxis: Connect2DSliceAxis;
  sliceAxisIndex: 0 | 1 | 2;
  sliceIndex: number;
  indices: Set<number>;
  canvas: [number, number];
  pointsWorld: Types.Point3[];
  radiusMM: number;
  lastWorld?: Types.Point3;
};

const connect2DSelection: {
  start: Connect2DRegion | null;
  end: Connect2DRegion | null;
} = {
  start: null,
  end: null,
};

const connect2DListeners = new Set<
  (state: Connect2DSelectionState) => void
>();
let connect2DMessage = '';
let connect2DApplying = false;

function getConnect2DRegionSummary(
  region: Connect2DRegion | null,
): Connect2DRegionSummary | null {
  if (!region) return null;

  return {
    viewportId: region.viewportId,
    sliceAxis: region.sliceAxis,
    sliceIndex: region.sliceIndex,
    voxelCount: region.indices.length,
  };
}

function getConnect2DSelectionState(): Connect2DSelectionState {
  return {
    start: getConnect2DRegionSummary(connect2DSelection.start),
    end: getConnect2DRegionSummary(connect2DSelection.end),
    ready: !!connect2DSelection.start && !!connect2DSelection.end,
    message: connect2DMessage,
  };
}

function notifyConnect2DSelectionChanged() {
  const state = getConnect2DSelectionState();
  connect2DListeners.forEach((listener) => listener(state));
}

export function subscribeConnect2DSelection(
  listener: (state: Connect2DSelectionState) => void,
) {
  connect2DListeners.add(listener);
  listener(getConnect2DSelectionState());

  return () => {
    connect2DListeners.delete(listener);
  };
}

function getConnect2DViewportSliceAxis(
  viewportId: PanelComponentKeyType,
): { axis: Connect2DSliceAxis; index: 0 | 1 | 2 } | null {
  switch (viewportId) {
    case 'Sagittal':
      return { axis: 'x', index: 0 };
    case 'Coronal':
      return { axis: 'y', index: 1 };
    case 'Axial':
      return { axis: 'z', index: 2 };
    default:
      return null;
  }
}

function linearIndexToPoint3D(
  index: number,
  width: number,
  height: number,
): Point3D {
  const sliceSize = width * height;
  const z = Math.floor(index / sliceSize);
  const remain = index - z * sliceSize;
  const y = Math.floor(remain / width);
  const x = remain - y * width;

  return { x, y, z };
}

function point3DToLinearIndex(
  point: Point3D,
  width: number,
  height: number,
) {
  return (point.z * height + point.y) * width + point.x;
}

function setConnect2DPreviewBit(
  manualVolume: any,
  index: number,
  enabled: boolean,
) {
  const [width, height] = manualVolume.dimensions as [number, number, number];
  const { x, y, z } = linearIndexToPoint3D(index, width, height);
  const value = Number(manualVolume.voxelManager?.getAtIJK(x, y, z) ?? 0);
  manualVolume.voxelManager?.setAtIJKPoint(
    [x, y, z],
    enabled ? value | 0b10 : value & 0b1,
  );
}

function clearConnect2DRegionPreview(region: Connect2DRegion | null) {
  if (!region) return;

  const manualVolume = getOrCreateManualVolume(
    region.seriesId,
    region.lowQuality,
  );
  if (!manualVolume) return;

  region.indices.forEach((index) => {
    setConnect2DPreviewBit(manualVolume, index, false);
  });
  manualVolume.modified();
}

function clearConnect2DSelectionInternal(emit = true) {
  const hasSelection = !!connect2DSelection.start || !!connect2DSelection.end;
  const hasMessage = connect2DMessage !== '';

  if (!hasSelection) {
    if (hasMessage) {
      connect2DMessage = '';
      if (emit) notifyConnect2DSelectionChanged();
    }
    return;
  }

  clearConnect2DRegionPreview(connect2DSelection.start);
  clearConnect2DRegionPreview(connect2DSelection.end);
  connect2DSelection.start = null;
  connect2DSelection.end = null;
  connect2DMessage = '';
  renderEnabledElements();

  if (emit) notifyConnect2DSelectionChanged();
}

export function clearConnect2DSelection() {
  clearConnect2DSelectionInternal(true);
}

export function popConnect2DSelection() {
  if (connect2DSelection.end) {
    clearConnect2DRegionPreview(connect2DSelection.end);
    connect2DSelection.end = null;
    connect2DMessage = '';

    renderEnabledElements();
    notifyConnect2DSelectionChanged();
    return;
  }

  if (connect2DSelection.start) {
    clearConnect2DRegionPreview(connect2DSelection.start);
    connect2DSelection.start = null;
    connect2DMessage = '';

    renderEnabledElements();
    notifyConnect2DSelectionChanged();
  }
}


function createBrushConnect2DStroke(params: {
  seriesId: string;
  lowQuality: boolean;
  viewportId: PanelComponentKeyType;
  manualVolume: any;
  centerWorld: Types.Point3;
  canvas: [number, number];
  radiusMM: number;
  indices?: Iterable<number> | null;
}): Connect2DActiveStroke | null {
  const {
    seriesId,
    lowQuality,
    viewportId,
    manualVolume,
    centerWorld,
    canvas,
    radiusMM,
    indices,
  } = params;
  const sliceAxis = getConnect2DViewportSliceAxis(viewportId);

  if (!sliceAxis || !manualVolume?.imageData || !manualVolume?.dimensions) {
    return null;
  }

  const centerIndex = worldToIndex(manualVolume.imageData, centerWorld);
  const [width, height, depth] = manualVolume.dimensions as [
    number,
    number,
    number,
  ];
  const maxByAxis = [width - 1, height - 1, depth - 1];
  const sliceIndex = Math.round(
    Math.max(
      0,
      Math.min(maxByAxis[sliceAxis.index], centerIndex[sliceAxis.index]),
    ),
  );

  return {
    seriesId,
    lowQuality,
    viewportId,
    sliceAxis: sliceAxis.axis,
    sliceAxisIndex: sliceAxis.index,
    sliceIndex,
    indices: new Set(indices ?? []),
    canvas,
    pointsWorld: [],
    radiusMM,
    lastWorld: centerWorld,
  };
}

function appendConnect2DStrokeLinearIndices(params: {
  manualVolume: any;
  stroke: Connect2DActiveStroke | null;
  indices: Iterable<number> | null | undefined;
  previewBit?: boolean;
}) {
  const { manualVolume, stroke, indices, previewBit = true } = params;
  if (!stroke || !indices) return 0;

  let addedCount = 0;
  for (const index of indices) {
    const numericIndex = Number(index);
    if (!Number.isInteger(numericIndex) || numericIndex < 0) continue;
    if (stroke.indices.has(numericIndex)) continue;

    stroke.indices.add(numericIndex);
    addedCount += 1;
    if (previewBit) {
      setConnect2DPreviewBit(manualVolume, numericIndex, true);
    }
  }

  return addedCount;
}

function stampConnect2DBrush(params: {
  manualVolume: any;
  imageData: any;
  stroke: Connect2DActiveStroke;
  centerWorld: Types.Point3;
  radiusMM: number;
}) {
  const { manualVolume, imageData, stroke, centerWorld, radiusMM } = params;
  const [width, height, depth] = manualVolume.dimensions as [
    number,
    number,
    number,
  ];
  const dimensions = [width, height, depth];
  const spacing = getImageDataVector(imageData, 'spacing', [1, 1, 1]) as
    | number[]
    | Float32Array
    | Float64Array;
  const center = worldToIndex(imageData, centerWorld);
  const fixedAxis = stroke.sliceAxisIndex;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== fixedAxis,
  );
  const uAxis = inPlaneAxes[0];
  const vAxis = inPlaneAxes[1];
  const safeRadiusMM = Math.max(0, Number(radiusMM) || 0);
  const uRadius = Math.max(0, Math.ceil(safeRadiusMM / Number(spacing[uAxis])));
  const vRadius = Math.max(0, Math.ceil(safeRadiusMM / Number(spacing[vAxis])));
  const uCenter = center[uAxis];
  const vCenter = center[vAxis];
  const fixedValue = stroke.sliceIndex;
  const radiusSq = safeRadiusMM * safeRadiusMM;

  for (let v = Math.floor(vCenter - vRadius); v <= Math.ceil(vCenter + vRadius); v += 1) {
    if (v < 0 || v >= dimensions[vAxis]) continue;

    for (let u = Math.floor(uCenter - uRadius); u <= Math.ceil(uCenter + uRadius); u += 1) {
      if (u < 0 || u >= dimensions[uAxis]) continue;

      const du = (u - uCenter) * Number(spacing[uAxis]);
      const dv = (v - vCenter) * Number(spacing[vAxis]);
      if (safeRadiusMM > 0 && du * du + dv * dv > radiusSq + Number.EPSILON) {
        continue;
      }

      const point = [0, 0, 0];
      point[fixedAxis] = fixedValue;
      point[uAxis] = u;
      point[vAxis] = v;

      const x = point[0];
      const y = point[1];
      const z = point[2];
      if (x < 0 || x >= width || y < 0 || y >= height || z < 0 || z >= depth) {
        continue;
      }

      const linearIndex = (z * height + y) * width + x;
      if (stroke.indices.has(linearIndex)) continue;

      stroke.indices.add(linearIndex);
      setConnect2DPreviewBit(manualVolume, linearIndex, true);
    }
  }

  manualVolume.modified();
}




function dot3(a: Types.Point3 | number[], b: Types.Point3 | number[]) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function sub3(a: Types.Point3 | number[], b: Types.Point3 | number[]): Types.Point3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]] as Types.Point3;
}

function getConnect2DStrokePointDistanceOnPlane(
  stroke: Connect2DActiveStroke,
  a: Types.Point3,
  b: Types.Point3,
) {
  const axes = getViewportAxes(stroke.viewportId);
  const d = sub3(b, a);
  if (!axes) return Math.hypot(d[0], d[1], d[2]);

  return Math.hypot(dot3(axes.viewRight, d), dot3(axes.viewUp, d));
}

function appendConnect2DStrokePoint(
  stroke: Connect2DActiveStroke,
  pointWorld: Types.Point3,
  radiusMM: number,
) {
  const last = stroke.pointsWorld[stroke.pointsWorld.length - 1];
  const minStep = Math.max(radiusMM * 0.15, 0.1);

  if (
    !last ||
    getConnect2DStrokePointDistanceOnPlane(stroke, last, pointWorld) >= minStep
  ) {
    stroke.pointsWorld.push(pointWorld);
  } else {
    stroke.pointsWorld[stroke.pointsWorld.length - 1] = pointWorld;
  }
}

function getConnect2DInterpolatedCenters(
  lastWorld: Types.Point3 | undefined,
  pointWorld: Types.Point3,
  radiusMM: number,
): Types.Point3[] {
  if (!lastWorld) return [pointWorld];

  const dx = pointWorld[0] - lastWorld[0];
  const dy = pointWorld[1] - lastWorld[1];
  const dz = pointWorld[2] - lastWorld[2];
  const len = Math.hypot(dx, dy, dz);
  const stepMM = Math.max(1, radiusMM * 0.5);
  const nSteps = Math.max(1, Math.floor(len / stepMM));
  const centers: Types.Point3[] = [];

  for (let t = 0; t <= nSteps; t += 1) {
    const ratio = t / nSteps;
    centers.push([
      lastWorld[0] + dx * ratio,
      lastWorld[1] + dy * ratio,
      lastWorld[2] + dz * ratio,
    ] as Types.Point3);
  }

  return centers;
}

function getConnect2DStrokeRadiusMM(
  manualVolume: any,
  stroke: Connect2DActiveStroke,
) {
  const spacing = getImageDataVector(manualVolume.imageData, 'spacing', [1, 1, 1]) as
    | number[]
    | Float32Array
    | Float64Array;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== stroke.sliceAxisIndex,
  );
  const minSpacing = Math.min(
    Number(spacing[inPlaneAxes[0]]) || 1,
    Number(spacing[inPlaneAxes[1]]) || 1,
  );
  const fallbackRadius = Math.max(minSpacing, 0.25);

  return Math.max(Number(stroke.radiusMM) || fallbackRadius, fallbackRadius);
}

function fillConnect2DClosedStrokeInterior(params: {
  manualVolume: any;
  stroke: Connect2DActiveStroke;
}) {
  const { manualVolume, stroke } = params;
  if (!manualVolume?.imageData || stroke.pointsWorld.length < 4) return;

  const [width, height, depth] = manualVolume.dimensions as [
    number,
    number,
    number,
  ];
  const dimensions = [width, height, depth];
  const spacing = getImageDataVector(manualVolume.imageData, 'spacing', [1, 1, 1]) as
    | number[]
    | Float32Array
    | Float64Array;
  const fixedAxis = stroke.sliceAxisIndex;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== fixedAxis,
  );
  const uAxis = inPlaneAxes[0];
  const vAxis = inPlaneAxes[1];
  const uSpacing = Number(spacing[uAxis]) || 1;
  const vSpacing = Number(spacing[vAxis]) || 1;
  const minSpacing = Math.min(uSpacing, vSpacing);

  const polygonPoints: Array<{ u: number; v: number }> = [];
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;

  stroke.pointsWorld.forEach((pointWorld) => {
    const ijk = worldToIndex(manualVolume.imageData, pointWorld);
    const u = Number(ijk[uAxis]) * uSpacing;
    const v = Number(ijk[vAxis]) * vSpacing;
    polygonPoints.push({ u, v });
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  });

  if (
    polygonPoints.length < 4 ||
    !Number.isFinite(minU) ||
    !Number.isFinite(maxU) ||
    !Number.isFinite(minV) ||
    !Number.isFinite(maxV)
  ) {
    return;
  }

  let area = 0;
  for (let n = 0, prev = polygonPoints.length - 1; n < polygonPoints.length; prev = n, n += 1) {
    const a = polygonPoints[prev];
    const b = polygonPoints[n];
    area += a.u * b.v - b.u * a.v;
  }
  if (Math.abs(area) < minSpacing * minSpacing * 2) return;

  const radiusMM = getConnect2DStrokeRadiusMM(manualVolume, stroke);
  const gridStep = Math.max(minSpacing * 0.5, 0.25);
  const boundaryRadius = Math.max(radiusMM, gridStep);
  const margin = boundaryRadius + gridStep * 3;
  const u0 = Math.floor((minU - margin) / gridStep) * gridStep;
  const v0 = Math.floor((minV - margin) / gridStep) * gridStep;
  const gridWidth = Math.ceil((maxU + margin - u0) / gridStep) + 1;
  const gridHeight = Math.ceil((maxV + margin - v0) / gridStep) + 1;
  const gridSize = gridWidth * gridHeight;
  if (gridWidth <= 2 || gridHeight <= 2 || gridSize > 16_000_000) return;

  const cells = new Uint8Array(gridSize);
  const radiusCells = Math.ceil(boundaryRadius / gridStep);
  const boundaryOffsets: number[] = [];
  const boundaryRadius2 = boundaryRadius * boundaryRadius;

  for (let y = -radiusCells; y <= radiusCells; y += 1) {
    for (let x = -radiusCells; x <= radiusCells; x += 1) {
      const du = x * gridStep;
      const dv = y * gridStep;
      if (du * du + dv * dv <= boundaryRadius2) {
        boundaryOffsets.push(x, y);
      }
    }
  }

  const markBoundary = (u: number, v: number) => {
    const cx = Math.round((u - u0) / gridStep);
    const cy = Math.round((v - v0) / gridStep);
    for (let n = 0; n < boundaryOffsets.length; n += 2) {
      const x = cx + boundaryOffsets[n];
      const y = cy + boundaryOffsets[n + 1];
      if (x < 0 || y < 0 || x >= gridWidth || y >= gridHeight) continue;
      cells[y * gridWidth + x] = 1;
    }
  };

  for (let i = 0; i < polygonPoints.length - 1; i += 1) {
    const a = polygonPoints[i];
    const b = polygonPoints[i + 1];
    const du = b.u - a.u;
    const dv = b.v - a.v;
    const steps = Math.max(1, Math.ceil(Math.hypot(du, dv) / (gridStep * 0.5)));
    for (let t = 0; t <= steps; t += 1) {
      const ratio = t / steps;
      markBoundary(a.u + du * ratio, a.v + dv * ratio);
    }
  }

  const queue = new Int32Array(gridSize);
  let head = 0;
  let tail = 0;
  const pushOutside = (x: number, y: number) => {
    const idx = y * gridWidth + x;
    if (cells[idx] !== 0) return;
    cells[idx] = 2;
    queue[tail] = idx;
    tail += 1;
  };

  for (let x = 0; x < gridWidth; x += 1) {
    pushOutside(x, 0);
    pushOutside(x, gridHeight - 1);
  }
  for (let y = 1; y < gridHeight - 1; y += 1) {
    pushOutside(0, y);
    pushOutside(gridWidth - 1, y);
  }

  while (head < tail) {
    const idx = queue[head];
    head += 1;
    const x = idx % gridWidth;
    const y = Math.floor(idx / gridWidth);
    if (x > 0) pushOutside(x - 1, y);
    if (x < gridWidth - 1) pushOutside(x + 1, y);
    if (y > 0) pushOutside(x, y - 1);
    if (y < gridHeight - 1) pushOutside(x, y + 1);
  }

  const fillCells = new Uint8Array(gridSize);
  let hasInterior = false;
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i] !== 0) continue;
    hasInterior = true;
    fillCells[i] = 1;
  }
  if (!hasInterior) return;

  const buildBoundaryDistance = (sourceCell: number) => {
    const dist = new Int16Array(gridSize);
    dist.fill(-1);
    let qHead = 0;
    let qTail = 0;

    const seedBoundary = (idx: number, distance = 1) => {
      if (cells[idx] !== 1 || dist[idx] >= 0) return;
      dist[idx] = distance;
      queue[qTail] = idx;
      qTail += 1;
    };

    for (let y = 0; y < gridHeight; y += 1) {
      for (let x = 0; x < gridWidth; x += 1) {
        const idx = y * gridWidth + x;
        if (cells[idx] !== sourceCell) continue;
        if (x > 0) seedBoundary(idx - 1);
        if (x < gridWidth - 1) seedBoundary(idx + 1);
        if (y > 0) seedBoundary(idx - gridWidth);
        if (y < gridHeight - 1) seedBoundary(idx + gridWidth);
      }
    }

    while (qHead < qTail) {
      const idx = queue[qHead];
      qHead += 1;
      const distance = dist[idx] + 1;
      const x = idx % gridWidth;
      const y = Math.floor(idx / gridWidth);

      if (x > 0) seedBoundary(idx - 1, distance);
      if (x < gridWidth - 1) seedBoundary(idx + 1, distance);
      if (y > 0) seedBoundary(idx - gridWidth, distance);
      if (y < gridHeight - 1) seedBoundary(idx + gridWidth, distance);
    }

    return dist;
  };

  const interiorDistance = buildBoundaryDistance(0);
  const outsideDistance = buildBoundaryDistance(2);
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i] !== 1 || interiorDistance[i] < 0) continue;
    if (outsideDistance[i] < 0 || interiorDistance[i] <= outsideDistance[i] + 1) {
      fillCells[i] = 1;
    }
  }

  const minVoxelU = Math.max(0, Math.floor((minU - margin) / uSpacing) - 1);
  const maxVoxelU = Math.min(
    dimensions[uAxis] - 1,
    Math.ceil((maxU + margin) / uSpacing) + 1,
  );
  const minVoxelV = Math.max(0, Math.floor((minV - margin) / vSpacing) - 1);
  const maxVoxelV = Math.min(
    dimensions[vAxis] - 1,
    Math.ceil((maxV + margin) / vSpacing) + 1,
  );

  let filled = 0;
  for (let v = minVoxelV; v <= maxVoxelV; v += 1) {
    const vCoord = v * vSpacing;
    const gridY = Math.round((vCoord - v0) / gridStep);
    if (gridY < 0 || gridY >= gridHeight) continue;

    for (let u = minVoxelU; u <= maxVoxelU; u += 1) {
      const uCoord = u * uSpacing;
      const gridX = Math.round((uCoord - u0) / gridStep);
      if (gridX < 0 || gridX >= gridWidth) continue;
      if (fillCells[gridY * gridWidth + gridX] !== 1) continue;

      const pointByAxis = [0, 0, 0];
      pointByAxis[fixedAxis] = stroke.sliceIndex;
      pointByAxis[uAxis] = u;
      pointByAxis[vAxis] = v;

      const x = pointByAxis[0];
      const y = pointByAxis[1];
      const z = pointByAxis[2];
      if (x < 0 || x >= width || y < 0 || y >= height || z < 0 || z >= depth) {
        continue;
      }

      const index = point3DToLinearIndex({ x, y, z }, width, height);
      if (stroke.indices.has(index)) continue;

      stroke.indices.add(index);
      setConnect2DPreviewBit(manualVolume, index, true);
      filled += 1;
    }
  }

  if (filled > 0) manualVolume.modified();
}

function isSameConnect2DRegionMeta(
  region: Pick<Connect2DRegion, 'seriesId' | 'viewportId' | 'sliceAxis' | 'sliceIndex'>,
  target: Pick<Connect2DRegion, 'seriesId' | 'viewportId' | 'sliceAxis' | 'sliceIndex'>,
) {
  return (
    region.seriesId === target.seriesId &&
    region.viewportId === target.viewportId &&
    region.sliceAxis === target.sliceAxis &&
    region.sliceIndex === target.sliceIndex
  );
}

function mergeConnect2DRegion(
  base: Connect2DRegion,
  incoming: Connect2DRegion,
): Connect2DRegion {
  const merged = new Set<number>(base.indices);
  incoming.indices.forEach((index) => merged.add(index));

  return {
    ...base,
    canvas: incoming.canvas,
    indices: Uint32Array.from(merged),
  };
}

function revertConnect2DStrokePreview(stroke: Connect2DActiveStroke | null) {
  if (!stroke || stroke.indices.size === 0) return;

  const manualVolume = getOrCreateManualVolume(stroke.seriesId, stroke.lowQuality);
  if (!manualVolume) return;

  stroke.indices.forEach((index) => {
    setConnect2DPreviewBit(manualVolume, index, false);
  });
  manualVolume.modified();
  renderEnabledElements();
}

function commitConnect2DStroke(stroke: Connect2DActiveStroke | null) {
  if (!stroke || stroke.indices.size === 0) return;

  const region: Connect2DRegion = {
    seriesId: stroke.seriesId,
    lowQuality: stroke.lowQuality,
    viewportId: stroke.viewportId,
    sliceAxis: stroke.sliceAxis,
    sliceAxisIndex: stroke.sliceAxisIndex,
    sliceIndex: stroke.sliceIndex,
    indices: Uint32Array.from(stroke.indices),
    canvas: stroke.canvas,
  };

  connect2DMessage = '';

  if (!connect2DSelection.start) {
    connect2DSelection.start = region;
    notifyConnect2DSelectionChanged();
    return;
  }

  if (isSameConnect2DRegionMeta(connect2DSelection.start, region)) {
    connect2DSelection.start = mergeConnect2DRegion(connect2DSelection.start, region);
    notifyConnect2DSelectionChanged();
    return;
  }

  if (
    connect2DSelection.start.viewportId !== region.viewportId ||
    connect2DSelection.start.sliceAxis !== region.sliceAxis
  ) {
    revertConnect2DStrokePreview(stroke);
    connect2DMessage = 'Start and End must be selected in the same 2D view.';
    notifyConnect2DSelectionChanged();
    return;
  }

  if (!connect2DSelection.end) {
    connect2DSelection.end = region;
    notifyConnect2DSelectionChanged();
    return;
  }

  if (isSameConnect2DRegionMeta(connect2DSelection.end, region)) {
    connect2DSelection.end = mergeConnect2DRegion(connect2DSelection.end, region);
    notifyConnect2DSelectionChanged();
    return;
  }

  revertConnect2DStrokePreview(stroke);
  connect2DMessage = 'Start and End have already been set. You can only continue adding regions on the existing Start or End slice.';
  notifyConnect2DSelectionChanged();
}

function commitBrushConnect2DStroke(stroke: Connect2DActiveStroke | null) {
  if (!stroke || stroke.indices.size === 0) return;

  const region: Connect2DRegion = {
    seriesId: stroke.seriesId,
    lowQuality: stroke.lowQuality,
    viewportId: stroke.viewportId,
    sliceAxis: stroke.sliceAxis,
    sliceAxisIndex: stroke.sliceAxisIndex,
    sliceIndex: stroke.sliceIndex,
    indices: Uint32Array.from(stroke.indices),
    canvas: stroke.canvas,
  };

  connect2DMessage = '';

  switch (
    getBrushConnect2DSelectionUpdateAction({
      start: connect2DSelection.start,
      end: connect2DSelection.end,
      incoming: region,
    })
  ) {
    case 'set-start':
    case 'reset-start':
      connect2DSelection.start = region;
      connect2DSelection.end = null;
      break;
    case 'roll-end-to-start':
      connect2DSelection.start = connect2DSelection.end;
      connect2DSelection.end = region;
      break;
    case 'merge-start':
      connect2DSelection.start = mergeConnect2DRegion(
        connect2DSelection.start!,
        region,
      );
      break;
    case 'set-end':
      connect2DSelection.end = region;
      break;
  }
  notifyConnect2DSelectionChanged();
}

function setConnect2DEvents(params: {
  seriesId: string;
  lowQuality: boolean;
}) {
  const { seriesId, lowQuality } = params;
  setMousePointer('crosshair');
  const manualVolume = getOrCreateManualVolume(seriesId, lowQuality);
  let activeStroke: Connect2DActiveStroke | null = null;

  const stampFromEvent = (
    viewportId: PanelComponentKeyType,
    element: HTMLDivElement,
    svg: Element | null | undefined,
    e: MouseEvent,
  ) => {
    if (!activeStroke || !manualVolume.imageData) return;

    const rect = element.getBoundingClientRect();
    const canvas: [number, number] = [
      e.clientX - rect.left,
      e.clientY - rect.top,
    ];
    const centerWorld = getCanvasToWorld(viewportId, canvas);
    if (!centerWorld) return;

    const brushCircle = svg?.querySelector('circle#brush');
    const radiusMM = Number(brushCircle?.getAttribute('radiusMM') ?? 0);

    activeStroke.radiusMM = radiusMM;
    const pointWorld = centerWorld as Types.Point3;
    const centers = getConnect2DInterpolatedCenters(
      activeStroke.lastWorld,
      pointWorld,
      radiusMM,
    );

    centers.forEach((center) => {
      appendConnect2DStrokePoint(activeStroke!, center, radiusMM);
      stampConnect2DBrush({
        manualVolume,
        imageData: manualVolume.imageData,
        stroke: activeStroke!,
        centerWorld: center,
        radiusMM,
      });
    });

    activeStroke.lastWorld = pointWorld;
    renderEnabledElements();
  };

  return getEnabledElements()
    .map((prop) => {
      const viewportId = prop.viewportId as PanelComponentKeyType;
      if (!DICOM_VIEWPORT_IDS.has(viewportId)) return undefined;

      const sliceAxis = getConnect2DViewportSliceAxis(viewportId);
      if (!sliceAxis) return undefined;
      const { axis: connect2DSliceAxis, index: connect2DSliceAxisIndex } =
        sliceAxis;

      const { element } = prop.viewport;
      const svg =
        prop.viewport.element.parentElement?.querySelector('svg#custom-overlay');
      if (svg) svg.setAttribute('visibility', 'visible');

      function onMouseDown(e: MouseEvent) {
        if (e.buttons !== 1 || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (connect2DApplying) return;
        if (!hasSegmentationState()) return;
        if (!manualVolume.imageData) return;

        updateBrushOverlayPosition({
          element,
          svg,
          clientX: e.clientX,
          clientY: e.clientY,
        });

        e.preventDefault();
        e.stopPropagation();
        (e as any).stopImmediatePropagation?.();

        const rect = element.getBoundingClientRect();
        const canvas: [number, number] = [
          e.clientX - rect.left,
          e.clientY - rect.top,
        ];
        const centerWorld = getCanvasToWorld(viewportId, canvas);
        if (!centerWorld) return;

        const centerIndex = worldToIndex(
          manualVolume.imageData,
          centerWorld as Types.Point3,
        );
        const [width, height, depth] = manualVolume.dimensions as [
          number,
          number,
          number,
        ];
        const maxByAxis = [width - 1, height - 1, depth - 1];
        const sliceIndex = Math.round(
          Math.max(
            0,
            Math.min(
              maxByAxis[connect2DSliceAxisIndex],
              centerIndex[connect2DSliceAxisIndex],
            ),
          ),
        );

        activeStroke = {
          seriesId,
          lowQuality,
          viewportId,
          sliceAxis: connect2DSliceAxis,
          sliceAxisIndex: connect2DSliceAxisIndex,
          sliceIndex,
          indices: new Set<number>(),
          canvas,
          pointsWorld: [],
          radiusMM: 0,
        };

        stampFromEvent(viewportId, element, svg, e);
      }

      function onMouseMove(e: MouseEvent) {
        updateBrushOverlayPosition({
          element,
          svg,
          clientX: e.clientX,
          clientY: e.clientY,
        });

        if (!activeStroke) return;
        if (e.buttons !== 1) {
          activeStroke = null;
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        (e as any).stopImmediatePropagation?.();
        stampFromEvent(viewportId, element, svg, e);
      }

      function onMouseUp(e: MouseEvent) {
        updateBrushOverlayPosition({
          element,
          svg,
          clientX: e.clientX,
          clientY: e.clientY,
        });

        if (!activeStroke) return;

        e.preventDefault();
        e.stopPropagation();
        (e as any).stopImmediatePropagation?.();
        fillConnect2DClosedStrokeInterior({ manualVolume, stroke: activeStroke });
        commitConnect2DStroke(activeStroke);
        activeStroke = null;
        renderEnabledElements();
      }

      element.addEventListener('mousedown', onMouseDown, true);
      element.addEventListener('mousemove', onMouseMove, true);
      element.addEventListener('mouseup', onMouseUp, true);

      return () => {
        element.removeEventListener('mousedown', onMouseDown, true);
        element.removeEventListener('mousemove', onMouseMove, true);
        element.removeEventListener('mouseup', onMouseUp, true);
        activeStroke = null;
      };
    })
    .filter(Boolean);
}


const CONNECT_2D_EXACT_PAIR_LIMIT = 250_000;
const CONNECT_2D_PROGRESS_INTERVAL_MS = 180;

function waitConnect2DFrame() {
  return new Promise<void>((resolve) => setTimeout(resolve, 0));
}

function getConnect2DRegionPoints(
  region: Connect2DRegion,
  width: number,
  height: number,
) {
  return Array.from(region.indices, (index) =>
    linearIndexToPoint3D(index, width, height),
  );
}

type Connect2DPlaneMask = {
  mask: Uint8Array;
  width: number;
  height: number;
};

function buildConnect2DPlaneMask(params: {
  region: Connect2DRegion;
  width: number;
  height: number;
  minU: number;
  minV: number;
  maskWidth: number;
  maskHeight: number;
}) {
  const { region, width, height, minU, minV, maskWidth, maskHeight } = params;
  const fixedAxis = region.sliceAxisIndex;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== fixedAxis,
  );
  const uAxis = inPlaneAxes[0];
  const vAxis = inPlaneAxes[1];
  const mask = new Uint8Array(maskWidth * maskHeight);

  region.indices.forEach((linearIndex) => {
    const point = linearIndexToPoint3D(linearIndex, width, height);
    const values = [point.x, point.y, point.z];
    const u = values[uAxis] - minU;
    const v = values[vAxis] - minV;
    if (u < 0 || u >= maskWidth || v < 0 || v >= maskHeight) return;
    mask[v * maskWidth + u] = 1;
  });

  return { mask, width: maskWidth, height: maskHeight };
}

function getConnect2DPlaneBounds(params: {
  region: Connect2DRegion;
  width: number;
  height: number;
}) {
  const { region, width, height } = params;
  const fixedAxis = region.sliceAxisIndex;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== fixedAxis,
  );
  const uAxis = inPlaneAxes[0];
  const vAxis = inPlaneAxes[1];
  let minU = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;

  region.indices.forEach((linearIndex) => {
    const point = linearIndexToPoint3D(linearIndex, width, height);
    const values = [point.x, point.y, point.z];
    const u = values[uAxis];
    const v = values[vAxis];
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  });

  return { minU, maxU, minV, maxV };
}

function distanceTransform2D(
  sourceMask: Uint8Array,
  width: number,
  height: number,
) {
  const distance = new Float32Array(width * height);
  const inf = width + height + 1024;
  const diagonal = Math.SQRT2;

  for (let i = 0; i < distance.length; i += 1) {
    distance[i] = sourceMask[i] ? 0 : inf;
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      let best = distance[i];
      if (x > 0) best = Math.min(best, distance[i - 1] + 1);
      if (y > 0) best = Math.min(best, distance[i - width] + 1);
      if (x > 0 && y > 0) best = Math.min(best, distance[i - width - 1] + diagonal);
      if (x + 1 < width && y > 0) best = Math.min(best, distance[i - width + 1] + diagonal);
      distance[i] = best;
    }
  }

  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const i = y * width + x;
      let best = distance[i];
      if (x + 1 < width) best = Math.min(best, distance[i + 1] + 1);
      if (y + 1 < height) best = Math.min(best, distance[i + width] + 1);
      if (x + 1 < width && y + 1 < height) best = Math.min(best, distance[i + width + 1] + diagonal);
      if (x > 0 && y + 1 < height) best = Math.min(best, distance[i + width - 1] + diagonal);
      distance[i] = best;
    }
  }

  return distance;
}

function buildConnect2DSignedDistance(maskInfo: Connect2DPlaneMask) {
  const { mask, width, height } = maskInfo;
  const outsideMask = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i += 1) {
    outsideMask[i] = mask[i] ? 0 : 1;
  }

  const distanceToInside = distanceTransform2D(mask, width, height);
  const distanceToOutside = distanceTransform2D(outsideMask, width, height);
  const signedDistance = new Float32Array(mask.length);

  for (let i = 0; i < mask.length; i += 1) {
    signedDistance[i] = mask[i] ? -distanceToOutside[i] : distanceToInside[i];
  }

  return signedDistance;
}

function getConnect2DMorphedFillIndices(params: {
  start: Connect2DRegion;
  end: Connect2DRegion;
  width: number;
  height: number;
  depth: number;
}) {
  const { start, end, width, height, depth } = params;
  const fixedAxis = start.sliceAxisIndex;
  const inPlaneAxes = ([0, 1, 2] as const).filter(
    (axis) => axis !== fixedAxis,
  );
  const uAxis = inPlaneAxes[0];
  const vAxis = inPlaneAxes[1];
  const dimensions = [width, height, depth];
  const startBounds = getConnect2DPlaneBounds({ region: start, width, height });
  const endBounds = getConnect2DPlaneBounds({ region: end, width, height });
  const margin = 3;
  const minU = Math.max(0, Math.min(startBounds.minU, endBounds.minU) - margin);
  const maxU = Math.min(
    dimensions[uAxis] - 1,
    Math.max(startBounds.maxU, endBounds.maxU) + margin,
  );
  const minV = Math.max(0, Math.min(startBounds.minV, endBounds.minV) - margin);
  const maxV = Math.min(
    dimensions[vAxis] - 1,
    Math.max(startBounds.maxV, endBounds.maxV) + margin,
  );
  const maskWidth = maxU - minU + 1;
  const maskHeight = maxV - minV + 1;
  const visited = new Uint8Array(width * height * depth);
  const linearIndices: number[] = [];

  const pushIndex = (index: number) => {
    if (index < 0 || index >= visited.length || visited[index]) return;
    visited[index] = 1;
    linearIndices.push(index);
  };

  start.indices.forEach(pushIndex);
  end.indices.forEach(pushIndex);

  if (maskWidth <= 0 || maskHeight <= 0) {
    return linearIndices;
  }

  const startMask = buildConnect2DPlaneMask({
    region: start,
    width,
    height,
    minU,
    minV,
    maskWidth,
    maskHeight,
  });
  const endMask = buildConnect2DPlaneMask({
    region: end,
    width,
    height,
    minU,
    minV,
    maskWidth,
    maskHeight,
  });
  const startSdf = buildConnect2DSignedDistance(startMask);
  const endSdf = buildConnect2DSignedDistance(endMask);
  const sliceDistance = Math.abs(end.sliceIndex - start.sliceIndex);
  const direction = end.sliceIndex >= start.sliceIndex ? 1 : -1;

  for (let s = 0; s <= sliceDistance; s += 1) {
    const fixed = start.sliceIndex + s * direction;
    const t = sliceDistance === 0 ? 0 : s / sliceDistance;

    for (let localV = 0; localV < maskHeight; localV += 1) {
      const v = minV + localV;
      for (let localU = 0; localU < maskWidth; localU += 1) {
        const u = minU + localU;
        const maskIndex = localV * maskWidth + localU;
        const blendedDistance = startSdf[maskIndex] * (1 - t) + endSdf[maskIndex] * t;
        if (blendedDistance > 0) continue;

        const values = [0, 0, 0];
        values[fixedAxis] = fixed;
        values[uAxis] = u;
        values[vAxis] = v;
        const x = values[0];
        const y = values[1];
        const z = values[2];
        if (x < 0 || x >= width || y < 0 || y >= height || z < 0 || z >= depth) {
          continue;
        }
        pushIndex((z * height + y) * width + x);
      }
    }
  }

  return linearIndices;
}

export async function applyConnect2DSelection(params: {
  seriesId: string;
  lowQuality: boolean;
}) {
  if (connect2DApplying) return null;

  const { seriesId, lowQuality } = params;
  const { start, end } = connect2DSelection;

  if (!start || !end) {
    connect2DMessage = 'Both Start and End regions must be set.';
    notifyConnect2DSelectionChanged();
    return null;
  }

  if (start.viewportId !== end.viewportId || start.sliceAxis !== end.sliceAxis) {
    connect2DMessage = 'Only two regions specified in the same 2D view can be connected.';
    notifyConnect2DSelectionChanged();
    return null;
  }

  if (start.seriesId !== seriesId || end.seriesId !== seriesId) {
    connect2DMessage = 'The selected regions do not match the current series.';
    notifyConnect2DSelectionChanged();
    return null;
  }

  if (!hasSegmentationState()) return null;

  const manualVolume = getOrCreateManualVolume(seriesId, lowQuality);
  if (!manualVolume?.imageData) return null;

  connect2DApplying = true;
  connect2DMessage = 'Applying Connect 2D...';
  notifyConnect2DSelectionChanged();

  try {
    await waitConnect2DFrame();

    const [width, height, depth] = manualVolume.dimensions as [
      number,
      number,
      number,
    ];
    const volume = { width, height, depth };
    const originalPairCount = start.indices.length * end.indices.length;
    let optimized = false;
    let pairCount = originalPairCount;
    let linearIndices: number[] = [];

    if (originalPairCount <= CONNECT_2D_EXACT_PAIR_LIMIT) {
      const visited = new Uint8Array(width * height * depth);
      const startPoints = getConnect2DRegionPoints(start, width, height);
      const endPoints = getConnect2DRegionPoints(end, width, height);
      let processedPairs = 0;
      let lastProgressAt = Date.now();

      connect2DMessage = `Applying Connect 2D... (${pairCount.toLocaleString()} pairs)`;
      notifyConnect2DSelectionChanged();

      for (const startPoint of startPoints) {
        for (const endPoint of endPoints) {
          const coords = getThickLineCoords3D(startPoint, endPoint, 1, {
            volume,
            thicknessUnit: 'voxel',
          });

          coords.forEach((point) => {
            const index = point3DToLinearIndex(point, width, height);
            if (visited[index]) return;
            visited[index] = 1;
            linearIndices.push(index);
          });

          processedPairs += 1;
          if (processedPairs % 512 === 0) {
            const now = Date.now();
            if (now - lastProgressAt >= CONNECT_2D_PROGRESS_INTERVAL_MS) {
              const percent = Math.round((processedPairs / pairCount) * 100);
              connect2DMessage = `Applying Connect 2D... ${percent}%`;
              notifyConnect2DSelectionChanged();
              lastProgressAt = now;
            }
            await waitConnect2DFrame();
          }
        }
      }
    } else {
      optimized = true;
      connect2DMessage = `Applying Connect 2D fill... (${originalPairCount.toLocaleString()} pairs optimized)`;
      notifyConnect2DSelectionChanged();
      await waitConnect2DFrame();
      linearIndices = getConnect2DMorphedFillIndices({
        start,
        end,
        width,
        height,
        depth,
      });
      pairCount = linearIndices.length;
    }

    const axes = getViewportAxes(start.viewportId);
    const centerWorld = getCanvasToWorld(start.viewportId, start.canvas);
    if (!axes || !centerWorld || linearIndices.length === 0) return null;

    beginStroke({
      imageData: manualVolume.imageData,
      plane: {
        centerWorld: centerWorld as Types.Point3,
        normalWorld: axes.viewPlaneNormal,
        uWorld: axes.viewRight,
        vWorld: axes.viewUp,
      },
      radiusMM: 0,
      pixelsPerMM: 1,
      segmentIndex: 1,
      flippedThisStroke: linearIndices,
      flood: true,
    });
    endStroke();

    const result = {
      sourceVoxelCount: start.indices.length + end.indices.length,
      pairCount,
      originalPairCount,
      optimized,
      connectedVoxelCount: linearIndices.length,
      viewportId: start.viewportId,
      startSliceIndex: start.sliceIndex,
      endSliceIndex: end.sliceIndex,
    };

    clearConnect2DSelectionInternal(true);
    console.log('[Connect 2D][Apply]', result);
    return result;
  } finally {
    connect2DApplying = false;
    connect2DMessage = '';
    notifyConnect2DSelectionChanged();
  }
}

export function setModifierEvents(params: {
  seriesId: string;
  // radiusMM:number,
  // pixelsPerMM:number,
  segKey: SegmentationControllerType;
  modifierType: ModifierType;
  lowQuality:boolean;
  range: [number, number];
}) {
  ensureManualModifierInputSnapshotListeners();

  const { seriesId, segKey, modifierType,lowQuality, range } = params;

  if (modifierType === ModifierType.CONNECT_2D) {
    return setConnect2DEvents({ seriesId, lowQuality });
  }

  // 마우스 포인터 이상해서
  setMousePointer();

  let tempIJKs: ReturnType<typeof computeAndDrawEx>;
  let tempLinearIndices: number[] | undefined;
  let shiftFloodPreviewSeedKey: string | null = null;
  let strokeParams: Parameters<typeof beginStroke>;
  let shiftFloodPreviewDown = manualModifierInputSnapshot.shiftDown;
  const startShiftFloodPreviewCallbacks = new Set<() => void>();
  const manualVolume = getOrCreateManualVolume(seriesId,lowQuality);
  const tabRangeViewportIds = new Set<PanelComponentKeyType>([
    'Axial',
    'Coronal',
    'Sagittal',
  ]);
  const tabRangePreviewOwnerKey = getTabRangePreviewOwnerKey(
    seriesId,
    lowQuality,
  );
  const tabRangePreviewBindingToken = ++tabRangePreviewState.bindingToken;
  if (
    modifierType !== ModifierType.BRUSH &&
    tabRangePreviewState.ownerKey === tabRangePreviewOwnerKey
  ) {
    tabRangePreviewState.held = false;
    stopTabRangePreview();
  }

  function refreshTabRangePreview(requestId: number, reason: string) {
    if (modifierType !== ModifierType.BRUSH) return;
    if (
      requestId !== tabRangePreviewState.refreshRequestId ||
      !tabRangePreviewState.held
    ) {
      return;
    }

    const volume = getOriginalVolumeFromSeriesId(seriesId);
    const scalarData = volume?.voxelManager?.getCompleteScalarDataArray?.() as
      | TabRangePreviewScalarData
      | undefined;
    if (!scalarData) {
      stopTabRangePreview();
      return;
    }

    tabRangePreviewState.active = true;
    const results: Uint32Array[] = [];
    const startedAt = performance.now();
    let sampledViewportCount = 0;

    try {
      getEnabledElements().forEach((prop) => {
        const viewportId = prop.viewportId as PanelComponentKeyType;
        if (!tabRangeViewportIds.has(viewportId)) return;

        const payload = createVisibleIJKSamplingPayload(
          prop.viewport as VolumeViewport,
          range[0],
          range[1],
          {
            step: 3,
            valueMode: 'nearest',
          },
        );

        if (!payload) return;

        sampledViewportCount += 1;
        results.push(
          sampleVisibleLinearIndicesInRangeFastOblique(scalarData, {
            ...payload,
            viewportId,
          }),
        );
      });

      if (requestId !== tabRangePreviewState.refreshRequestId) {
        return;
      }

      const mergedIndices = mergeLinearIndicesSorted(results);
      setTabRangePreviewIndices(
        tabRangePreviewOwnerKey,
        manualVolume,
        mergedIndices,
      );
      tabRangePreviewState.range = [range[0], range[1]];

      console.debug('[Tab range preview][refresh]', {
        reason,
        durationMs: Number((performance.now() - startedAt).toFixed(2)),
        range: [range[0], range[1]],
        viewportCount: sampledViewportCount,
        previewVoxelCount: mergedIndices.length,
      });
    } catch (error) {
      stopTabRangePreview();
      console.warn('Tab range preview failed:', error);
    }
  }

  function scheduleTabRangePreviewRefresh(reason: string, delayMs: number) {
    if (modifierType !== ModifierType.BRUSH) return;

    const hasCurrentPreview =
      tabRangePreviewState.ownerKey === tabRangePreviewOwnerKey &&
      tabRangePreviewState.active;
    if (
      tabRangePreviewState.ownerKey === tabRangePreviewOwnerKey &&
      !shouldRefreshPreviewForRangeChange(
        tabRangePreviewState.range,
        range,
        hasCurrentPreview,
      )
    ) {
      console.debug('[Tab range preview][refresh-skip]', {
        reason,
        range: [range[0], range[1]],
        active: tabRangePreviewState.active,
      });
      return;
    }

    clearTabRangePreviewRefreshTimer();
    tabRangePreviewState.ownerKey = tabRangePreviewOwnerKey;
    tabRangePreviewState.manualVolume = manualVolume;
    const requestId = ++tabRangePreviewState.refreshRequestId;

    if (delayMs <= 0) {
      refreshTabRangePreview(requestId, reason);
      return;
    }

    tabRangePreviewState.refreshTimer = window.setTimeout(() => {
      tabRangePreviewState.refreshTimer = null;
      refreshTabRangePreview(requestId, reason);
    }, delayMs);
  }

  let tabPreviewBrushStrokeActive = false;
  let tabPreviewConnect2DStroke: Connect2DActiveStroke | null = null;

  function commitPreviewConnect2DStrokeFromDelta(params: {
    stroke: Connect2DActiveStroke | null;
    strokeDelta: UndoDelta | null;
    previewMode: 'shift' | 'tab';
  }) {
    const { stroke, strokeDelta, previewMode } = params;
    if (!stroke) return;

    const strokeIndices = getPreviewMaskConnect2DStrokeIndices({
      isDicomViewport: isShiftFloodPreviewViewport(stroke.viewportId),
      isBrushModifier: modifierType === ModifierType.BRUSH,
      previewMode,
      strokeFlippedIndices: strokeDelta?.flippedIndices,
    });
    if (!strokeIndices.length) return;

    const addedCount = appendConnect2DStrokeLinearIndices({
      manualVolume,
      stroke,
      indices: strokeIndices,
    });
    if (addedCount <= 0) return;

    manualVolume.modified();
    renderEnabledElements();
    commitBrushConnect2DStroke(stroke);
  }

  function endTabPreviewBrushStroke() {
    if (!tabPreviewBrushStrokeActive) return;

    const strokeDelta = endStrokeAndCaptureDelta();
    commitPreviewConnect2DStrokeFromDelta({
      stroke: tabPreviewConnect2DStroke,
      strokeDelta,
      previewMode: 'tab',
    });
    tabPreviewConnect2DStroke = null;
    tabPreviewBrushStrokeActive = false;
  }

  function onGlobalKeyDown(e: KeyboardEvent) {
    if (e.key === 'Shift' && modifierType === ModifierType.BRUSH) {
      if (e.repeat) return;
      shiftFloodPreviewDown = true;
      startShiftFloodPreviewCallbacks.forEach((startPreview) => startPreview());
      return;
    }

    if (modifierType!==ModifierType.BRUSH) return;
    if (e.key !== 'Tab') return;

    e.preventDefault();
    tabRangePreviewState.held = true;
    if (e.repeat) return;

    scheduleTabRangePreviewRefresh('tab-down', 0);
  }

  function onGlobalKeyUp(e: KeyboardEvent) {
    if (e.key === 'Shift') {
      shiftFloodPreviewDown = false;
      clearPreview();
      return;
    }

    if (e.key !== 'Tab') return;
    e.preventDefault();
    endTabPreviewBrushStroke();
    tabRangePreviewState.held = false;
    stopTabRangePreview();
  }

  function isShiftFloodPreviewActive(viewportId: PanelComponentKeyType) {
    return shouldUseShiftFloodPreview({
      isDicomViewport: isShiftFloodPreviewViewport(viewportId),
      isBrushModifier: modifierType === ModifierType.BRUSH,
      shiftDown: shiftFloodPreviewDown,
    });
  }

  function shouldCommitCurrentShiftFloodPreview(viewportId: PanelComponentKeyType) {
    return shouldCommitShiftFloodPreview({
      isDicomViewport: isShiftFloodPreviewViewport(viewportId),
      isBrushModifier: modifierType === ModifierType.BRUSH,
      shiftDown: shiftFloodPreviewDown,
      hasActiveStroke: !!hasSess(),
    });
  }

  function syncShiftFloodPreviewFromMouseEvent(e: MouseEvent | WheelEvent) {
    if (modifierType !== ModifierType.BRUSH) return;

    if (e.shiftKey) {
      shiftFloodPreviewDown = true;
      return;
    }

    if (shiftFloodPreviewDown) {
      shiftFloodPreviewDown = false;
      clearPreview();
    }
  }

  function writePreviewLinearIndices(
    indices: ReadonlyArray<number>,
    previewEnabled: boolean,
  ) {
    indices.forEach((index) => {
      setPreviewBitAtLinearIndex(
        manualVolume.voxelManager as any,
        index,
        manualVolume.dimensions,
        previewEnabled,
      );
    });
  }

  function clearPreview(render = true) {
    //돌려놓고 버림
    if (!tempIJKs) {
      tempLinearIndices = undefined;
      shiftFloodPreviewSeedKey = null;
      return false;
    }

    const previewLinearIndices =
      tempLinearIndices ??
      previewIJKsToLinearIndices(tempIJKs, manualVolume.dimensions);
    writePreviewLinearIndices(previewLinearIndices, false);
    const changed = previewLinearIndices.length > 0;
    tempIJKs = undefined;
    tempLinearIndices = undefined;
    shiftFloodPreviewSeedKey = null;
    if (changed && render) {
      manualVolume.modified();
      renderEnabledElements();
    }

    return changed;
  }

  function setPreview(render = true) {
    if (!tempIJKs) return false;
    // 뭔가 있으니 칠해야함.
    const previewLinearIndices =
      tempLinearIndices ??
      previewIJKsToLinearIndices(tempIJKs, manualVolume.dimensions);
    writePreviewLinearIndices(previewLinearIndices, true);
    const changed = previewLinearIndices.length > 0;
    if (changed && render) {
      manualVolume.modified();
      renderEnabledElements();
    }

    return changed;
  }
  const cleanupEvents = getEnabledElements().map((prop) => {
    const { viewport } = prop;
    const viewportId = prop.viewportId as PanelComponentKeyType;

    const { element } = viewport;
    const svg =
      viewport.element.parentElement?.querySelector('svg#custom-overlay');
    let pointerInside = false;
    let lastPreviewClientXY: [number, number] | null = null;
    let queuedShiftPreviewClientXY: [number, number] | null = null;
    let shiftPreviewFrameId: number | null = null;
    let shiftPreviewTimerId: number | null = null;
    let lastShiftPreviewCompletedAtMs = 0;
    let lastShiftPreviewDurationMs = 0;

    // function onKeyDown(e: KeyboardEvent) {
    //   if (e.key === 'z' || e.key === 'Z'){
    //      undoStroke();
    //      return
    //     }
    //   if (e.key === 'x' || e.key === 'x') {
    //     redoStroke();
    //     return
    //   }
    // }

    // function onKeyUp(e: KeyboardEvent) {
    //   if (e.key === 'z' || e.key === 'Z') {
    //     undoStroke();
    //     return;
    //   }
    //   if (e.key === 'x' || e.key === 'x') {
    //     redoStroke();
    //     return;
    //   }
    // }

    function cancelShiftFloodPreviewFrame() {
      if (shiftPreviewFrameId !== null) {
        window.cancelAnimationFrame(shiftPreviewFrameId);
      }
      if (shiftPreviewTimerId !== null) {
        window.clearTimeout(shiftPreviewTimerId);
      }
      shiftPreviewFrameId = null;
      shiftPreviewTimerId = null;
      queuedShiftPreviewClientXY = null;
    }

    function hasPendingShiftFloodPreviewFrame() {
      return shiftPreviewFrameId !== null || shiftPreviewTimerId !== null;
    }

    function scheduleShiftFloodPreviewFrame(delayMs = 0) {
      if (shiftPreviewFrameId !== null || shiftPreviewTimerId !== null) return;

      if (delayMs > 0) {
        shiftPreviewTimerId = window.setTimeout(() => {
          shiftPreviewTimerId = null;
          scheduleShiftFloodPreviewFrame();
        }, delayMs);
        return;
      }

      shiftPreviewFrameId = window.requestAnimationFrame(
        runQueuedShiftFloodPreview,
      );
    }

    function runQueuedShiftFloodPreview() {
      shiftPreviewFrameId = null;

      const delayMs = getShiftFloodPreviewDelayMs({
        nowMs: performance.now(),
        lastCompletedAtMs: lastShiftPreviewCompletedAtMs,
        lastDurationMs: lastShiftPreviewDurationMs,
      });
      if (delayMs > 0) {
        scheduleShiftFloodPreviewFrame(delayMs);
        return;
      }

      const clientXY = queuedShiftPreviewClientXY;
      queuedShiftPreviewClientXY = null;

      if (
        !clientXY ||
        !shouldScheduleShiftFloodPreview({
          isDicomViewport: isShiftFloodPreviewViewport(viewportId),
          isBrushModifier: modifierType === ModifierType.BRUSH,
          shiftDown: shiftFloodPreviewDown,
          pointerInside,
          hasPointer: true,
          hasActiveStroke: !!hasSess(),
          hasPendingFrame: false,
        })
      ) {
        return;
      }

      const startedAtMs = performance.now();
      loadingPreview(clientXY);
      const completedAtMs = performance.now();
      lastShiftPreviewCompletedAtMs = completedAtMs;
      lastShiftPreviewDurationMs = completedAtMs - startedAtMs;
    }

    function requestShiftFloodPreview(clientXY: [number, number]) {
      queuedShiftPreviewClientXY = clientXY;
      if (
        !shouldScheduleShiftFloodPreview({
          isDicomViewport: isShiftFloodPreviewViewport(viewportId),
          isBrushModifier: modifierType === ModifierType.BRUSH,
          shiftDown: shiftFloodPreviewDown,
          pointerInside,
          hasPointer: !!queuedShiftPreviewClientXY,
          hasActiveStroke: !!hasSess(),
          hasPendingFrame: hasPendingShiftFloodPreviewFrame(),
        })
      ) {
        return;
      }

      scheduleShiftFloodPreviewFrame();
    }

    function onMouseEnterBrush(e: MouseEvent) {
      syncShiftFloodPreviewFromMouseEvent(e);
      pointerInside = true;
      lastPreviewClientXY = [e.clientX, e.clientY];
      svg?.setAttribute('visibility', 'visible');
      startShiftFloodPreview();
    }
    function onMouseLeaveBrush(e: MouseEvent) {
      pointerInside = false;
      lastPreviewClientXY = null;
      cancelShiftFloodPreviewFrame();
      svg?.setAttribute('visibility', 'hidden');
      if (isShiftFloodPreviewActive(viewportId)) {
        clearPreview();
      }
    }
    let activeViewportEl: HTMLElement | null = null;
    let activeBrushCanvas: [number, number] | null = null;
    let brushConnect2DStroke: Connect2DActiveStroke | null = null;
    const shouldConstrainBrushToTabPreview = () =>
      modifierType === ModifierType.BRUSH &&
      isTabRangePreviewHeld(tabRangePreviewOwnerKey);

    function handleMouseAuxClick(e:MouseEvent){
      if (e.button !== 3 && e.button !== 4) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (e.button===3){
        handleUndo(viewportId);
        return;
      }
      if (e.button===4){
        handleRedo(viewportId);
        return;
      }
    }
    function onMouseDownBrush(e: MouseEvent) {
      syncShiftFloodPreviewFromMouseEvent(e);
      const shiftFloodPreviewActive = isShiftFloodPreviewActive(viewportId);


      // 다른 툴들 도망쳐
      if (e.ctrlKey || e.buttons !== 1) return;
      if (e.shiftKey && !shiftFloodPreviewActive) return;
      if (modifierType === ModifierType.PREVIEW && !shiftFloodPreviewActive) {
        return;
      }
      // e.preventDefault();
      if (!hasSegmentationState()) return;

      activeViewportEl = element;
      const rect = element.getBoundingClientRect()!;
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      activeBrushCanvas = [canvasX, canvasY];

      const axes = getViewportAxes(viewportId);
      if (!axes) return;
      const {
        viewPlaneNormal: normalWorld,
        viewUp: vWorld,
        viewRight: uWorld,
      } = axes;

      const centerWorld = getCanvasToWorld(viewportId, [canvasX, canvasY]);
      if (!centerWorld) return;

      //메뉴얼모드일때 기능이 없어도 된다? 막아둔다
      //근데 이게 맞나싶고
      // const isHitAnno = isHitCornerstoneAnnotation(
      //   element,
      //   [e.clientX, e.clientY],
      //   centerWorld,
      // );
      // if (isHitAnno) return;

      //경우는 없지만..
      const brushCircle = svg?.querySelector('circle#brush');
      if (!brushCircle) return;

      // 클릭 시점으로 다시 계산해야징. attrib만 ㄴ낼름 가져와서
      const pixelsPerMM = Number(brushCircle.getAttribute('pixelsPerMM'));
      const radiusMM = Number(brushCircle.getAttribute('radiusMM'));

      strokeParams = [
        {
          imageData: manualVolume.imageData!,
          // labelScalar:manualVolume.voxelManager?.getCompleteScalarDataArray!() as Uint8Array,
          plane: {
            centerWorld,
            normalWorld,
            uWorld,
            vWorld,
          },
          radiusMM,
          pixelsPerMM,
          segmentIndex: Number(modifierType > 0),
        },
      ];

      if (modifierType === ModifierType.PREVIEW) return;
      if (shouldConstrainBrushToTabPreview()) {
        strokeParams[0].disableClosedFill = true;
        beginStroke(strokeParams[0]);
        tabPreviewBrushStrokeActive = true;
        tabPreviewConnect2DStroke = createBrushConnect2DStroke({
          seriesId,
          lowQuality,
          viewportId,
          manualVolume,
          centerWorld: centerWorld as Types.Point3,
          canvas: [canvasX, canvasY],
          radiusMM,
        });
        paintAtPointWithinIndices(
          centerWorld,
          getTabRangePreviewIndices(tabRangePreviewOwnerKey),
        );
        return;
      }
      if (shiftFloodPreviewActive) return;

      beginStroke(strokeParams[0]);
      paintAtPoint(centerWorld);

      if (modifierType === ModifierType.BRUSH) {
        const sliceAxis = getConnect2DViewportSliceAxis(viewportId);

        if (sliceAxis && manualVolume.imageData) {
          const centerIndex = worldToIndex(
            manualVolume.imageData,
            centerWorld as Types.Point3,
          );

          const [width, height, depth] = manualVolume.dimensions as [
            number,
            number,
            number,
          ];

          const maxByAxis = [width - 1, height - 1, depth - 1];

          const sliceIndex = Math.round(
            Math.max(
              0,
              Math.min(
                maxByAxis[sliceAxis.index],
                centerIndex[sliceAxis.index],
              ),
            ),
          );

          brushConnect2DStroke = {
            seriesId,
            lowQuality,
            viewportId,
            sliceAxis: sliceAxis.axis,
            sliceAxisIndex: sliceAxis.index,
            sliceIndex,
            indices: new Set<number>(),
            canvas: [canvasX, canvasY],
            pointsWorld: [],
            radiusMM,
            lastWorld: centerWorld as Types.Point3,
          };
        }
        if (brushConnect2DStroke) {
          appendConnect2DStrokePoint(
            brushConnect2DStroke,
            centerWorld as Types.Point3,
            radiusMM,
          );

          stampConnect2DBrush({
            manualVolume,
            imageData: manualVolume.imageData!,
            stroke: brushConnect2DStroke,
            centerWorld: centerWorld as Types.Point3,
            radiusMM,
          });
        }
      }
    }
    function loadingPreview(clientXY:[number,number]){
      //경우는 없지만..
      const brushCircle = svg?.querySelector('circle#brush');
      if (!brushCircle) return;
      const range= brushCircle.getAttribute('range')?.split(',').map(Number) as [number,number];
      const seedCanvas = getFloodFillSeedCanvas(clientXY, viewportId);
      if (!seedCanvas) return;

      const nextSeedKey = getShiftFloodPreviewSeedKey(
        viewportId,
        seedCanvas,
        range,
        getShiftFloodPreviewSliceKey(viewportId, manualVolume),
      );
      if (shiftFloodPreviewSeedKey === nextSeedKey) return;

      const nextTempIJKs = computeAndDrawEx(clientXY, viewportId, range, seedCanvas);
      const nextLinearIndices = nextTempIJKs
        ? previewIJKsToLinearIndices(nextTempIJKs, manualVolume.dimensions)
        : undefined;
      const { clearLinearIndices, paintLinearIndices } = diffPreviewLinearIndices(
        tempLinearIndices,
        nextLinearIndices,
      );
      writePreviewLinearIndices(clearLinearIndices, false);
      writePreviewLinearIndices(paintLinearIndices, true);
      tempIJKs = nextTempIJKs;
      tempLinearIndices = nextLinearIndices;
      shiftFloodPreviewSeedKey = tempIJKs ? nextSeedKey : null;

      if (clearLinearIndices.length > 0 || paintLinearIndices.length > 0) {
        manualVolume.modified();
        renderEnabledElements();
      }
    }

    function startShiftFloodPreview() {
      if (!isShiftFloodPreviewActive(viewportId)) return;
      if (!pointerInside || !lastPreviewClientXY) return;
      if (hasSess()) return;

      requestShiftFloodPreview(lastPreviewClientXY);
    }

    function bootstrapShiftFloodPreviewFromSnapshot() {
      if (modifierType !== ModifierType.BRUSH) return;

      const clientXY = manualModifierInputSnapshot.clientXY;
      const viewportRect = element.getBoundingClientRect();
      if (!isClientXYInsideViewportRect(clientXY, viewportRect)) return;

      pointerInside = true;
      lastPreviewClientXY = [clientXY![0], clientXY![1]];
      svg?.setAttribute('visibility', 'visible');
      updateBrushOverlayPosition({
        element,
        svg,
        clientX: clientXY![0],
        clientY: clientXY![1],
      });

      if (
        shouldBootstrapShiftFloodPreview({
          isDicomViewport: isShiftFloodPreviewViewport(viewportId),
          isBrushModifier: modifierType === ModifierType.BRUSH,
          shiftDown: shiftFloodPreviewDown,
          clientXY,
          viewportRect,
          hasActiveStroke: !!hasSess(),
          hasPendingFrame: hasPendingShiftFloodPreviewFrame(),
        })
      ) {
        requestShiftFloodPreview(lastPreviewClientXY);
      }
    }

    function onMouseMoveBrush(e: MouseEvent) {
      syncShiftFloodPreviewFromMouseEvent(e);
      lastPreviewClientXY = [e.clientX, e.clientY];
      if (isShiftFloodPreviewActive(viewportId) && !hasSess()) {
        requestShiftFloodPreview(lastPreviewClientXY);
        return;
      }
      if (modifierType === ModifierType.PREVIEW) {
        return;
      }
      if (shouldConstrainBrushToTabPreview()) {
        if (e.buttons !== 1) {
          brushConnect2DStroke = null;
          activeBrushCanvas = null;
          endTabPreviewBrushStroke();
          return;
        }
        if (!tabPreviewBrushStrokeActive || !hasSess()) return;
        const rect = element.getBoundingClientRect()!;
        const canvasX = e.clientX - rect.left;
        const canvasY = e.clientY - rect.top;
        const world = getCanvasToWorld(viewportId, [canvasX, canvasY]);
        if (!world) return;
        if (e.currentTarget !== activeViewportEl) {
          return;
        }
        paintAtPointWithinIndices(
          world,
          getTabRangePreviewIndices(tabRangePreviewOwnerKey),
        );
        return;
      }
      if (e.buttons !== 1) {
        endStroke();
        brushConnect2DStroke = null;
        activeBrushCanvas = null;
        return;
      }
      if (!hasSess()) return;
      const rect = element.getBoundingClientRect()!;
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const world = getCanvasToWorld(viewportId, [canvasX, canvasY]);
      if (!world) return;
      if (e.currentTarget !== activeViewportEl) {
        return;
      }
      paintAtPoint(world);
      if (modifierType === ModifierType.BRUSH) {
        if (!brushConnect2DStroke) return;
        const brushCircle = svg?.querySelector('circle#brush');
        const radiusMM = Number(
          brushCircle?.getAttribute('radiusMM') ??
            brushConnect2DStroke.radiusMM ??
            0,
        );

        appendConnect2DStrokePoint(
          brushConnect2DStroke,
          world as Types.Point3,
          radiusMM,
        );

        stampConnect2DBrush({
          manualVolume,
          imageData: manualVolume.imageData!,
          stroke: brushConnect2DStroke,
          centerWorld: world as Types.Point3,
          radiusMM,
        });

        brushConnect2DStroke.lastWorld = world as Types.Point3;
      }
    }
    function onMouseUpBrush(e: MouseEvent) {
      if (shouldConstrainBrushToTabPreview()) {
        endTabPreviewBrushStroke();
        activeBrushCanvas = null;
        return;
      }

      // 여기서 활성화 해야지
      syncShiftFloodPreviewFromMouseEvent(e);

      let shiftPreviewConnect2DStroke: Connect2DActiveStroke | null = null;
      if (shouldCommitCurrentShiftFloodPreview(viewportId)) {
        if(!strokeParams)return;
        // console.log(strokeParams.length,tempIJKs?.length)
        if (!strokeParams.length || !tempIJKs) return;
        const activeStrokeParams = strokeParams[0];
        const previewLinearIndices =
          tempLinearIndices ??
          previewIJKsToLinearIndices(tempIJKs, manualVolume.dimensions);
        activeStrokeParams.flippedThisStroke = previewLinearIndices;
        activeStrokeParams.flood=true;
        beginStroke(activeStrokeParams);
        strokeParams.pop();
        if (previewLinearIndices.length > 0) {
          const rect = element.getBoundingClientRect()!;
          const canvas: [number, number] = activeBrushCanvas ?? [
            e.clientX - rect.left,
            e.clientY - rect.top,
          ];
          shiftPreviewConnect2DStroke = createBrushConnect2DStroke({
            seriesId,
            lowQuality,
            viewportId,
            manualVolume,
            centerWorld: activeStrokeParams.plane.centerWorld as Types.Point3,
            canvas,
            radiusMM: activeStrokeParams.radiusMM,
          });
        }
        tempIJKs = [];
        tempLinearIndices = [];
        shiftFloodPreviewSeedKey = null;
        // return;
      }
      if (modifierType === ModifierType.PREVIEW) return;
      const strokeDelta = endStrokeAndCaptureDelta();
      if (shiftPreviewConnect2DStroke) {
        commitPreviewConnect2DStrokeFromDelta({
          stroke: shiftPreviewConnect2DStroke,
          strokeDelta,
          previewMode: 'shift',
        });
        activeBrushCanvas = null;
        return;
      }
      if (modifierType === ModifierType.BRUSH) {
        if (brushConnect2DStroke) {
          fillConnect2DClosedStrokeInterior({
            manualVolume,
            stroke: brushConnect2DStroke,
          });

          commitBrushConnect2DStroke(brushConnect2DStroke);
          brushConnect2DStroke = null;
        }
      }
      activeBrushCanvas = null;
    }

    // 마우스 휠로 장면 굴릴시에 카운터 초기화하려고
    function onMouseWheel(e:WheelEvent){
      syncShiftFloodPreviewFromMouseEvent(e);
      lastPreviewClientXY = [e.clientX, e.clientY];
      if (isShiftFloodPreviewActive(viewportId) && e.deltaY !== 0 && !hasSess()) {
        requestShiftFloodPreview(lastPreviewClientXY);
      }
    }
    // function onMouseOverFloodFill(e:MouseEvent){
    //   hoverTime = setTimeout(()=>{
    //     console.log('지났어. 몇초')
    //   },500);
    // };
    element.addEventListener('mouseenter', onMouseEnterBrush);
    element.addEventListener('mouseleave', onMouseLeaveBrush);
    element.addEventListener('mousedown', onMouseDownBrush);
    element.addEventListener('mousemove', onMouseMoveBrush);
    element.addEventListener('mouseup', onMouseUpBrush);
    element.addEventListener('wheel', onMouseWheel);
    element.addEventListener('auxclick',handleMouseAuxClick,true);
    startShiftFloodPreviewCallbacks.add(startShiftFloodPreview);
    bootstrapShiftFloodPreviewFromSnapshot();
    //element.addEventListener('keydown',onKeyDown)
    //element.addEventListener('keyup',onKeyUp)
    // if(modifierType===ModifierType.PREVIEW)
    //   element.addEventListener('mouseover',onMouseOverFloodFill)
    return () => {
      element.removeEventListener('mouseenter', onMouseEnterBrush);
      element.removeEventListener('mouseleave', onMouseLeaveBrush);
      element.removeEventListener('mousedown', onMouseDownBrush);
      element.removeEventListener('mousemove', onMouseMoveBrush);
      element.removeEventListener('mouseup', onMouseUpBrush);
      element.removeEventListener('wheel', onMouseWheel);
      element.removeEventListener('auxclick',handleMouseAuxClick,true);
      startShiftFloodPreviewCallbacks.delete(startShiftFloodPreview);
      cancelShiftFloodPreviewFrame();
      //element.removeEventListener('keydown',onKeyDown)
      //element.removeEventListener('keyup',onKeyUp)
      // if(modifierType===ModifierType.PREVIEW)
      //   element.removeEventListener('mouseover',onMouseOverFloodFill)
    };
  });

  window.addEventListener('keydown', onGlobalKeyDown, true);
  window.addEventListener('keyup', onGlobalKeyUp, true);
  if (modifierType === ModifierType.BRUSH && tabRangePreviewState.held) {
    scheduleTabRangePreviewRefresh('modifier-update', 80);
  }

  return [
    () => {
      endTabPreviewBrushStroke();
      window.removeEventListener('keydown', onGlobalKeyDown, true);
      window.removeEventListener('keyup', onGlobalKeyUp, true);

      const shouldDeferTabRangePreviewStop =
        tabRangePreviewState.held &&
        tabRangePreviewState.ownerKey === tabRangePreviewOwnerKey;

      if (!shouldDeferTabRangePreviewStop) {
        if (tabRangePreviewState.ownerKey === tabRangePreviewOwnerKey) {
          stopTabRangePreview();
        }
      } else {
        window.setTimeout(() => {
          if (
            tabRangePreviewState.bindingToken !== tabRangePreviewBindingToken ||
            tabRangePreviewState.ownerKey !== tabRangePreviewOwnerKey
          ) {
            return;
          }

          tabRangePreviewState.held = false;
          stopTabRangePreview();
        }, 0);
      }

      shiftFloodPreviewDown = false;
      clearPreview();
    },
    ...cleanupEvents,
  ];
}

type RegulatorConnectSelectionPoint = {
  viewportId: PanelComponentKeyType;
  canvas: [number, number];
  ijk: Point3D;
  world: [number, number, number];
  surfaceWorld: [number, number, number];
};

export type RegulatorConnectSelectionState = {
  start: Point3D | null;
  end: Point3D | null;
  ready: boolean;
};

const regulatorConnectSelection: {
  start: RegulatorConnectSelectionPoint | null;
  end: RegulatorConnectSelectionPoint | null;
} = {
  start: null,
  end: null,
};
const regulatorConnectListeners = new Set<
  (state: RegulatorConnectSelectionState) => void
>();

const DICOM_VIEWPORT_IDS = new Set<PanelComponentKeyType>([
  'Axial',
  'Coronal',
  'Sagittal',
]);

function getImageDataVector(
  imageData: any,
  key: 'origin' | 'spacing' | 'direction',
  fallback: number[],
) {
  const getterName =
    key === 'origin'
      ? 'getOrigin'
      : key === 'spacing'
        ? 'getSpacing'
        : 'getDirection';
  return (imageData?.[key] ?? imageData?.[getterName]?.() ?? fallback) as
    | number[]
    | Float32Array
    | Float64Array;
}

function indexToManualSurfaceWorld(
  imageData: any,
  point: Point3D,
): [number, number, number] {
  const origin = getImageDataVector(imageData, 'origin', [0, 0, 0]);
  const spacing = getImageDataVector(imageData, 'spacing', [1, 1, 1]);
  const direction = getImageDataVector(imageData, 'direction', [
    1, 0, 0,
    0, 1, 0,
    0, 0, 1,
  ]);

  const x = -point.x * spacing[0];
  const y = point.y * spacing[1];
  const z = point.z * spacing[2];

  return [
    -origin[0] + direction[0] * x + direction[1] * y + direction[2] * z,
    origin[1] + direction[3] * x + direction[4] * y + direction[5] * z,
    origin[2] + direction[6] * x + direction[7] * y + direction[8] * z,
  ];
}

function getRegulatorConnectSelectionState(): RegulatorConnectSelectionState {
  return {
    start: regulatorConnectSelection.start?.ijk ?? null,
    end: regulatorConnectSelection.end?.ijk ?? null,
    ready: !!regulatorConnectSelection.start && !!regulatorConnectSelection.end,
  };
}

function notifyRegulatorConnectSelectionChanged() {
  const state = getRegulatorConnectSelectionState();
  regulatorConnectListeners.forEach((listener) => listener(state));
  scheduleRender('Surface3D', {
    type: 'regulatorConnectMarkers',
    start: regulatorConnectSelection.start?.surfaceWorld ?? null,
    end: regulatorConnectSelection.end?.surfaceWorld ?? null,
  });
}

export function subscribeRegulatorConnectSelection(
  listener: (state: RegulatorConnectSelectionState) => void,
) {
  regulatorConnectListeners.add(listener);
  listener(getRegulatorConnectSelectionState());

  return () => {
    regulatorConnectListeners.delete(listener);
  };
}

function renderRegulatorConnectOverlay() {
  getEnabledElements().forEach((prop) => {
    prop.viewport.element.parentElement
      ?.querySelector('svg#custom-overlay g#regulator-connect-overlay')
      ?.remove();
  });
}

function updateSegmentationRepresentationsVisibility(
  segmentationId: string,
  colorLUT: Types.ColorLUT | undefined,
) {
  const reps =
    segmentation.state.getSegmentationRepresentationsBySegmentationId(
      segmentationId,
    );

  reps.forEach(({ viewportId, representations }) => {
    representations.forEach((rep) => {
      Object.entries(rep.segments).forEach(([key, segInfo]) => {
        const idx = Number(key);
        if (colorLUT?.[idx]) {
          const visible = colorLUT[idx][3] !== 0;
          segInfo.visible = visible;
          segmentation.config.visibility.setSegmentIndexVisibility(
            viewportId,
            {
              segmentationId,
              type: rep.type,
            },
            idx,
            visible,
          );
        }
      });
    });
  });
}


export function isManualSegVisible(seriesId: string | undefined) {
  if(!seriesId) return false;

  const segmentationIds = [
    `segmentation_${seriesId}_regulator`,
    `segmentation_${seriesId}_manual`,
  ];

  return segmentationIds.some((segmentationId) =>
    segmentation.state
      .getSegmentationRepresentationsBySegmentationId(segmentationId)
      .some(({ representations }) =>
        representations.some((rep) =>
          Object.values(rep.segments).some(
            ({ visible }) => visible,
          ),
        ),
      ),
  );
}

export function toggleManualSegVisible(
  seriesId: string,
  visibility?:boolean
) {
  const segIds = [
    `segmentation_${seriesId}_regulator`,
    `segmentation_${seriesId}_manual`,
  ];

  const nextVisible =
    visibility ??
    !segIds.some((segId) =>
      segmentation.state
        .getSegmentationRepresentationsBySegmentationId(segId)
        .some(({ representations }) =>
          representations.some((rep) =>
            Object.values(rep.segments).some(({ visible }) => visible),
          ),
        ),
    );
  setRegulatorManualSegVisible(seriesId, nextVisible);
  return nextVisible;
}

export function setRegulatorManualSegVisible(
  seriesId: string,
  visible: boolean,
) {
  const segIds = [
    `segmentation_${seriesId}_regulator`,
    `segmentation_${seriesId}_manual`,
    `segmentation_${seriesId}_labelmap`,
  ];

  const renderVpIds = new Set<string>();

  segIds.forEach((segId) => {
    const seg = segmentation.state.getSegmentation(segId);
    if (!seg) return;

    const lutIdx = seg.cachedStats?.colorLUTIndex as
      | number
      | undefined;

    const colorLUT =
      lutIdx !== undefined
        ? segmentation.state.getColorLUT(lutIdx)
        : undefined;

    updateSegmentationRepresentationsVisibility(
      segId,
      visible
        ? colorLUT
        : colorLUT?.map(
            ([r, g, b]) =>
              [r, g, b, 0] as Types.Color,
          ),
    );

    segmentation.state
      .getSegmentationRepresentationsBySegmentationId(segId)
      .forEach(({ viewportId }) => {
        renderVpIds.add(viewportId);
      });
  });

  requestAnimationFrame(() => {
    const engine = getEngine(mainEngineId);

    renderVpIds.forEach((vpId) => {
      engine.getViewport(vpId)?.render();
    });
  });
}

export function clearRegulatorConnectSelection() {
  regulatorConnectSelection.start = null;
  regulatorConnectSelection.end = null;
  renderRegulatorConnectOverlay();
  notifyRegulatorConnectSelectionChanged();
}

export function setRegulatorConnectEvents(params: {
  seriesId: string;
  lowQuality: boolean;
}) {
  const { seriesId, lowQuality } = params;
  setMousePointer('crosshair');
  const manualVolume = getOrCreateManualVolume(seriesId, lowQuality);

  const toPoint = (
    viewportId: PanelComponentKeyType,
    element: HTMLDivElement,
    e: MouseEvent,
  ): RegulatorConnectSelectionPoint | null => {
    const rect = element.getBoundingClientRect();
    const canvas: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
    const world = getCanvasToWorld(viewportId, canvas);
    if (!world || !manualVolume.imageData) return null;

    const ijk = worldToIndex(manualVolume.imageData, world as Types.Point3);
    const [nx, ny, nz] = manualVolume.dimensions as [number, number, number];
    const point: Point3D = {
      x: Math.round(Math.max(0, Math.min(nx - 1, ijk[0]))),
      y: Math.round(Math.max(0, Math.min(ny - 1, ijk[1]))),
      z: Math.round(Math.max(0, Math.min(nz - 1, ijk[2]))),
    };

    return {
      viewportId,
      canvas,
      ijk: point,
      world: world as [number, number, number],
      surfaceWorld: indexToManualSurfaceWorld(manualVolume.imageData, point),
    };
  };

  return getEnabledElements()
    .map((prop) => {
      const viewportId = prop.viewportId as PanelComponentKeyType;
      if (!DICOM_VIEWPORT_IDS.has(viewportId)) return undefined;

      const { element } = prop.viewport;
      const svg =
        prop.viewport.element.parentElement?.querySelector('svg#custom-overlay');
      const brushGroup = svg?.querySelector('g#brush');
      if (svg) svg.setAttribute('visibility', 'visible');
      brushGroup?.setAttribute('visibility', 'hidden');

      function onMouseDown(e: MouseEvent) {
        if (e.buttons !== 1 || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (!hasSegmentationState()) return;

        const point = toPoint(viewportId, element, e);
        if (!point) return;

        e.preventDefault();
        e.stopPropagation();
        (e as any).stopImmediatePropagation?.();

        if (
          !regulatorConnectSelection.start ||
          regulatorConnectSelection.end
        ) {
          regulatorConnectSelection.start = point;
          regulatorConnectSelection.end = null;
        } else {
          regulatorConnectSelection.end = point;
        }

        notifyRegulatorConnectSelectionChanged();
      }

      element.addEventListener('mousedown', onMouseDown, true);
      return () => {
        element.removeEventListener('mousedown', onMouseDown, true);
        brushGroup?.removeAttribute('visibility');
        element.parentElement
          ?.querySelector('svg#custom-overlay g#regulator-connect-overlay')
          ?.remove();
      };
    })
    .filter(Boolean);
}

export function applyRegulatorConnectSelection(params: {
  seriesId: string;
  lowQuality: boolean;
  thickness: number;
  huFilter?:
    | { enabled: false }
    | { enabled: true; min: number; max: number };
}) {
  const { seriesId, lowQuality, thickness, huFilter } = params;
  const { start, end } = regulatorConnectSelection;
  if (!start || !end) return null;
  if (!hasSegmentationState()) return null;

  const manualVolume = getOrCreateManualVolume(seriesId, lowQuality);
  const [width, height, depth] = manualVolume.dimensions as [
    number,
    number,
    number,
  ];
  const scalar = huFilter?.enabled ? getHuData(start.viewportId) : null;
  const expectedVoxelCount = width * height * depth;
  const canUseHuFilter =
    !!huFilter?.enabled &&
    !!scalar &&
    scalar.length === expectedVoxelCount &&
    Number.isFinite(huFilter.min) &&
    Number.isFinite(huFilter.max);

  if (huFilter?.enabled && !canUseHuFilter) {
    console.warn('[Regulator Connect][HU Filter] Ignored because HU data is unavailable or dimension mismatch.', {
      scalarLength: scalar?.length ?? 0,
      expectedVoxelCount,
      huFilter,
    });
  }

  const coords = getThickLineCoords3D(start.ijk, end.ijk, thickness, {
    volume: {
      width,
      height,
      depth,
      ...(canUseHuFilter ? { data: scalar as Int16Array } : {}),
    },
    ...(canUseHuFilter
      ? {
          huMin: Math.min(huFilter.min, huFilter.max),
          huMax: Math.max(huFilter.min, huFilter.max),
        }
      : {}),
    thicknessUnit: 'voxel',
  });
  const linearIndices = coords.map(
    ({ x, y, z }) => (z * height + y) * width + x,
  );
  const axes = getViewportAxes(start.viewportId);
  const centerWorld = getCanvasToWorld(start.viewportId, start.canvas);
  if (!manualVolume.imageData || !axes || !centerWorld) return null;

  beginStroke({
    imageData: manualVolume.imageData,
    plane: {
      centerWorld,
      normalWorld: axes.viewPlaneNormal,
      uWorld: axes.viewRight,
      vWorld: axes.viewUp,
    },
    radiusMM: 0,
    pixelsPerMM: 1,
    segmentIndex: 1,
    flippedThisStroke: linearIndices,
    flood: true,
  });
  endStroke();

  const result = {
    sourceVoxelCount: coords.length,
    start: start.ijk,
    end: end.ijk,
    huFilter: canUseHuFilter
      ? {
          min: Math.min(huFilter.min, huFilter.max),
          max: Math.max(huFilter.min, huFilter.max),
        }
      : null,
  };
  clearRegulatorConnectSelection();
  console.log('[Regulator Connect][Apply]', result);
  return result;
}
// export function changeManualVolumeSegmentColor(seriesId:string,color:Types.Point3){
//   let colorLUT = segmentation.state.getColorLUT(99);
//   if(!colorLUT) return;
//   colorLUT[1] = [...color,255];
//   segmentation.state.addColorLUT(colorLUT,99);
//   getEnabledElements().forEach(({viewportId})=>{
//     segmentation.config.color.setColorLUT(viewportId,`${seriesId}_manual`,99)}
//   )
// }

export function getVolumes(
  keepSeriesId?: string,
){
  console.log(segmentation.state.getSegmentations(),cache.getVolumes());
}

const REGULATOR_COLOR_LUT_INDEX = 100;

function getManualColorLUT(): Types.ColorLUT {
  const colorLUT: Types.ColorLUT = Array.from(
    { length: 256 },
    () => [0, 0, 0, 0] as Types.Color,
  );

  const color = configStore.get().manualColors['2D'] as [number, number, number];
  colorLUT[1] = [...color, 255];
  colorLUT[2] = [255, 245, 0, 255];
  colorLUT[3] = [...color, 255];
  colorLUT[255] = [...color, 255];
  return colorLUT;
}

function getRegulatorManualSegIds(seriesId: string) {
  return [
    `segmentation_${seriesId}_manual`,
    `segmentation_${seriesId}_regulator`,
  ];
}

function getSegmentationColorLUTTargetIndexes(seg: any, colorLUT: Types.ColorLUT) {
  const arrangeBitMask =
    (seg.cachedStats?.arrangeBitMask as Record<number, number> | undefined) ??
    {};
  const targetIndexes = new Set(
    Object.values(arrangeBitMask).map((idx) => Number(idx)),
  );

  if (targetIndexes.size === 0) {
    colorLUT.forEach((lutColor, index) => {
      if ((lutColor?.[3] ?? 0) !== 0) targetIndexes.add(index);
    });
  }

  return targetIndexes;
}

export function getRegulatorManualSegColor(
  seriesId: string | undefined | null,
): [number, number, number] | undefined {
  if (!seriesId) return undefined;

  for (const segId of getRegulatorManualSegIds(seriesId)) {
    const seg = segmentation.state.getSegmentation(segId);
    const lutIdx = seg?.cachedStats?.colorLUTIndex as number | undefined;
    if (!seg || lutIdx === undefined) continue;

    const colorLUT = segmentation.state.getColorLUT(lutIdx);
    if (!colorLUT) continue;

    const targetIndexes = getSegmentationColorLUTTargetIndexes(seg, colorLUT);
    for (const segmentIndex of targetIndexes) {
      const color = colorLUT[segmentIndex];
      if (!color || color[3] === 0) continue;
      return [color[0], color[1], color[2]];
    }
  }

  return undefined;
}


export function updateManualSegStyle(
  seriesId: string | undefined | null,
  mode:'fill'|'outline',
) {
  if (!seriesId) return;
  const segIds = getRegulatorManualSegIds(seriesId);
  const type = ToolEnums.SegmentationRepresentations.Labelmap;

  segIds.forEach((segId) => {
    const seg = segmentation.state.getSegmentation(segId);
    const lutIdx = seg?.cachedStats?.colorLUTIndex as number | undefined;
    if (!seg || lutIdx === undefined) return;

    const colorLUT = segmentation.state.getColorLUT(lutIdx);
    if (!colorLUT) return;

    segmentation.config.style.setStyle(
      {
        type,
        segmentationId: segId,
      },
      mode === 'fill' ? defaultLabelmapStyle: defaultOutlineStyle,
    );
  });


}

export function updateManualSegColor(
  seriesId: string | undefined | null,
  color: [number, number, number],
) {
  if (!seriesId) return;

  const segIds = getRegulatorManualSegIds(seriesId);
  const renderVpIds = new Set<string>();

  segIds.forEach((segId) => {
    const seg = segmentation.state.getSegmentation(segId);
    const lutIdx = seg?.cachedStats?.colorLUTIndex as number | undefined;
    if (!seg || lutIdx === undefined) return;

    const colorLUT = segmentation.state.getColorLUT(lutIdx);
    if (!colorLUT) return;

    const targetIndexes = getSegmentationColorLUTTargetIndexes(seg, colorLUT);

    segmentation.state
      .getSegmentationRepresentationsBySegmentationId(segId)
      .forEach(({ viewportId }) => {
        renderVpIds.add(viewportId);

        targetIndexes.forEach((segmentIndex) => {
          const alpha = colorLUT[segmentIndex]?.[3] ?? 255;
          const nextColor = [...color, alpha] as Types.Color;

          if(segmentIndex === 2) return;
          colorLUT[segmentIndex] = nextColor;
          segmentation.config.color.setSegmentIndexColor(
            viewportId,
            segId,
            segmentIndex,
            nextColor,
          );
        });

        segmentation.config.color.setColorLUT(viewportId, segId, lutIdx);
      });
  });

  requestAnimationFrame(() => {
    const engine = getEngine(mainEngineId);

    renderVpIds.forEach((vpId) => {
      engine.getViewport(vpId)?.render();
    });
  });
}

export function updateRegulatorSegmentationViewport(
  viewportId: PanelComponentKeyType,
  seriesId: string,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as Types.IBaseVolumeViewport;
  if (!vp) {
    console.warn('Viewport not found:', viewportId);
    return false;
  }

  const segmentationId =
    `segmentation_${seriesId}_regulator`;

  const seg = segmentation.state.getSegmentation(segmentationId);
  if (!seg) {
    console.warn(segmentationId, 'regulator segmentation이 없습니다.');
    return false;
  }

  const colorLUTIndex = (seg.cachedStats?.['colorLUTIndex'] ??
    REGULATOR_COLOR_LUT_INDEX) as number;
  const originCamera = utilities.deepClone(vp.getCamera()) as Types.ICamera;

  const type = ToolEnums.SegmentationRepresentations.Labelmap;

  if (
    !segmentation.state.getSegmentationRepresentation(viewportId, {
      segmentationId,
      type,
    })
  ) {
    segmentation.addLabelmapRepresentationToViewport(viewportId, [
      {
        segmentationId,
        type,
        config: { colorLUTOrIndex: colorLUTIndex },
      },
    ]);
  }

  const rep = segmentation.state.getSegmentationRepresentation(viewportId, {
    segmentationId,
    type,
  });
  if (!rep) return false;

  const colorLUT = segmentation.state.getColorLUT(colorLUTIndex);
  Object.keys(rep.segments).forEach((key) => {
    const segmentIndex = Number(key);
    rep.segments[segmentIndex].visible =
      (colorLUT?.[segmentIndex]?.[3] ?? 0) !== 0;
  });

  segmentation.config.color.setColorLUT(
    viewportId,
    segmentationId,
    colorLUTIndex,
  );
  segmentation.config.style.setStyle(
    {
      type,
      segmentationId,
    },
    configStore.get().manualLabelmapStyle === 'fill'
      ? defaultLabelmapStyle
      : defaultOutlineStyle,
  );

  requestAnimationFrame(() => {
    safeSetCamera(vp, originCamera);
    vp.render();
  });

  return true;
}
function removeSegmentationRepresentations(
  segmentationId: string
){
  const segReps =
    segmentation.state.getSegmentationRepresentationsBySegmentationId(
      segmentationId,
    );

  if (!segReps.length) return segReps;

  segReps.forEach(({ viewportId }) => {
    segmentation.removeLabelmapRepresentation(
      viewportId,
      segmentationId,
      false,
    );
  });
  return segReps;
};

export function clearRegulatorSegmentation(
  seriesId: string | undefined,
) {
  if(!seriesId) return;
  const volumeId = `regulator_${seriesId}`;
  const baseSegmentationId = `segmentation_${seriesId}`;
  const segmentationId = `${baseSegmentationId}_regulator`;
  removeVolumeById(volumeId, segmentationId);
}

export function clearSegmentation(
  seriesId: string | undefined,
  type: 'regulator' | 'manual',
) {
  if (!seriesId) return;

  const volumeId =
    type === 'manual'
      ? `${seriesId}_manual`
      : `${type}_${seriesId}`;

  const segmentationId = `segmentation_${seriesId}_${type}`;

  removeVolumeById(volumeId, segmentationId);
}


export function createRegulatorSegmentation(
  seriesId: string,
  data: Uint8Array | Uint16Array | Float32Array,
  originZ:number,
  interpolatedZ:number,
  lowQuality: boolean,
) {
  const volumeId = `regulator_${seriesId}`;
  let referencedVolume: ImageVolume | undefined;

  const baseSegmentationId = `segmentation_${seriesId}`;
  const segmentationId = `${baseSegmentationId}_regulator`;
  const segmentation_manualId = `${baseSegmentationId}_manual`;

  //기존 seg 만든거 불러와서 옵션 값 셋팅
  const prevSegmentationId = lowQuality
    ? `${baseSegmentationId}_labelmapHalf`
    : `${baseSegmentationId}_labelmap`;

  if (!lowQuality) {
    referencedVolume = getVolumeFromSeriesId(seriesId); //cache.getVolume(`ORIGINAL_${seriesId}`);
  } else {
    referencedVolume = getVolumeFromSeriesId(seriesId, true); //cache.getVolume(`PREVIEW_${seriesId}`);
  }

  const oriSegRep = removeSegmentationRepresentations(prevSegmentationId);
  removeSegmentationRepresentations(segmentation_manualId);

  if (!referencedVolume) {
    throw '참고할 볼륨이 없으니 실패, 말이 안되는 상황';
  }

  //regulator 바뀔수도 있자나 매번 새로 만들어야대...
  clearSegmentation(seriesId, 'manual');
  clearSegmentation(seriesId, 'regulator');

  if (!cache.getVolume(volumeId)) {
    const options = defaultCreateLocalLabelmapVolumeOptions(
      8,
      referencedVolume,
    );
    volumeLoader.createLocalLabelmapVolume(
      {
        ...options,
        scalarData: data,
      },
      volumeId,
    );

    const colorLUTIndex = segmentation.state.addColorLUT(
      getManualColorLUT(),
      REGULATOR_COLOR_LUT_INDEX,
    );

    segmentation.addSegmentations([
      {
        segmentationId,
        representation: {
          type: ToolEnums.SegmentationRepresentations.Labelmap,
          data: {
            volumeId: volumeId,
            referencedImageIds: referencedVolume.imageIds,
            referencedVolumeId: referencedVolume.volumeId,
          },
        },
        config: {
          cachedStats: {
            representIndex: 3,
            arrangeBitMask: { 1: 1, 2: 2, 255: 255 },
            coronary: new Set([1, 2, 255]),
            colorLUTIndex,
            originZ,
            interpolatedZ,
            lowQuality,
          },
          segments: {
            [1]: {
              label: 'segment',
              segmentIndex: 1,
              active: true,
            },
            [2]: {
              label: 'segment',
              segmentIndex: 2,
              active: true,
            },
            [255]: {
              label: 'segment',
              segmentIndex: 255,
              active: true,
            },
          },
        },
      },
    ]);

    segmentation.config.style.setStyle(
      {
        type: ToolEnums.SegmentationRepresentations.Labelmap,
        segmentationId,
      },
      configStore.get().manualLabelmapStyle === 'fill'
      ? defaultLabelmapStyle
      : defaultOutlineStyle,
    );
  }

  oriSegRep.forEach(({ viewportId }) => {
    updateRegulatorSegmentationViewport(viewportId as PanelComponentKeyType, seriesId);
  });
}

export function enterOrExitManualSegmentation(
  seriesId: string,
  context: any,
  isEnter: boolean,
  lowQuality: boolean,
  seg: Segmentation,
) {
  const baseSegmentationId = `segmentation_${seriesId}`;
  const editMode = configStore.get().EditMode;
  const isRegulator = editMode === EditNames.regulator;

  const getDefaultSegmentationId = () => {
    if (isRegulator) {
      return `${baseSegmentationId}_regulator`;
    }

    return lowQuality
      ? `${baseSegmentationId}_labelmapHalf`
      : `${baseSegmentationId}_labelmap`;
  };

  const defaultSegmentationId = getDefaultSegmentationId();
  const prevSegmentationId = isEnter
    ? defaultSegmentationId
    : `${baseSegmentationId}_manual`;

  const nextSegmentationId = isEnter
    ? `${baseSegmentationId}_manual`
    : defaultSegmentationId;

  const oriVolumeId = isRegulator
    ? `regulator_${seriesId}`
    : lowQuality
      ? `${seriesId}_labelmapHalf`
      : `${seriesId}_labelmap`;

  if (!cache.getVolume(`${seriesId}_manual`) && !cache.getVolume(oriVolumeId)) {
    console.warn('[Manual Segmentation] Source labelmap volume is not ready yet.', {
      seriesId,
      oriVolumeId,
      isRegulator,
      lowQuality,
      isEnter,
    });
    return;
  }

  let manualVolume: ImageVolume | undefined;
  if (isRegulator) {
    const manualVolumeId = `${seriesId}_manual`;

    const cachedVolume = cache.getVolume(manualVolumeId);

    if (cachedVolume) {
      manualVolume = cachedVolume as ImageVolume;
    } else {
      const labelmapVolume = cache.getVolume(oriVolumeId);

      if (!labelmapVolume) {
        getVolumes();
        console.log(defaultSegmentationId)
        throw 'isRegulator 라벨맵이 없는데 어떻게 오셨죠?';
      }


      manualVolume = volumeLoader.createAndCacheDerivedLabelmapVolume(
        oriVolumeId,
        {
          volumeId: manualVolumeId,
          voxelRepresentation: Enums.VoxelManagerEnum.Volume,
        },
      );
    }
  } else if(isEnter) {
    manualVolume = getOrCreateManualVolume(seriesId, lowQuality);
  }

  // 기존꺼 조사기
  const oriSegRep = removeSegmentationRepresentations(prevSegmentationId);
  // 없어..나일거야 아마..
  if (!oriSegRep.length) return;

  if(!manualVolume) {
    clearSegmentManager();
    return
  };

  // 매누얼 없으면 만들기.
  if (!segmentation.state.getSegmentation(nextSegmentationId)) {
    const manualVolumeId = `${seriesId}_manual`;
    const referenceVolume = cache.getVolume(oriVolumeId)!;

    const colorLUTIndex = editMode
      ? segmentation.state.addColorLUT(
          getManualColorLUT(),
          REGULATOR_COLOR_LUT_INDEX,
        )
      : segmentation.state.addColorLUT(
          [
            [0, 0, 0, 0], //0
            [217, 22, 86, 255], //1
            [255, 219, 88, 255], //2
            [236, 120, 87, 255], //1,2 blend
          ],
          REGULATOR_COLOR_LUT_INDEX,
        );
    segmentation.addSegmentations([
      {
        segmentationId: nextSegmentationId,
        representation: {
          type: ToolEnums.SegmentationRepresentations.Labelmap,
          data: {
            volumeId: manualVolumeId,
            referencedImageIds: referenceVolume.imageIds,
            referencedVolumeId: referenceVolume.volumeId,
          },
        },
        config: {
          cachedStats: {
            representIndex: 3,
            arrangeBitMask: { 1: 1, 2: 2, 3: 3 },
            colorLUTIndex,
          },
          segments: {
            [1]: {
              label: 'segment',
              segmentIndex: 1,
              active: true,
            },
            [2]: {
              label: 'preview',
              segmentIndex: 2,
              active: true,
            },
            [3]: {
              label: 'preview + segmented',
              segmentIndex: 3,
              active: true,
            },
          },
        },
      },
    ]);
  }
  const sr = {
    segmentationId: nextSegmentationId,
    type: ToolEnums.SegmentationRepresentations.Labelmap,
  };

  const colorLUTIndex = segmentation.state.getSegmentation(nextSegmentationId)
    ?.cachedStats['colorLUTIndex'] as number;

  // const engine = getEngine(mainEngineId);
  // console.log('여길 몇번?')
  // 새론거 껴넣기
  oriSegRep.forEach(({ viewportId }) => {
    // const vp = engine.getViewport(viewportId);
    // const handler = () => {
    //   requestAnimationFrame(() => {
    //     if (!originCamera) captureOriginCamera(vp);
    //     safeSetCamera(vp, originCamera);
    //   });
    // };

    segmentation.addLabelmapRepresentationToViewport(viewportId, [sr]);
    segmentation.config.color.setColorLUT(
      viewportId,
      nextSegmentationId,
      colorLUTIndex,
    );
    // vp.element.addEventListener(Enums.Events.DISPLAY_AREA_MODIFIED, handler, {
    //   once: true,
    // });
  });

  // 보였던 스타일대로
  segmentation.config.style.setStyle(
    {
      type: ToolEnums.SegmentationRepresentations.Labelmap,
      segmentationId: nextSegmentationId,
    },
    !isEnter
      ? defaultLabelmapStyle
      : configStore.get().manualLabelmapStyle === 'fill'
        ? defaultLabelmapStyle
        : defaultOutlineStyle,
  );

  if (editMode === EditNames.regulator || editMode === EditNames.lumen) {
    //regulator는 별도로 Segment 만들어서 사용하므로 기존 Segment 사용하지않음
    //따라서 clear를 안하면 처음에 사용했던 Segment 값이 적용됨
    clearSegmentManager();
  }
  // 조건이 몬가 이상한데?
  if (!hasSegmentationState()) {
    //console.time('initializeSegmentManager');

    const activeManualVolume = manualVolume;
    const { setCompleteScalarDataArray, setAtIndex } =
      activeManualVolume.voxelManager!;
    if (!setCompleteScalarDataArray || !setAtIndex) throw '이럴리리';

    function updateElements() {
      activeManualVolume.modified();
      renderEnabledElements();
    }

    const baseSegmentationId = `segmentation_${seriesId}`;
    const segmentationId = `${baseSegmentationId}_regulator`;
    const seg = segmentation.state.getSegmentation(segmentationId);
    const labelmapVolume = isRegulator
      ? cache.getVolume(`regulator_${seriesId}`)
      : getLabelVolumeFromSeriesId(seriesId, lowQuality)!;
    const cachedStats = isRegulator
      ? seg!.cachedStats
      : getLabelmapSegmentationCachedStats(seriesId, lowQuality);
    initializeSegmentManager(
      {
        imageData: labelmapVolume?.imageData,
        labelVolume: labelmapVolume?.voxelManager!
          .getCompleteScalarDataArray!() as Uint8Array,
        cachedStats,
        setCompleteScalarDataArray,
        setAtIndex,
        updateElements,
      },
      context,
    );
  }

  requestAnimationFrame(() => {
    scheduleRender('Surface3D', {
      type: 'showOrHideSegmentation',
      segmentation: seg,
      isShow: !isEnter,
    });
  });
}

export function restoreCamera(
  viewportId: PanelComponentKeyType,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as Types.IBaseVolumeViewport;
  if (!vp) {
    console.log('없는데 어떻게 왔냐?', viewportId);
    return;
  }

  const originCamera = utilities.deepClone(vp.getCamera()) as Types.ICamera;

  const handler = () => {
    // requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // if (isPreview) return;
      if (!originCamera) captureOriginCamera(vp);
      // console.log(originCamera.position,originCamera.focalPoint,originCamera.parallelScale)
      safeSetCamera(vp, originCamera);
    });
    // });
  };
  vp.element.addEventListener(Enums.Events.DISPLAY_AREA_MODIFIED, handler, {
    once: true,
  });
}

export function updateLabelmapViewport(
  viewportId: PanelComponentKeyType,
  seriesId: string,
  requestSegmentation: Partial<Segmentation>,
  isPreview?: boolean,
) {
  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId) as Types.IBaseVolumeViewport;
  if (!vp) {
    console.log('없는데 어떻게 왔냐?', viewportId);
    return;
  }
  const segmentationState = segmentation.state;
  const segmentationId = `segmentation_${seriesId}_${isPreview ? 'labelmapHalf' : 'labelmap'}`;

  const seg = segmentationState.getSegmentation(segmentationId);
  if (!seg) {
    console.warn(segmentationId, '너무 빨라요..');
    return;
  }

  const type = ToolEnums.SegmentationRepresentations.Labelmap;
  const arrangeBitMask = seg.cachedStats['arrangeBitMask'] as Record<
    number,
    number
  >;
  const colorLUTIndex = seg.cachedStats['colorLUTIndex'] as number;
  const prevRepresentIndex = seg.cachedStats['representIndex'] as number;
  const originCamera = utilities.deepClone(vp.getCamera()) as Types.ICamera;

  // let rep = segmentationState.getSegmentationRepresentation(viewportId,{segmentationId,type});

  // if(!rep) {
  segmentation.addLabelmapRepresentationToViewport(viewportId, [
    { segmentationId, type, config: { colorLUTOrIndex: colorLUTIndex } },
  ]);
  // console.log(colorLUTIndex,viewportId,segmentationId,type)
  const rep = segmentationState.getSegmentationRepresentation(viewportId, {
    segmentationId,
    type,
  });
  // return;
  // }

  if (!rep) {
    throw '디버깅이 아니고서야 이럴수가 있을까?2';
  }

  // < 1ms
  const visibleSegmentations = Object.fromEntries(
    Object.entries(requestSegmentation)
      // .filter(([_,value])=>(value as SegmentationProps).visibility)
      .map(([key, value]) => [
        BitMaskMap[key],
        (value as SegmentationProps).color,
      ]),
  );

  let representIndex = 0;
  Object.keys(visibleSegmentations).forEach(
    (key) => (representIndex |= key ? Number(key) : 0),
  );

  let colorLUT = segmentationState.getColorLUT(colorLUTIndex)!;

  // 먼저 도달한 녀석이 처리하고 끝내버리겠지요, 라벨맵은
  if (prevRepresentIndex !== representIndex || colorLUT === undefined) {
    if (colorLUT) segmentationState.removeColorLUT(colorLUTIndex);
    colorLUT = createColorLUT({
      arrangeBitMask,
      segmentations: requestSegmentation,
      representIndex,
    });
    //console.log(colorLUT);
    segmentationState.addColorLUT(colorLUT, colorLUTIndex);
    seg.cachedStats['representIndex'] = representIndex;
    segmentation.config.style.setStyle(
      {
        type,
        segmentationId,
      },
      defaultLabelmapStyle,
    );
  }

  Object.entries(rep.segments).forEach(([key, { visible }]) => {
    rep.segments[Number(key)].visible = colorLUT[Number(key)][3] !== 0;
  });

  segmentation.config.color.setColorLUT(
    viewportId,
    segmentationId,
    colorLUTIndex,
  );

  //정녕 이 방법뿐인가
  //https://github.com/cornerstonejs/cornerstone3D/blob/fe67167a11a1e8d06c3af39e4729ae3dfd044270/packages/core/src/RenderingEngine/Viewport.ts#L648
  // vp.element.addEventListener(
  //   Enums.Events.DISPLAY_AREA_MODIFIED,
  //   (e) => {
  //     requestAnimationFrame(() => {
  //       // 여기서 깜빡이는게 보기싫어서 프리뷰는 패쓰
  //       if (!isPreview) {
  //         vp.setCamera(originCamera);
  //       }
  //     });
  //   },
  //   { once: true },
  // );

  // 시리즈 변경 및 메뉴 변경을 빠르게하거나 했을때 위에서 자꾸 에러남
  // 예외처리 넣어봤는데 잘되는거같은데 확인은 필요
  // requestAnimationFrame 한 번으로 해야 label맵 zoom이 안튕김
  const handler = () => {
    // requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // if (isPreview) return;
      if (!originCamera) captureOriginCamera(vp);
      // console.log(originCamera.position,originCamera.focalPoint,originCamera.parallelScale)
      safeSetCamera(vp, originCamera);
    });
    // });
  };


  vp.element.addEventListener(Enums.Events.DISPLAY_AREA_MODIFIED, handler, {
    once: true,
  });
}

// 위 에러를 해결하기 위한 함수들~
function safeSetCamera(vp: any, cam: any) {
  if (!vp) return;
  const el = vp.element ?? vp.getElement?.();
  if (!el || !el.isConnected) return;

  const cur = (() => {
    try {
      return vp.getCamera?.();
    } catch {
      return null;
    }
  })();
  if (!cur) return;

  const target = cam ?? cur;
  const { position, focalPoint, viewUp } = target || {};
  if (!position || !focalPoint || !viewUp) return;
  // const { position, focalPoint, viewUp, viewPlaneNormal } = target || {};
  // if (!position || !focalPoint || !viewUp || !viewPlaneNormal) return;

  // const eyeFocalDist = Math.min(vec3.distance(focalPoint, position),1.0);

  try {
    vp.setCamera({
      position: [...position],
      // position: (viewPlaneNormal as vec3).map((v, i) => focalPoint[i] + v * eyeFocalDist),
      focalPoint: [...focalPoint],
      viewUp: [...viewUp],
      parallelProjection: target.parallelProjection ?? cur.parallelProjection,
      parallelScale: target.parallelScale ?? cur.parallelScale,
    });
  } catch (e) {}
}

function cloneCam(cam: any) {
  if (!cam) return cam;
  const { position, focalPoint, viewUp, ...rest } = cam;
  return {
    position: position ? [...position] : position,
    focalPoint: focalPoint ? [...focalPoint] : focalPoint,
    viewUp: viewUp ? [...viewUp] : viewUp,
    ...rest,
  };
}

let originCamera: any = null;

function captureOriginCamera(vp: any) {
  try {
    const cam = vp?.getCamera?.();
    if (cam && cam.position && cam.focalPoint && cam.viewUp) {
      originCamera = cloneCam(cam);
    }
  } catch {}
}
// ~~~~~위 에러를 해결하기 위한 함수들~

// 이 함수는 seriesId가 바뀐 상태의 Surface3D마다 들어온다.
// 이게 지금 하트만 타겟하는것이 상당한 문제
export async function checkLabelmap<T extends keyof Segmentation>(
  seriesId: string,
  lowQuality?: boolean,
  // requestSegmentation : Pick<Segmentation,T>,
  // setterLAWT?: ()=>void,
) {
  let referencedVolume: ImageVolume | undefined;
  let volumeId: string | undefined;
  let segmentationId: string | undefined;
  let poolId: string | undefined;
  if (!lowQuality) {
    referencedVolume = getVolumeFromSeriesId(seriesId); //cache.getVolume(`ORIGINAL_${seriesId}`);
    volumeId = `${seriesId}_labelmap`;
    segmentationId = `segmentation_${seriesId}_labelmap`;
    poolId = `${seriesId}.labelmap`;
  } else {
    referencedVolume = getVolumeFromSeriesId(seriesId, true); //cache.getVolume(`PREVIEW_${seriesId}`);
    volumeId = `${seriesId}_labelmapHalf`;
    segmentationId = `segmentation_${seriesId}_labelmapHalf`;
    poolId = `${seriesId}.labelmapHalf`;
  }

  if (!referencedVolume) {
    throw '참고할 볼륨이 없으니 실패, 말이 안되는 상황';
  }

  const type = ToolEnums.SegmentationRepresentations.Labelmap;

  // 라벨맵 때리기
  const seg = segmentation.state.getSegmentation(segmentationId);
  {
    if (!seg) {
      if (!immediateRawBufferPool[poolId]) throw '이럴순 없어요......';
      // 기존캐시 읎으면
      if (!cache.getVolume(volumeId)) {
        const { imageData } = await immediateRawBufferPool[poolId]!;
        const scalarData = new Uint8Array(imageData);
        const options = defaultCreateLocalLabelmapVolumeOptions(
          8,
          referencedVolume,
        );
        volumeLoader.createLocalLabelmapVolume(
          {
            ...options,
            scalarData,
          },
          volumeId,
        );
      }

      // 찾아낸 모든 인덱스들 active 시킴. 일단.
      let segments: { [segmentIndex: number]: Partial<ToolTypes.Segment> } = {};
      Object.entries(arrangeBitMaskMap[seriesId]).forEach(([key, value]) => {
        if (value != 0) {
          segments[value] = {
            segmentIndex: value,
            label: `Segment ${value}`,
            active: true,
            locked: false,
            cachedStats: {
              originBit: Number(key),
            },
          };
        }
      });
      const colorLUTIndex = segmentation.state.addColorLUT([]);

      const params = {
        segmentationId,
        representation: {
          type,
          data: {
            volumeId,
            referencedVolumeId: referencedVolume.volumeId,
            referencedImageIds: referencedVolume.imageIds,
          },
        },
        config: {
          segments,
          label: seriesId,
          cachedStats: {
            ...bitMaskSetMap[seriesId],
            arrangeBitMask: arrangeBitMaskMap[seriesId],
            colorLUTIndex,
            representIndex: 0,
            lowQuality,
          },
        },
      } as SegmentationPublicInput;
      //등록, cachedStats
      segmentation.addSegmentations([params], true);

      immediateRawBufferPool[poolId] = undefined;
    }
    // const seg = segmentation.state.getSegmentation(segmentationId);
  }
  // 이제는 내가 세팅 안합니더.
  // 여기까지 왔으믄 segmentationId로 DICOM패널 스스로 찾을 수 있을거임.
  // 즉, 스케쥴랜더 쏴줘버리기.
  // 3D로 역시나 그렇게 하고싶다.
  // 근데, surface는 만들지 않을거임. 너무 오래걸림.
  // 지금은 draco를 기다려보고, 우선은 라벨맵 갱신만 요청하기.
  // );
  // if(!isPreview){

  const currentSeg = segmentation.state.getSegmentation(segmentationId);
  return !!currentSeg && !!cache.getVolume(volumeId);
  // if (!isPreview) {
  //   requestAnimationFrame(() => {
  //     scheduleRender('Axial', { type: 'updateLabelmap', seriesId });
  //     scheduleRender('Coronal', { type: 'updateLabelmap', seriesId });
  //     scheduleRender('Sagittal', { type: 'updateLabelmap', seriesId });
  //     scheduleRender('VR', { type: 'updateLabelmap' });
  //   });

  //   console.log('아 저 근데 쏴요?');
  // }
}

function removeVolumeById(volumeId: string, segmentationId?: string) {
  if (segmentationId) {
    getEnabledElements().forEach(({ viewportId }) => {
      segmentation.removeLabelmapRepresentation(
        viewportId,
        segmentationId,
        true,
      );
    });

    segmentation.removeSegmentation(segmentationId);
  }

  const volume = cache.getVolumes().find((v) => v.volumeId === volumeId);
  if (!volume) return;

  volume.imageIds.forEach((imageId) => {
    cache.removeImageLoadObject(imageId, { force: true });
  });

  cache.removeVolumeLoadObject(volumeId);
}

// export function updateBranches(seriesId: string, buffers: ArrayBuffer[]) {
//   if (!buffers) return;
//   const volumeId = `${seriesId}.branches`;
//   const scalarData = new Uint8Array(buffers[1]);
//   removeVolumeById(volumeId);
//   const originalVolume = getVolumeFromSeriesId(seriesId); //cache.getVolume(`ORIGINAL_${seriesId}`);
//   if (!originalVolume) {
//     throw '오리 없는데 Branches 시도';
//   }

//   const volumeOptions = defaultCreateLocalLabelmapVolumeOptions(
//     8,
//     originalVolume,
//   );
//   const newMeta = {
//     ...volumeOptions,
//     metadata: {
//       ...volumeOptions.metadata,
//       BitsAllocated: 8,
//       BitsStored: 8,
//       HighBit: 7,
//       PixelRepresentation: 1,
//       SamplesPerPixel: 1,
//     },
//     scalarData,
//     targetBuffer: { type: 'Uint8Array' },
//   } as Types.LocalVolumeOptions;
//   const volume = volumeLoader.createLocalVolume(volumeId, newMeta);
//   volume.load();
// }

// export async function loadBranches(seriesId: string) {
//   downloadQueue.enqueue(async () => {
//     const volumeId = `${seriesId}.branches`;
//     const testLAWTVolume = cache.getVolume(volumeId);
//     if (!testLAWTVolume) {
//       const { renderingQuality } = configStore.get().generalSetting;
//       const originalVolume = getVolumeFromSeriesId(
//         seriesId,
//         renderingQuality !== 'high',
//       ); //cache.getVolume(`ORIGINAL_${seriesId}`);
//       if (!originalVolume) {
//         throw '오리 없는데 Branches 시도';
//       }
//       const result = await immediateRawBufferPool[`${seriesId}.branches`];
//       if (!result) {
//         throw 'branches 없는데 시도하는 상황';
//       }
//       const { imageData } = result as DownloadAndDecompressType;
//       const scalarData = new Uint8Array(imageData);
//       const volumeOptions = defaultCreateLocalLabelmapVolumeOptions(
//         8,
//         originalVolume,
//       );
//       const newMeta = {
//         ...volumeOptions,
//         metadata: {
//           ...volumeOptions.metadata,
//           BitsAllocated: 8,
//           BitsStored: 8,
//           HighBit: 7,
//           PixelRepresentation: 1,
//           SamplesPerPixel: 1,
//         },
//         scalarData,
//         targetBuffer: { type: 'Uint8Array' },
//       } as Types.LocalVolumeOptions;

//       const volume = volumeLoader.createLocalVolume(volumeId, newMeta);
//       volume.load();
//       immediateRawBufferPool[`${seriesId}.branches`] = undefined;
//     }
//   });
// }

// export async function updateLabelSegmentationConfig(
//   key : SegmentationControllerType,
//   configType: keyof SegmentationProps,
//   value : SegmentationProps[keyof SegmentationProps]
// ){
//   console.log('설마 여길..')
//   throw "아직"
//   // const enableElements = getEnabledElements();
//   // const DICOMPanels : PanelComponentKeyType[] = ['Axial','Coronal','Sagittal'];
//   const type = ToolEnums.SegmentationRepresentations.Labelmap;
//   const segmentIndex = 255;

//   getEnabledElements().forEach(async ({viewportId})=>{
//     const viewportAllSegmentation = segmentation.state.getViewportSegmentations(viewportId,type)
//     const targets = viewportAllSegmentation.filter((seg)=>seg.segments[segmentIndex].active && seg.label == key as string);
//     // 무조건 하나일수밖에 없음.하지만 필터는 배열반환
//     targets.forEach(async (target)=>{
//       const {segmentationId} = target;
//       switch(configType){
//       case "color":
//         const prevColor4 = segmentation.config.color.getSegmentIndexColor(viewportId, segmentationId,segmentIndex);
//         const color = (value as SegmentationProps['color']).concat(prevColor4[3]) as Types.Color;
//         updateLabelSegmentationForEnableViewport(false,{viewportId,segmentationId,segmentIndex,color})
//         // segmentation.config.color.setSegmentIndexColor(viewportId, segmentationId,segmentIndex,nextColor4);
//         break;
//       case "opacity":
//         const style = segmentation.config.style.getStyle({
//           segmentationId,
//           type,
//           segmentIndex,
//           viewportId
//         })

//         style.fillAlpha = value as SegmentationProps['opacity'];
//         // style.outlineOpacity = value as SegmentationProps['opacity']
//         updateLabelSegmentationForEnableViewport(false,{viewportId,segmentationId,segmentIndex,style});
//         break;
//       case "visibility":
//         const visibility = value as SegmentationProps['visibility']
//         updateLabelSegmentationForEnableViewport(false,{viewportId,segmentationId,segmentIndex,visibility});
//         break;
//     }
//     })
//   })
// }

export function coordsOfSeedLabel(
  labels: Uint32Array,
  size: [number, number, number], // [cols, rows, slices]
  seedIndex: number,
): Vec3[] {
  console.time('label list');
  const [cols, rows, slices] = size;
  const seedLabel = labels[seedIndex];
  if (seedLabel === 0) return []; // 배경이면 빈 배열

  const wh = cols * rows;
  const out: Vec3[] = [];

  for (let idx = 0; idx < labels.length; idx++) {
    if (labels[idx] !== seedLabel) continue;

    // idx -> (x,y,z)
    const z = (idx / wh) | 0; // Math.floor(idx / wh)
    const rest = idx - z * wh;
    const y = (rest / cols) | 0;
    const x = rest - y * cols;

    out.push([x, y, z]);
  }

  console.timeEnd('label list');
  return out;
}

export function getRequestSegmentation<K extends keyof Segmentation>(
  analysisType: AnalysisType,
  segmentation: Segmentation,
): Pick<Segmentation, K> {
  const keys = getSegmentationAnalysisType(analysisType) as K[];
  const result = {} as Pick<Segmentation, K>;
  for (const key of keys) {
    result[key] = segmentation[key] as Segmentation[K]; // 타입 보장됨
  }
  return result;
}

export function enableShiftLiveFlood(viewportId: PanelComponentKeyType) {
  const enabled = getEnabledElementByViewportId(viewportId);
  if (!enabled) return () => {};
  const element = enabled.viewport.element as HTMLDivElement;
  const vp = enabled.viewport as Types.IVolumeViewport;

  let shiftDown = false;
  let inside = false;
  let rafFlag = false;
  let lastClient: { x: number; y: number } | null = null;

  const clearOverlay = () => {
    const pack = getOrCreatePreviewCanvas(viewportId);
    if (pack) {
      const { ctx, canvas } = pack;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const computeAndDraw = () => {
    rafFlag = false;
    if (!shiftDown || !inside || !lastClient) return;

    const info = getVolumeWorldPointInfo(
      viewportId,
      lastClient.x,
      lastClient.y,
    );
    if (!info?.position || typeof info.value !== 'number') return;

    const vp = getViewport(viewportId) as Types.IVolumeViewport;
    const imageData = vp.getImageData();
    if (!imageData) return;

    const scalar = getHuData(viewportId);
    if (!scalar) return;

    const cam = vp.getCamera();
    const seedHU = info.value!;
    const lo = seedHU - 200;
    const hi = seedHU + 200;

    const cs = (vp as any).canvas as HTMLCanvasElement;
    const rect = cs.getBoundingClientRect();

    const seedCanvas = {
      x: Math.round((lastClient.x - rect.left) * (cs.width / rect.width)),
      y: Math.round((lastClient.y - rect.top) * (cs.height / rect.height)),
    };

    console.time('floodfill2D');
    const mul = floodFill2D_Multi(vp, scalar, seedCanvas, lo, hi);
    console.timeEnd('floodfill2D');
    console.log('floodfill2D length', mul.length);

    // const seedX = Math.round(
    //   (lastClient.x - rect.left) * (cs.width / rect.width),
    // );
    // const seedY = Math.round(
    //   (lastClient.y - rect.top) * (cs.height / rect.height),
    // );

    // if (!isAxisAligned(cam)) {
    //   const coordsCanvas = floodFill2DCanvasOblique(vp, scalar, lo, hi, {
    //     x: seedX,
    //     y: seedY,
    //   });

    //   console.log('oblique ori', coordsCanvas.length);

    //   drawPreviewOnCanvasPixels(
    //     viewportId,
    //     coordsCanvas,
    //     'rgba(255, 80, 80, 0.45)',
    //   );
    //   return;
    // }

    // const [vi, vj, vk] = info.position as [number, number, number];

    // let plane: Plane;
    // let seedA = 0,
    //   seedB = 0,
    //   fixed = 0;
    // const [cols, rows, slices] = imageData.dimensions as [
    //   number,
    //   number,
    //   number,
    // ];

    // if (viewportId === 'Axial') {
    //   plane = 'Axial';
    //   seedA = vi;
    //   seedB = vj;
    //   fixed = vk;
    // } else if (viewportId === 'Coronal') {
    //   plane = 'Coronal';
    //   seedA = vi;
    //   seedB = vk;
    //   fixed = vj;
    // } else {
    //   plane = 'Sagittal';
    //   seedA = vj;
    //   seedB = vk;
    //   fixed = vi;
    // }

    // // 3D floodfill 테스트용
    // if (false) {
    //   const coords3Dmask = floodFill3D_mask(
    //     scalar,
    //     [cols, rows, slices],
    //     seedA,
    //     seedB,
    //     fixed,
    //     lo,
    //     hi,
    //   );

    //   const coords3Dmaskfast = floodFill3D_mask_fast(
    //     scalar,
    //     [cols, rows, slices],
    //     seedA,
    //     seedB,
    //     fixed,
    //     lo,
    //     hi,
    //   );
    // }

    // const coords2D = floodFill2D(
    //   scalar,
    //   [cols, rows, slices],
    //   Math.round(seedA),
    //   Math.round(seedB),
    //   Math.round(fixed),
    //   plane,
    //   lo,
    //   hi,
    //   false,
    // );

    // console.log('ori', coords2D.length);

    // drawPreview2DPlane(
    //   viewportId,
    //   plane,
    //   coords2D,
    //   Math.round(fixed),
    //   'rgba(255, 80, 80, 0.45)',
    // );
  };

  const schedule = () => {
    if (rafFlag) return;
    rafFlag = true;
    requestAnimationFrame(computeAndDraw);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      const wasDown = shiftDown;
      shiftDown = true;
      if (!wasDown && inside && lastClient) {
        schedule(); // 누르는 순간도 한 번 갱신
      }
    }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      shiftDown = false;
      clearOverlay(); // 떼면 미리보기 지움
    }
  };

  const onPointerEnter = () => {
    inside = true;
    if (shiftDown) schedule();
  };
  const onPointerLeave = () => {
    inside = false;
    clearOverlay();
  };
  const onPointerMove = (e: PointerEvent) => {
    lastClient = { x: e.clientX, y: e.clientY };
    if (shiftDown && inside) schedule();
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  element.addEventListener('pointerenter', onPointerEnter);
  element.addEventListener('pointerleave', onPointerLeave);
  element.addEventListener('pointermove', onPointerMove);

  // 해제 함수 반환 (기존 K-콘솔과 동일 패턴)
  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    element.removeEventListener('pointerenter', onPointerEnter);
    element.removeEventListener('pointerleave', onPointerLeave);
    element.removeEventListener('pointermove', onPointerMove);
    clearOverlay();
  };
}

export function floodFill3D_mask(
  scalar: Int16Array,
  dims: [number, number, number], // [cols, rows, slices]
  seedX: number,
  seedY: number,
  seedZ: number,
  lo: number,
  hi: number,
): Uint8Array {
  const [cols, rows, slices] = dims;
  const strideXY = cols * rows;
  const volSize = strideXY * slices;

  // 0) 결과(out) 마스크
  const out = new Uint8Array(volSize); // 0으로 초기화

  // 1) threshold (0/1) 마스크
  function bitsetToMask(bs: Uint8Array, n: number): Uint8Array {
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      out[i] = (bs[i >> 3] >> (i & 7)) & 1;
    }
    return out;
  }

  // const maskFromBitset = buildThresholdBitset(scalar, lo, hi);
  // const thr = bitsetToMask(maskFromBitset, volSize);
  const thr = new Uint8Array(volSize);
  for (let i = 0; i < volSize; i++) {
    const v = scalar[i];
    // (lo..hi) 안이면 1
    thr[i] = v >= lo && v <= hi ? 1 : 0;
  }

  // 2) 시드 검사
  if (
    seedX < 0 ||
    seedY < 0 ||
    seedZ < 0 ||
    seedX >= cols ||
    seedY >= rows ||
    seedZ >= slices
  ) {
    return out;
  }
  const seedIdx = seedZ * strideXY + seedY * cols + seedX;
  if (thr[seedIdx] === 0) {
    // 시드가 임계범위 밖 → 빈 결과
    return out;
  }

  console.time('mask');

  // 3) 방문표시: thr를 워킹 버퍼로 재활용(1→2로 방문 마킹)
  //    (thr를 보존해야 한다면 여기서 복사하면 됨. 지금은 심플하게 바로 재활용)
  thr[seedIdx] = 2;
  out[seedIdx] = 1;

  // 4) 큐(링 버퍼): 선형 인덱스만 저장
  const QN = volSize;
  const q = new Int32Array(QN);
  let head = 0,
    tail = 0;
  const push = (idx: number) => {
    q[tail] = idx;
    tail = tail + 1 === QN ? 0 : tail + 1;
  };
  const pop = (): number => {
    const v = q[head];
    head = head + 1 === QN ? 0 : head + 1;
    return v;
  };
  const isEmpty = () => head === tail;

  push(seedIdx);

  // 5) BFS (6-이웃)
  const lastX = cols - 1;

  while (!isEmpty()) {
    const idx = pop();

    // 슬라이스 내 오프셋 / x좌표 (경계 체크용)
    // 최소 계산을 위해 현재 픽셀당 두 번의 나머지/나눗셈 허용
    const sliceLocal = idx % strideXY; // 0..strideXY-1
    const x = sliceLocal % cols; // 0..cols-1

    // 이웃 인덱스
    const idxPX = idx + 1;
    const idxMX = idx - 1;
    const idxPY = idx + cols;
    const idxMY = idx - cols;
    const idxPZ = idx + strideXY;
    const idxMZ = idx - strideXY;

    // +x (같은 행에서만)
    if (x < lastX && thr[idxPX] === 1) {
      thr[idxPX] = 2;
      out[idxPX] = 1;
      push(idxPX);
    }
    // -x
    if (x > 0 && thr[idxMX] === 1) {
      thr[idxMX] = 2;
      out[idxMX] = 1;
      push(idxMX);
    }
    // +y (슬라이스 내 오프셋이 아래 행으로 가능할 때)
    if (sliceLocal < strideXY - cols && thr[idxPY] === 1) {
      thr[idxPY] = 2;
      out[idxPY] = 1;
      push(idxPY);
    }
    // -y
    if (sliceLocal >= cols && thr[idxMY] === 1) {
      thr[idxMY] = 2;
      out[idxMY] = 1;
      push(idxMY);
    }
    // +z (전체 범위만 확인하면 됨)
    if (idxPZ < volSize && thr[idxPZ] === 1) {
      thr[idxPZ] = 2;
      out[idxPZ] = 1;
      push(idxPZ);
    }
    // -z
    if (idxMZ >= 0 && thr[idxMZ] === 1) {
      thr[idxMZ] = 2;
      out[idxMZ] = 1;
      push(idxMZ);
    }
  }
  console.timeEnd('mask');
  return out; // 0/1 : 연결된 컴포넌트
}

export function floodFill3D_mask_fast(
  scalar: Int16Array,
  dims: [number, number, number], // [cols, rows, slices]
  seedX: number,
  seedY: number,
  seedZ: number,
  lo: number,
  hi: number,
): Uint8Array {
  function allocBitset(n: number): Uint8Array {
    return new Uint8Array((n + 7) >> 3);
  }
  function getBit(bs: Uint8Array, i: number): Boolean {
    return Boolean((bs[i >> 3] >> (i & 7)) & (1 as 0 | 1));
  }
  function setBit(bs: Uint8Array, i: number) {
    bs[i >> 3] |= 1 << (i & 7);
  }
  function clearBit(bs: Uint8Array, i: number) {
    bs[i >> 3] &= ~(1 << (i & 7));
  }

  const [cols, rows, slices] = dims;
  const strideXY = cols * rows;
  const volSize = strideXY * slices;

  const out = new Uint8Array(volSize); // 결과(0/1)

  // 1) threshold를 1비트로 압축
  const thr = allocBitset(volSize);
  for (let i = 0; i < volSize; i++) {
    const v = scalar[i];
    if (v >= lo && v <= hi) setBit(thr, i);
  }

  // 2) 시드 검사
  if (
    seedX < 0 ||
    seedY < 0 ||
    seedZ < 0 ||
    seedX >= cols ||
    seedY >= rows ||
    seedZ >= slices
  )
    return out;

  const seedIdx = seedZ * strideXY + seedY * cols + seedX;
  if (!getBit(thr, seedIdx)) return out;

  console.time('mask_fast');

  // 3) thr비트를 방문표시로 재활용(1=eligible, 0=visited/불가)
  clearBit(thr, seedIdx);
  out[seedIdx] = 1;

  // 4) 링버퍼 큐: idx + x + sliceLocal 를 병렬 배열로 저장해 % 최소화
  const QN = volSize; // upper bound
  const qIdx = new Int32Array(QN);
  const qX = new Int32Array(QN);
  const qSL = new Int32Array(QN); // sliceLocal = idx % strideXY

  let head = 0,
    tail = 0;
  const push = (idx: number, x: number, sliceLocal: number) => {
    qIdx[tail] = idx;
    qX[tail] = x;
    qSL[tail] = sliceLocal;
    tail = tail + 1 === QN ? 0 : tail + 1;
  };
  const pop = () => {
    const i = qIdx[head];
    const x = qX[head];
    const sl = qSL[head];
    head = head + 1 === QN ? 0 : head + 1;
    return { i, x, sl };
  };
  const empty = () => head === tail;

  // 시드의 x, sliceLocal(= idx % strideXY) 미리 계산
  const seedSL = seedIdx - Math.trunc(seedIdx / strideXY) * strideXY; // faster than %
  push(seedIdx, seedX, seedSL);

  const lastX = cols - 1;
  const maxSL_NoWrapDown = strideXY - cols; // sl < strideXY - cols => +y 가능

  // 5) BFS(6-이웃), thr 비트로 방문 제어
  while (!empty()) {
    const { i, x, sl } = pop();

    // 이웃 인덱스
    const iPX = i + 1;
    const iMX = i - 1;
    const iPY = i + cols;
    const iMY = i - cols;
    const iPZ = i + strideXY;
    const iMZ = i - strideXY;

    // +x
    if (x < lastX && getBit(thr, iPX)) {
      clearBit(thr, iPX);
      out[iPX] = 1;
      push(iPX, x + 1, sl + 1); // 같은 슬라이스 내에서 +1
    }
    // -x
    if (x > 0 && getBit(thr, iMX)) {
      clearBit(thr, iMX);
      out[iMX] = 1;
      push(iMX, x - 1, sl - 1);
    }
    // +y (다음 행으로 내려갈 수 있는지: sl < strideXY - cols)
    if (sl < maxSL_NoWrapDown && getBit(thr, iPY)) {
      clearBit(thr, iPY);
      out[iPY] = 1;
      // x는 동일, sliceLocal은 +cols
      push(iPY, x, sl + cols);
    }
    // -y (이전 행으로 갈 수 있는지: sl >= cols)
    if (sl >= cols && getBit(thr, iMY)) {
      clearBit(thr, iMY);
      out[iMY] = 1;
      push(iMY, x, sl - cols);
    }
    // +z (슬라이스 위)
    if (iPZ < volSize && getBit(thr, iPZ)) {
      clearBit(thr, iPZ);
      out[iPZ] = 1;
      // z만 바뀌므로 x는 동일, sliceLocal 동일
      push(iPZ, x, sl);
    }
    // -z (슬라이스 아래)
    if (iMZ >= 0 && getBit(thr, iMZ)) {
      clearBit(thr, iMZ);
      out[iMZ] = 1;
      push(iMZ, x, sl);
    }
  }

  console.timeEnd('mask_fast');
  return out;
}

function floodFill3D_basic(
  scalar: Int16Array,
  dims: [number, number, number], // [cols, rows, slices]
  seedX: number,
  seedY: number,
  seedZ: number,
  lo: number,
  hi: number,
  maxVoxels = 10_000_000, // 안전장치
): Array<[number, number, number]> {
  console.time('basic 3d floodfill');
  const [cols, rows, slices] = dims;
  const sliceSize = cols * rows;
  const volSize = sliceSize * slices;

  const idx3D = (x: number, y: number, z: number) =>
    z * sliceSize + y * cols + x;

  const inRange = (x: number, y: number, z: number) => {
    if (x < 0 || y < 0 || z < 0 || x >= cols || y >= rows || z >= slices)
      return false;
    const hu = scalar[idx3D(x, y, z)];
    return hu >= lo && hu <= hi;
  };

  if (!inRange(seedX, seedY, seedZ)) return [];

  const visited = new Uint8Array(volSize);
  const q: Array<[number, number, number]> = [[seedX, seedY, seedZ]];
  visited[idx3D(seedX, seedY, seedZ)] = 1;

  const out: Array<[number, number, number]> = [];

  while (q.length) {
    const [x, y, z] = q.pop()!;
    out.push([x, y, z]);
    if (out.length >= maxVoxels) break;

    // 6-이웃: ±x, ±y, ±z
    const neigh = [
      [x + 1, y, z],
      [x - 1, y, z],
      [x, y + 1, z],
      [x, y - 1, z],
      [x, y, z + 1],
      [x, y, z - 1],
    ] as Array<[number, number, number]>;

    for (const [nx, ny, nz] of neigh) {
      if (
        nx < 0 ||
        ny < 0 ||
        nz < 0 ||
        nx >= cols ||
        ny >= rows ||
        nz >= slices
      )
        continue;
      const vi = idx3D(nx, ny, nz);
      if (visited[vi]) continue;
      if (!inRange(nx, ny, nz)) continue;
      visited[vi] = 1;
      q.push([nx, ny, nz]);
    }
  }

  console.timeEnd('basic 3d floodfill');
  return out;
}

export function getOrCreatePreviewCanvas(viewportId: PanelComponentKeyType) {
  const enabled = getEnabledElementByViewportId(viewportId);
  if (!enabled) return null;

  const parent = enabled.viewport.element as HTMLDivElement; // cornerstone 뷰포트 wrapper div
  // parent는 반드시 position:relative 여야 오버레이가 정확히 겹칩니다.
  const style = window.getComputedStyle(parent);
  if (style.position === 'static') {
    parent.style.position = 'relative';
  }

  let canvas = parent.querySelector(
    'canvas.preview-fill',
  ) as HTMLCanvasElement | null;
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.className = 'preview-fill';
    Object.assign(canvas.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '9999',
    });
    parent.appendChild(canvas); // 맨 위 레이어로
  }

  const vp = enabled.viewport as Types.IVolumeViewport;
  const cs = (vp as any).canvas as HTMLCanvasElement;

  // 내부 해상도(버퍼 크기) 일치
  canvas.width = cs.width;
  canvas.height = cs.height;

  // CSS 크기 일치 (devicePixelRatio 보정은 cornerstone가 이미 cs에 적용함)
  canvas.style.width = cs.style.width || `${cs.clientWidth}px`;
  canvas.style.height = cs.style.height || `${cs.clientHeight}px`;

  const ctx = canvas.getContext('2d')!;
  // 매번 그리기 전에 깨끗하게
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  return { ctx, canvas, enabled };
}

type Plane = 'Axial' | 'Coronal' | 'Sagittal';

export function floodFill2D(
  scalar: Int16Array,
  dims: [number, number, number], // [cols(i), rows(j), slices(k)]
  seedA: number, // in-plane axis #1
  seedB: number, // in-plane axis #2
  fixed: number, // orthogonal fixed index
  plane: Plane,
  lo: number,
  hi: number,
  diagonals = false,
): Array<[number, number]> {
  console.time('2d 최적화');
  const [cols, rows, slices] = dims;

  // ===== 평면별 in-plane 크기와 선형 인덱싱 파라미터 계산 =====
  // idx = base + a*sa + b*sb
  let W = 0,
    H = 0;
  let base = 0,
    sa = 0,
    sb = 0;
  const strideXY = cols * rows;

  if (plane === 'Axial') {
    // (i,j) @ k=fixed
    W = cols;
    H = rows;
    base = (fixed >>> 0) * strideXY;
    sa = 1;
    sb = cols;
  } else if (plane === 'Coronal') {
    // (i,k) @ j=fixed
    W = cols;
    H = slices;
    base = (fixed >>> 0) * cols;
    sa = 1;
    sb = strideXY;
  } else {
    // Sagittal
    // (j,k) @ i=fixed
    W = rows;
    H = slices;
    base = fixed >>> 0;
    sa = cols;
    sb = strideXY;
  }

  // ===== 시드 유효성 검사 =====
  if (seedA < 0 || seedB < 0 || seedA >= W || seedB >= H) {
    return [];
  }
  const seedIdx = (base + seedA * sa + seedB * sb) | 0;
  const seedV = scalar[seedIdx];
  if (seedV < lo || seedV > hi) {
    return [];
  }

  // ===== 방문표시 & 스택/결과 버퍼 =====
  const areaSize = (W * H) | 0; // 이게 자연스러운 상한

  const visited = new Uint8Array(areaSize);

  // 스택: (a,b) 쌍을 별도 배열로 보관
  let stackCap = Math.min(areaSize || 1, 1 << 20);
  let stackA = new Uint32Array(stackCap);
  let stackB = new Uint32Array(stackCap);
  let top = 0;
  const push = (a: number, b: number) => {
    if (top >= stackCap) {
      const nextCap = Math.min(stackCap * 2, areaSize);
      const na = new Uint32Array(nextCap);
      na.set(stackA);
      const nb = new Uint32Array(nextCap);
      nb.set(stackB);
      stackA = na;
      stackB = nb;
      stackCap = nextCap;
    }
    stackA[top] = a >>> 0;
    stackB[top] = b >>> 0;
    top++;
  };
  const pop = () => {
    top--;
    return [stackA[top] | 0, stackB[top] | 0] as [number, number];
  };

  // 결과 버퍼: (a,b) 각각 보관, 마지막에 tuple 배열로 변환
  let outCap = Math.min(areaSize || 1, 1 << 20);
  let outA = new Uint32Array(outCap);
  let outB = new Uint32Array(outCap);
  let outN = 0;
  const emit = (a: number, b: number) => {
    if (outN >= outCap) {
      const nextCap = Math.min(Math.max(outCap * 2, 1024), areaSize);
      const na = new Uint32Array(nextCap);
      na.set(outA);
      const nb = new Uint32Array(nextCap);
      nb.set(outB);
      outA = na;
      outB = nb;
      outCap = nextCap;
    }
    outA[outN] = a >>> 0;
    outB[outN] = b >>> 0;
    outN++;
  };

  const vIdx = (a: number, b: number) => (b * W + a) | 0;

  // 이웃(4/8 연결)
  const neigh4: [number, number][] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  const neigh8: [number, number][] = [
    ...neigh4,
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];
  const neigh = diagonals ? neigh8 : neigh4;

  // 초기화
  visited[vIdx(seedA, seedB)] = 1;
  push(seedA, seedB);

  // ===== 메인 DFS 루프 =====
  while (top > 0) {
    const [a, b] = pop();
    emit(a, b);

    for (let k = 0; k < neigh.length; k++) {
      const na = a + neigh[k][0];
      const nb = b + neigh[k][1];
      if (na < 0 || nb < 0 || na >= W || nb >= H) continue;
      const vi = vIdx(na, nb);
      if (visited[vi]) continue;

      const idx = (base + na * sa + nb * sb) | 0;
      const v = scalar[idx];
      if (v < lo || v > hi) continue;

      visited[vi] = 1;
      push(na, nb);
    }
  }

  console.timeEnd('2d 최적화');
  // ===== 결과 변환 (기존 API 유지) =====
  const res = new Array<[number, number]>(outN);
  for (let i = 0; i < outN; i++) {
    res[i] = [outA[i] | 0, outB[i] | 0];
  }

  return res;
}

export function drawPreview2DPlane(
  viewportId: PanelComponentKeyType, // Axial/Coronal/Sagittal 중 하나
  plane: Plane,
  coords: Array<[number, number]>, // Axial:(i,j) / Coronal:(i,k) / Sagittal:(j,k)
  fixedIndex: number, // Axial:k / Coronal:j / Sagittal:i
  color = 'rgba(255,0,0,0.45)',
) {
  function computeVoxelToCanvasTransformForPlane(
    vp: Types.IVolumeViewport,
    imageData: any,
    plane: 'Axial' | 'Coronal' | 'Sagittal',
    fixed: number,
  ) {
    const origin = Array.from(imageData.origin ?? [0, 0, 0]) as [
      number,
      number,
      number,
    ];
    const spacing = Array.from(imageData.spacing ?? [1, 1, 1]) as [
      number,
      number,
      number,
    ];
    const dirArr = Array.from(
      imageData.direction ?? [1, 0, 0, 0, 1, 0, 0, 0, 1],
    ) as number[];
    const D = dirArr.length === 9 ? dirArr : [1, 0, 0, 0, 1, 0, 0, 0, 1];

    const ijkToWorld = (
      i: number,
      j: number,
      k: number,
    ): [number, number, number] => {
      const x = i * spacing[0],
        y = j * spacing[1],
        z = k * spacing[2];
      return [
        D[0] * x + D[1] * y + D[2] * z + origin[0],
        D[3] * x + D[4] * y + D[5] * z + origin[1],
        D[6] * x + D[7] * y + D[8] * z + origin[2],
      ];
    };

    // 평면별 기준 점/기저 벡터 (보셀축 기준)
    let P00: number[], PI: number[], PJ: number[];
    if (plane === 'Axial') {
      // in-plane: (i,j) @ k=fixed
      P00 = vp.worldToCanvas(ijkToWorld(0, 0, fixed));
      PI = vp.worldToCanvas(ijkToWorld(1, 0, fixed));
      PJ = vp.worldToCanvas(ijkToWorld(0, 1, fixed));
    } else if (plane === 'Coronal') {
      // in-plane: (i,k) @ j=fixed
      P00 = vp.worldToCanvas(ijkToWorld(0, fixed, 0));
      PI = vp.worldToCanvas(ijkToWorld(1, fixed, 0));
      PJ = vp.worldToCanvas(ijkToWorld(0, fixed, 1));
    } else {
      // Sagittal
      // in-plane: (j,k) @ i=fixed
      P00 = vp.worldToCanvas(ijkToWorld(fixed, 0, 0));
      PI = vp.worldToCanvas(ijkToWorld(fixed, 1, 0));
      PJ = vp.worldToCanvas(ijkToWorld(fixed, 0, 1));
    }

    if (!P00 || !PI || !PJ) return null;

    // ✅ 추가 보정 금지: worldToCanvas는 이미 뷰포트 로컬 좌표임
    const C00 = [P00[0], P00[1]];
    const vI = [PI[0] - P00[0], PI[1] - P00[1]];
    const vJ = [PJ[0] - P00[0], PJ[1] - P00[1]];

    return {
      m11: vI[0],
      m12: vI[1],
      m21: vJ[0],
      m22: vJ[1],
      dx: C00[0],
      dy: C00[1],
    };
  }

  const pack = getOrCreatePreviewCanvas(viewportId);
  if (!pack) return;
  const { ctx, enabled } = pack;

  const vp = enabled.viewport as Types.IVolumeViewport;
  const imageData = vp.getImageData();
  if (!imageData) return;

  // 오버레이 해상도 동기화(기존 유지)
  const csCanvas = (vp as any).canvas as HTMLCanvasElement;
  const overlay = ctx.canvas as HTMLCanvasElement;
  if (overlay.width !== csCanvas.width || overlay.height !== csCanvas.height) {
    overlay.width = csCanvas.width;
    overlay.height = csCanvas.height;
    overlay.style.width = csCanvas.style.width || `${csCanvas.clientWidth}px`;
    overlay.style.height =
      csCanvas.style.height || `${csCanvas.clientHeight}px`;
  }

  const T = computeVoxelToCanvasTransformForPlane(
    vp,
    imageData,
    plane,
    fixedIndex,
  );
  if (!T) return;

  ctx.save();
  ctx.setTransform(T.m11, T.m12, T.m21, T.m22, T.dx, T.dy);
  ctx.imageSmoothingEnabled = false;
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = color;

  // scanline 묶기 (첫 번째 좌표축 기준)
  const rows = new Map<number, number[]>();
  for (let k = 0; k < coords.length; k++) {
    const [a, b] = coords[k];
    let arr = rows.get(b); // 같은 b(두번째 축)에서 연속 a를 묶어도 되고, 반대로 해도 됨
    if (!arr) {
      arr = [];
      rows.set(b, arr);
    }
    arr.push(a);
  }

  rows.forEach((as, b) => {
    as.sort((x, y) => x - y);
    let runStart = as[0],
      prev = as[0];
    for (let i = 1; i < as.length; i++) {
      const cur = as[i];
      if (cur === prev + 1) prev = cur;
      else {
        ctx.fillRect(runStart, b, prev - runStart + 1, 1);
        runStart = prev = cur;
      }
    }
    ctx.fillRect(runStart, b, prev - runStart + 1, 1);
  });

  ctx.restore();
}

type Vec3 = [number, number, number];

// 1-1) 카메라가 축정렬인지 판단 (회전 여부 체크)
function isAxisAligned(cam: Types.ICamera) {
  const n = normalize(cam.viewPlaneNormal as Types.Point3);
  const axes: Types.Point3[] = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
    [-1, 0, 0],
    [0, -1, 0],
    [0, 0, -1],
  ];
  const EPS = 1e-3;
  return axes.some(
    (a) =>
      Math.abs(n[0] - a[0]) < EPS &&
      Math.abs(n[1] - a[1]) < EPS &&
      Math.abs(n[2] - a[2]) < EPS,
  );
}

// 1-2) world -> index(IJK) (imageData 기준, vtk 방향/spacing/origin 반영)
function worldToIndex(
  imageData: any,
  world: Types.Point3,
): [number, number, number] {
  const origin = (imageData.origin ??
    imageData.getOrigin?.() ??
    [0, 0, 0]) as [number, number, number];
  const spacing = (imageData.spacing ??
    imageData.getSpacing?.() ??
    [1, 1, 1]) as [number, number, number];
  const direction =
    imageData.direction ?? imageData.getDirection?.() ?? undefined;
  const dirArr = (
    direction?.length === 9
      ? direction
      : [1, 0, 0, 0, 1, 0, 0, 0, 1]
  ) as number[];

  // dir 3x3의 역행렬 (정규직교라면 전치와 동일)
  const R = [
    [dirArr[0], dirArr[1], dirArr[2]],
    [dirArr[3], dirArr[4], dirArr[5]],
    [dirArr[6], dirArr[7], dirArr[8]],
  ];
  const Rt = [
    [R[0][0], R[1][0], R[2][0]],
    [R[0][1], R[1][1], R[2][1]],
    [R[0][2], R[1][2], R[2][2]],
  ];

  const dx = world[0] - origin[0];
  const dy = world[1] - origin[1];
  const dz = world[2] - origin[2];

  const lx = Rt[0][0] * dx + Rt[0][1] * dy + Rt[0][2] * dz;
  const ly = Rt[1][0] * dx + Rt[1][1] * dy + Rt[1][2] * dz;
  const lz = Rt[2][0] * dx + Rt[2][1] * dy + Rt[2][2] * dz;

  return [lx / spacing[0], ly / spacing[1], lz / spacing[2]];
}

// 2) Oblique 전용: 캔버스 픽셀 공간에서 flood fill (HU는 world->index 샘플)
function floodFill2DCanvasOblique(
  vp: Types.IVolumeViewport,
  scalar: Int16Array,
  lo: number,
  hi: number,
  seedCanvas: { x: number; y: number },
): Array<[number, number]> {
  const imageData = vp.getImageData();
  if (!imageData) return [];
  const [cols, rows, slices] = imageData.dimensions as [number, number, number];

  // 오프스크린 캔버스 해상도 사용
  const cs = (vp as any).canvas as HTMLCanvasElement;
  const W = cs.width,
    H = cs.height;

  // 방문/출력 버퍼
  const visited = new Uint8Array(W * H);
  const outA: number[] = [];
  const outB: number[] = [];

  // 좌표 → 인덱스
  const toIdx = (x: number, y: number) => y * W + x;

  // 시드 보정(정수화 & 범위)
  const sx = Math.max(0, Math.min(W - 1, Math.round(seedCanvas.x)));
  const sy = Math.max(0, Math.min(H - 1, Math.round(seedCanvas.y)));

  // 시드에서 HU 검사
  const seedWorld = vp.canvasToWorld([sx, sy]);
  const seedIJK = worldToIndex(imageData, seedWorld as Types.Point3);
  const seedHU = sampleHU_Nearest(scalar, [cols, rows, slices], seedIJK);
  if (seedHU == null || seedHU < lo || seedHU > hi) return [];

  // DFS (4-연결)
  const stackX = new Int32Array(W * H);
  const stackY = new Int32Array(W * H);
  let top = 0;
  stackX[top] = sx;
  stackY[top] = sy;
  top++;
  visited[toIdx(sx, sy)] = 1;

  while (top > 0) {
    top--;
    const x = stackX[top] | 0,
      y = stackY[top] | 0;

    outA.push(x);
    outB.push(y);

    // 이웃
    const neigh = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const;
    for (const [dx, dy] of neigh) {
      const nx = x + dx,
        ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const vi = toIdx(nx, ny);
      if (visited[vi]) continue;

      // 이 픽셀의 HU를 world→ijk→scalar 로 판단
      const w = vp.canvasToWorld([nx, ny]) as Types.Point3;
      const ijk = worldToIndex(imageData, w);
      const hu = sampleHU_Nearest(scalar, [cols, rows, slices], ijk);
      if (hu == null || hu < lo || hu > hi) continue;

      visited[vi] = 1;
      stackX[top] = nx;
      stackY[top] = ny;
      top++;
    }
  }

  // 출력: [canvasX, canvasY]
  const out: Array<[number, number]> = new Array(outA.length);
  for (let i = 0; i < outA.length; i++) out[i] = [outA[i], outB[i]];
  return out;
}

function drawPreviewOnCanvasPixels(
  viewportId: PanelComponentKeyType,
  coordsCanvas: Array<[number, number]>,
  color = 'rgba(255, 80, 80, 0.45)',
) {
  const pack = getOrCreatePreviewCanvas(viewportId);
  if (!pack) return;
  const { ctx, canvas } = pack;

  // 캔버스 픽셀 좌표 그대로 채움
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = color;

  // scanline 묶기(속도)
  const byY = new Map<number, number[]>();
  for (const [x, y] of coordsCanvas) {
    let arr = byY.get(y);
    if (!arr) {
      arr = [];
      byY.set(y, arr);
    }
    arr.push(x);
  }
  byY.forEach((xs, y) => {
    xs.sort((a, b) => a - b);
    let s = xs[0],
      p = xs[0];
    for (let i = 1; i < xs.length; i++) {
      const c = xs[i];
      if (c === p + 1) {
        p = c;
        continue;
      }
      ctx.fillRect(s, y, p - s + 1, 1);
      s = p = c;
    }
    ctx.fillRect(s, y, p - s + 1, 1);
  });

  ctx.restore();
}

// ====== Optional: 최근접 샘플링 유틸 (없으면 아래 구현 그대로 쓰세요) ======
function sampleHU_Nearest(
  scalar: Int16Array,
  dims: [number, number, number], // [cols(i), rows(j), slices(k)]
  ijk: [number, number, number] | number[],
): number | null {
  const [cols, rows, slices] = dims;
  const ii = Math.round(ijk[0]);
  const jj = Math.round(ijk[1]);
  const kk = Math.round(ijk[2]);
  if (ii < 0 || jj < 0 || kk < 0 || ii >= cols || jj >= rows || kk >= slices) {
    return null;
  }
  const idx = kk * (cols * rows) + jj * cols + ii;
  return scalar[idx] as number;
}

/** viewPlaneNormal이 축정렬인지 판정하고 평면을 반환, 아니면 null */
function detectAxisAlignedPlane(
  vp: Types.IVolumeViewport,
  eps = 1e-4,
): Plane | null {
  const cam = vp.getCamera();
  const vpn = cam?.viewPlaneNormal as
    | { x: number; y: number; z: number }
    | number[]
    | undefined;
  if (!vpn) return null;

  const nx = Array.isArray(vpn) ? vpn[0] : vpn.x;
  const ny = Array.isArray(vpn) ? vpn[1] : vpn.y;
  const nz = Array.isArray(vpn) ? vpn[2] : vpn.z;

  const ax = Math.abs(nx),
    ay = Math.abs(ny),
    az = Math.abs(nz);
  // 가장 큰 축을 기준으로 선택하고, 나머지 축이 충분히 작으면 정렬로 간주
  if (az >= ax && az >= ay) {
    if (ax < eps && ay < eps) return 'Axial';
  } else if (ay >= ax && ay >= az) {
    if (ax < eps && az < eps) return 'Coronal';
  } else {
    if (ay < eps && az < eps) return 'Sagittal';
  }
  return null;
}

/** 캔버스 좌표를 캔버스 버퍼 해상도에 맞게 반올림+클램프 */
function clampCanvasSeed(
  vp: Types.IVolumeViewport,
  seedCanvas: { x: number; y: number },
) {
  const cs = (vp as any).canvas as HTMLCanvasElement;
  const W = cs.width,
    H = cs.height;
  const sx = Math.max(0, Math.min(W - 1, Math.round(seedCanvas.x)));
  const sy = Math.max(0, Math.min(H - 1, Math.round(seedCanvas.y)));
  return { sx, sy, W, H };
}

/** in-plane flood 결과([a,b])를 [i,j,k]로 승격 */
function liftInplaneToIJK(
  plane: Plane,
  fixed: number,
  pairs: Array<[number, number]>,
): Array<[number, number, number]> {
  const out = new Array<[number, number, number]>(pairs.length);
  if (plane === 'Axial') {
    for (let t = 0; t < pairs.length; t++)
      out[t] = [pairs[t][0], pairs[t][1], fixed];
  } else if (plane === 'Coronal') {
    for (let t = 0; t < pairs.length; t++)
      out[t] = [pairs[t][0], fixed, pairs[t][1]];
  } else {
    // Sagittal
    for (let t = 0; t < pairs.length; t++)
      out[t] = [fixed, pairs[t][0], pairs[t][1]];
  }
  return out;
}

type InplaneFloodScratch = {
  areaSize: number;
  visited: Uint32Array;
  visitStamp: number;
  stackA: Uint32Array;
  stackB: Uint32Array;
  outA: Uint32Array;
  outB: Uint32Array;
};

const inplaneFloodScratchBySize = new Map<string, InplaneFloodScratch>();

function getInplaneFloodScratch(W: number, H: number): InplaneFloodScratch {
  const areaSize = (W * H) | 0;
  const key = `${W}x${H}`;
  let scratch = inplaneFloodScratchBySize.get(key);
  if (!scratch || scratch.areaSize !== areaSize) {
    const initialCapacity = Math.min(areaSize || 1, 1 << 20);
    scratch = {
      areaSize,
      visited: new Uint32Array(areaSize),
      visitStamp: 1,
      stackA: new Uint32Array(initialCapacity),
      stackB: new Uint32Array(initialCapacity),
      outA: new Uint32Array(initialCapacity),
      outB: new Uint32Array(initialCapacity),
    };
    inplaneFloodScratchBySize.set(key, scratch);
  }

  if (scratch.visitStamp >= 0xffffffff) {
    scratch.visited.fill(0);
    scratch.visitStamp = 1;
  }

  return scratch;
}

function ensureUint32Capacity(
  current: Uint32Array,
  requiredLength: number,
  maxLength: number,
) {
  if (requiredLength <= current.length) return current;

  const nextLength = Math.min(
    Math.max(current.length * 2, requiredLength),
    maxLength,
  );
  const next = new Uint32Array(nextLength);
  next.set(current);
  return next;
}

/** 축정렬 평면용: 네가 쓰던 2D voxel 격자 flood (4-연결 고정) */
function floodInplaneVoxel2D(
  scalar: Int16Array,
  dims: [number, number, number],
  seedA: number,
  seedB: number,
  fixed: number,
  plane: Plane,
  lo: number,
  hi: number,
): Array<[number, number]> {
  const [cols, rows, slices] = dims;
  const strideXY = cols * rows;

  let W = 0,
    H = 0,
    base = 0,
    sa = 0,
    sb = 0;

  if (plane === 'Axial') {
    W = cols;
    H = rows;
    base = (fixed >>> 0) * strideXY;
    sa = 1;
    sb = cols;
  } else if (plane === 'Coronal') {
    W = cols;
    H = slices;
    base = (fixed >>> 0) * cols;
    sa = 1;
    sb = strideXY;
  } else {
    W = rows;
    H = slices;
    base = fixed >>> 0;
    sa = cols;
    sb = strideXY;
  }

  if (seedA < 0 || seedB < 0 || seedA >= W || seedB >= H) return [];
  const seedIdx = (base + seedA * sa + seedB * sb) | 0;
  const seedV = scalar[seedIdx];
  if (seedV < lo || seedV > hi) return [];

  const areaSize = (W * H) | 0;
  const scratch = getInplaneFloodScratch(W, H);
  const visited = scratch.visited;
  const visitStamp = scratch.visitStamp++;
  let stackA = scratch.stackA;
  let stackB = scratch.stackB;
  let top = 0;
  const push = (a: number, b: number) => {
    if (top >= stackA.length) {
      stackA = ensureUint32Capacity(stackA, top + 1, areaSize);
      stackB = ensureUint32Capacity(stackB, top + 1, areaSize);
      scratch.stackA = stackA;
      scratch.stackB = stackB;
    }
    stackA[top] = a >>> 0;
    stackB[top] = b >>> 0;
    top++;
  };
  const pop = () => {
    top--;
    return [stackA[top] | 0, stackB[top] | 0] as [number, number];
  };
  const vIdx = (a: number, b: number) => (b * W + a) | 0;

  const neigh4: [number, number][] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  visited[vIdx(seedA, seedB)] = visitStamp;
  push(seedA, seedB);

  let outA = scratch.outA;
  let outB = scratch.outB;
  let outN = 0;
  const emit = (a: number, b: number) => {
    if (outN >= outA.length) {
      outA = ensureUint32Capacity(outA, outN + 1, areaSize);
      outB = ensureUint32Capacity(outB, outN + 1, areaSize);
      scratch.outA = outA;
      scratch.outB = outB;
    }
    outA[outN] = a >>> 0;
    outB[outN] = b >>> 0;
    outN++;
  };

  while (top > 0) {
    const [a, b] = pop();
    emit(a, b);

    for (let k = 0; k < 4; k++) {
      const na = a + neigh4[k][0];
      const nb = b + neigh4[k][1];
      if (na < 0 || nb < 0 || na >= W || nb >= H) continue;
      const vi = vIdx(na, nb);
      if (visited[vi] === visitStamp) continue;

      const idx = (base + na * sa + nb * sb) | 0;
      const v = scalar[idx];
      if (v < lo || v > hi) continue;

      visited[vi] = visitStamp;
      push(na, nb);
    }
  }

  const res = new Array<[number, number]>(outN);
  for (let i = 0; i < outN; i++) res[i] = [outA[i] | 0, outB[i] | 0];
  return res;
}

/** Oblique용: 캔버스 격자에서 flood, 방문 픽셀을 voxel로 매핑하여 중복 제거 후 [i,j,k][] 반환 */
function floodCanvasObliqueToVoxels(
  vp: Types.IVolumeViewport,
  scalar: Int16Array,
  lo: number,
  hi: number,
  seedCanvas: { x: number; y: number },
): Array<[number, number, number]> {
  const imageData = vp.getImageData();
  if (!imageData) return [];
  const [cols, rows, slices] = imageData.dimensions as [number, number, number];
  const strideXY = cols * rows;

  const { sx, sy, W, H } = clampCanvasSeed(vp, seedCanvas);

  // 시드 HU 검사
  const seedWorld = vp.canvasToWorld([sx, sy]) as Types.Point3;
  const seedIJK = worldToIndex(imageData, seedWorld);
  const seedHU = sampleHU_Nearest(scalar, [cols, rows, slices], seedIJK);
  if (seedHU == null || seedHU < lo || seedHU > hi) return [];

  // 캔버스 flood (4-연결)
  const visited = new Uint8Array(W * H);
  const toIdx = (x: number, y: number) => y * W + x;

  const stackX = new Int32Array(W * H);
  const stackY = new Int32Array(W * H);
  let top = 0;
  stackX[top] = sx;
  stackY[top] = sy;
  top++;
  visited[toIdx(sx, sy)] = 1;

  // voxel 중복 제거
  const voxelSet = new Set<number>();
  const addVoxel = (ijk: number[] | [number, number, number]) => {
    const ii = Math.round(ijk[0]);
    const jj = Math.round(ijk[1]);
    const kk = Math.round(ijk[2]);
    if (ii < 0 || jj < 0 || kk < 0 || ii >= cols || jj >= rows || kk >= slices)
      return;
    voxelSet.add(kk * strideXY + jj * cols + ii);
  };
  addVoxel(seedIJK as any);

  const neigh = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;

  while (top > 0) {
    top--;
    const x = stackX[top] | 0,
      y = stackY[top] | 0;

    for (let k = 0; k < 4; k++) {
      const nx = x + neigh[k][0],
        ny = y + neigh[k][1];
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const vi = toIdx(nx, ny);
      if (visited[vi]) continue;

      const w = vp.canvasToWorld([nx, ny]) as Types.Point3;
      const ijk = worldToIndex(imageData, w);
      const hu = sampleHU_Nearest(scalar, [cols, rows, slices], ijk);
      visited[vi] = 1;
      if (hu == null || hu < lo || hu > hi) continue;

      stackX[top] = nx;
      stackY[top] = ny;
      top++;
      addVoxel(ijk as any);
    }
  }

  // Set → [i,j,k][]
  const out: Array<[number, number, number]> = new Array(voxelSet.size);
  let idx = 0;
  voxelSet.forEach((lin) => {
    const k = Math.floor(lin / strideXY);
    const rem = lin - k * strideXY;
    const j = Math.floor(rem / cols);
    const i = rem - j * cols;
    out[idx++] = [i, j, k];
  });
  return out;
}

/** === 외부에서 호출할 단일 API ===
 *  seedCanvas를 받아 oblique/정렬 여부를 내부에서 판단하여 항상 [i,j,k][]를 반환
 */
export function floodFill2D_Multi(
  vp: Types.IVolumeViewport,
  scalar: Int16Array,
  seedCanvas: { x: number; y: number },
  lo: number,
  hi: number,
): Array<[number, number, number]> {
  const imageData = vp.getImageData();
  if (!imageData) return [];
  const dims = imageData.dimensions as [number, number, number];
  const plane = detectAxisAlignedPlane(vp);

  // 캔버스 시드 → world → ijk
  const { sx, sy } = clampCanvasSeed(vp, seedCanvas);
  const seedWorld = vp.canvasToWorld([sx, sy]) as Types.Point3;
  const seedIJK = worldToIndex(imageData, seedWorld);
  const si = Math.round(seedIJK[0]);
  const sj = Math.round(seedIJK[1]);
  const sk = Math.round(seedIJK[2]);

  // 정렬: in-plane voxel 격자에서 flood → [i,j,k][] 승격
  if (plane) {
    let seedA = 0,
      seedB = 0,
      fixed = 0 as number;

    if (plane === 'Axial') {
      // (i,j) @ k=fixed
      seedA = si;
      seedB = sj;
      fixed = sk;
    } else if (plane === 'Coronal') {
      // (i,k) @ j=fixed
      seedA = si;
      seedB = sk;
      fixed = sj;
    } else {
      // Sagittal: (j,k) @ i=fixed
      seedA = sj;
      seedB = sk;
      fixed = si;
    }

    const inplane = floodInplaneVoxel2D(
      scalar,
      dims,
      seedA,
      seedB,
      fixed,
      plane,
      lo,
      hi,
    );
    return liftInplaneToIJK(plane, fixed, inplane);
  }

  // Oblique: 캔버스 격자에서 flood → voxel Set으로 귀결
  return floodCanvasObliqueToVoxels(vp, scalar, lo, hi, { x: sx, y: sy });
}
export function ensureManualSegmentationViewport(
  viewportId: PanelComponentKeyType,
  seriesId: string,
  lowQuality: boolean,
) {
  const editMode = configStore.get().EditMode;
  if (!editMode) return false;

  const baseSegmentationId = `segmentation_${seriesId}`;

  const segmentationId = `${baseSegmentationId}_manual`;
  const type = ToolEnums.SegmentationRepresentations.Labelmap;

  if (!segmentation.state.getSegmentation(segmentationId)) return false;
  if (!cache.getVolume(`${seriesId}_manual`)) {
    const sourceVolumeId =
      editMode === EditNames.regulator
        ? `regulator_${seriesId}`
        : lowQuality
          ? `${seriesId}_labelmapHalf`
          : `${seriesId}_labelmap`;
    if (!cache.getVolume(sourceVolumeId)) return false;
  }

  segmentation.removeLabelmapRepresentation(viewportId, `${baseSegmentationId}_regulator`, false);

  const colorLUTIndex = segmentation.state.getSegmentation(segmentationId)
    ?.cachedStats['colorLUTIndex'] as number;

  if (
    !segmentation.state.getSegmentationRepresentation(viewportId, {
      segmentationId,
      type,
    })
  ) {
    segmentation.addLabelmapRepresentationToViewport(viewportId, [
      { segmentationId, type },
    ]);
  }

  segmentation.config.color.setColorLUT(
    viewportId,
    segmentationId,
    colorLUTIndex,
  );
  segmentation.config.style.setStyle(
    {
      type,
      segmentationId,
    },
    configStore.get().manualLabelmapStyle === 'fill'
      ? defaultLabelmapStyle
      : defaultOutlineStyle,
  );

  const engine = getEngine(mainEngineId);
  const vp = engine.getViewport(viewportId);
  vp?.render();

  return true;
}
